import { useEffect, useRef } from 'preact/hooks';
import type { CarProfile } from '../../cars/CarProfile';
import type { DrivetrainState } from '../../drivetrain/VirtualDrivetrain';
import { drawRoardriveWordmark, ROARDRIVE_WORDMARK_W } from './roardriveWordmark';
import type { LapState } from '../../lap/LapTimer';
import { formatLap, formatDelta } from '../../lap/LapTimer';
import { loadSettings } from '../../utils/settings';
import { isCarBrowser } from '../../utils/platform';

/**
 * RaceDash — a distinct motorsport instrument cluster per car, recreating the
 * real dashes. Only shows data derivable on a real Tesla from GPS speed
 * (speed, synthesized rpm/gear/load, lap timing) — NO fake pedal-input meters.
 *
 *   cosworth (Porsche 911 RSR — Cosworth ICD): clean white-on-black, full-width
 *       segmented rev bar (green→amber→red, blue flash at the limiter), one huge
 *       centre gear, speed left, lap block right.
 *   f1 (open-wheel — McLaren Applied): dense & multicolour, iconic 5 green /
 *       5 red / 5 blue rev strip, big gear left-of-centre, large coloured
 *       predictive DELTA as the hero on the right, tyre/energy-style tiles.
 *   bosch (Mercedes-AMG GT3 — Bosch DDU): rev arc bracketing the gear (Bosch
 *       signature), silver hairline tiles, tiles flip red on the limiter alarm.
 *
 * Canvas, double-buffered (opaque back-buffer, single blit) → no flicker.
 */
type Style = 'cosworth' | 'bosch' | 'f1' | 'pcu' | 'svj' | 'f812' | 'default';

/** Driver-settable controls mirrored on the dash (tappable boxes). */
export interface DashControls {
  gearbox: 'auto' | 'manual';
  /** the driver's own gearbox is visible over OBD and the engine is copying it */
  following?: boolean;
  style: 'auto' | 'eco' | 'sport';
  resolvedStyle: 'eco' | 'sport';
  lapRunning: boolean;
  lapHasData: boolean;
  /** pit-limiter cap in km/h when armed (null = off) */
  pitCap?: number | null;
  rawSpeedKmh?: number;
  /** WIPER panel text while showing (replaces the Laptime block, blue) */
  wiperMsg?: string | null;
  /** headlights (CAN); null = unknown */
  lightsOn?: boolean | null;
  /** real TPMS pressures [FL, FR, RL, RR] bar from the CAN — cosmetic values when absent */
  tires?: number[] | null;
  /** WET from the car's wiper (CAN); null = the dash key decides */
  wet?: boolean | null;
  /** bottom units: engine START/STOP (pop-up when alone) and the Racelogic lap timer */
  showStart?: boolean;
  showLap?: boolean;
}
export type DashAction = 'gearbox:auto' | 'gearbox:manual' | 'gearbox:car' | 'style:eco' | 'style:sport' | 'lap:start' | 'lap:mark' | 'lap:stop' | 'lap:reset' | 'wet:toggle' | 'engine:toggle' | 'engine:press' | 'engine:release' | 'launch:toggle' | 'pit:toggle' | 'wet:on' | 'wet:off' | 'ui:start' | 'ui:lap' | 'ui:bottom';
interface HitRect { x: number; y: number; w: number; h: number; action: DashAction }


export function RaceDash({
  profile,
  getState,
  getLap,
  getControls,
  onAction,
  accent = '#ff3d1f',
  accent2 = '#ff6a3d',
  style = 'default',
}: {
  profile: CarProfile;
  getState: () => DrivetrainState;
  getLap: () => LapState;
  getControls?: () => DashControls;
  onAction?: (a: DashAction) => void;
  accent?: string;
  accent2?: string;
  style?: Style;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const hits = useRef<HitRect[]>([]);
  const wet = useRef(false); // cosmetic DRY/WET chip
  const crankUntil = useRef(0); // STARTER ON lingers 1 s after the engine catches
  const lapSeen = useRef<{ count: number; best: number | null }>({ count: 0, best: null }); const lapDiff = useRef(0); // Time Diff of the last completed lap vs the best before it
  const prevLaunch = useRef(false); const launchSlip = useRef(false); // purple wheelspin flash for the first 10 km/h after a launch
  const rpmShown = useRef({ v: 0, t: 0 }); // the rpm NUMBER updates ~8 Hz in readable steps, not per frame
  const pressing = useRef(false); // START/STOP held down
  const onPointer = (e: PointerEvent) => {
    const c = ref.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    for (const h of hits.current) {
      if (x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h) {
        if (h.action === 'wet:toggle') { wet.current = !wet.current; onAction?.(wet.current ? 'wet:on' : 'wet:off'); }
        else onAction?.(h.action);
        if (h.action === 'engine:press') {
          pressing.current = true;
          try { (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId); } catch { /* ignore */ }
        }
        return;
      }
    }
  };
  const onPointerUp = () => {
    if (pressing.current) { pressing.current = false; onAction?.('engine:release'); }
  };

  // the controls change identity on every UI tap; the canvas must not be rebuilt for that (it would
  // restart the fold animation, the vitals warm-up and the Racelogic crawl every time)
  const ctrlRef = useRef(getControls);
  ctrlRef.current = getControls;

  useEffect(() => {
    const getControls = ctrlRef.current && (() => ctrlRef.current!());
    const canvas = ref.current!;
    const ctx = canvas.getContext('2d')!;
    const back = document.createElement('canvas');
    const b = back.getContext('2d')!;
    let raf = 0;
    let w = 0;
    let h = 0;
    let dpr = 1;
    let needle = profile.idleRpm;
    // A REAL BINNACLE HAS TEMPERATURES AND A FUEL LEVEL, and a dash whose gauges never move reads as a
    // picture of an instrument rather than an instrument. These are SIMULATED and say so: water and oil
    // climb from ambient towards their working point while the engine runs, faster the harder it is
    // worked and oil lagging water the way it does in a car; fuel falls with throttle against revs.
    // Nothing here is dressed up as a measurement we do not have.
    const sim812 = { water: 18, oil: 16, fuel: 0.72, lastT: 0, odoKm: 504 };
    // the SVJ's gear digit remembers when it last changed, so the change can be shown rather than jumped
    const svjGear = { last: '', prev: '', changedAt: -9e9 };
    // The G-METER is a real shape on this cluster and it was missing entirely. Longitudinal g is
    // differentiated from the speed we already have and smoothed; LATERAL g we genuinely do not
    // have (no heading rate in the telemetry), so it reads 0.0 rather than a decorative wobble -
    // which is exactly what Marc's photograph shows on all four corners with the car standing.
    const svjG = { spd: 0, t: 0, lon: 0 };
    const state812 = sim812;

    const fg = '#f2f2f0';
    const muted = 'rgba(255,255,255,0.42)';
    const rl = '#0ab4ff';
    const silver = '#c7ccd1';
    const max = profile.maxRpm;
    const cond = (px: number, wt = 500) => `${wt} ${px}px "Barlow Condensed", sans-serif`;
    const mono = (px: number) => `500 ${px}px "JetBrains Mono", monospace`;
    // speed units (Home toggle): every dash shows mph when chosen — the drivetrain stays in km/h
    const mph = loadSettings().units === 'mph';
    const spd = (kmh: number) => (mph ? kmh / 1.609344 : kmh);
    const unitLabel = mph ? 'mph' : 'km/h';

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      // the car's browser draws at 1: its screen is big and slow, and every pixel this canvas paints is
      // main-thread time the audio thread on that SoC does not get (Marc's 'piedrecitas' in the Tesla)
      dpr = isCarBrowser() ? 1 : Math.min(1.5, window.devicePixelRatio || 1);
      w = Math.max(1, Math.round(rect.width));
      h = Math.max(1, Math.round(rect.height));
      canvas.width = back.width = Math.round(w * dpr);
      canvas.height = back.height = Math.round(h * dpr);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    const rr = (x: number, y: number, ww: number, hh: number, r: number) => {
      const rad = Math.min(r, Math.abs(ww) / 2, Math.abs(hh) / 2);
      b.beginPath();
      b.moveTo(x + rad, y);
      b.arcTo(x + ww, y, x + ww, y + hh, rad);
      b.arcTo(x + ww, y + hh, x, y + hh, rad);
      b.arcTo(x, y + hh, x, y, rad);
      b.arcTo(x, y, x + ww, y, rad);
      b.closePath();
    };
    const seg = (x: number, y: number, sw: number, sh: number, col: string, on: boolean) => {
      if (on) {
        b.globalAlpha = 0.3;
        b.fillStyle = col;
        rr(x - 2, y - 2, sw + 4, sh + 4, 3);
        b.fill();
        b.globalAlpha = 1;
      }
      b.fillStyle = on ? col : 'rgba(255,255,255,0.06)';
      rr(x, y, sw, sh, 2);
      b.fill();
    };
    // shift flash: arm a little before the cut (94 %) or at the limiter, and switch OFF the
    // instant a gearchange starts / the gear has changed (the flash must never outlive the shift)
    let blueUntil = 0;
    let blueGear = -1;
    const blueActive = (t: number, s: DrivetrainState) => {
      if (s.shifting || (blueGear >= 0 && s.gear !== blueGear)) { blueUntil = 0; blueGear = s.gear; return false; }
      blueGear = s.gear;
      if (s.limiter || s.rpm >= max * 0.94) blueUntil = t + 120;   // barely any tail: the blue dies with the shift
      return t < blueUntil;
    };

    const text = (s: string, x: number, y: number, font: string, col: string, align: CanvasTextAlign = 'left', base: CanvasTextBaseline = 'alphabetic') => {
      b.font = font;
      b.fillStyle = col;
      b.textAlign = align;
      b.textBaseline = base;
      b.fillText(s, x, y);
    };

    // ================= per-style layouts =================
    // Porsche 911 RSR — Cosworth ICD, recreated from the real cockpit display:
    // symmetric LED strip (green→amber→red from the OUTSIDE towards the centre,
    // all-blue flash at the limiter), embossed PORSCHE wordmark, speed box +
    // Lap box + DRY chip on a top row, DIM/ATH/MUL yellow chips and oil/water
    // tiles on the left, one huge framed gear digit, Laptime / Time Diff / Pred
    // Time block on the right, Brake Bias chip bottom-right.
    // tappable setting boxes: GEAR [AUTO|MANUAL] · MODE [DRIVE|SPORT]
    // blue back-lit keypad push-buttons (like the Porsche GT3 switch panel): label on top, value below
    const ctrlGroup = (x: number, y: number, ww: number, hh: number, label: string, px: number, opts: { text: string; active: boolean; action: DashAction; tag?: string }[]) => {
      const gap = ww * 0.06;
      const bw = (ww - gap * (opts.length - 1)) / opts.length;
      const bh = hh;
      opts.forEach((o, i) => {
        const bx = x + i * (bw + gap);
        // bezel
        b.fillStyle = '#15171b'; rr(bx, y, bw, bh, 5); b.fill();
        b.strokeStyle = 'rgba(255,255,255,0.22)'; b.lineWidth = 1.5; rr(bx, y, bw, bh, 5); b.stroke();
        // key face
        const ix = bx + bw * 0.07, iy = y + bh * 0.09, iw = bw * 0.86, ih = bh * 0.82;
        if (o.active) {
          b.shadowColor = 'rgba(60,120,255,0.9)'; b.shadowBlur = px * 0.9;
          b.fillStyle = '#1d46c8'; rr(ix, iy, iw, ih, 4); b.fill();
          b.shadowBlur = 0;
          b.strokeStyle = '#6aa0ff'; b.lineWidth = 1.5; rr(ix, iy, iw, ih, 4); b.stroke();
        } else {
          b.fillStyle = '#0a1030'; rr(ix, iy, iw, ih, 4); b.fill();
          b.strokeStyle = 'rgba(80,120,255,0.45)'; b.lineWidth = 1.5; rr(ix, iy, iw, ih, 4); b.stroke();
        }
        text(label, ix + iw / 2, iy + ih * 0.28, cond(Math.round(px * 0.62), 600), o.active ? '#cfe0ff' : 'rgba(120,160,255,0.75)', 'center', 'middle');
        text(o.text, ix + iw / 2, iy + ih * 0.68, cond(Math.round(px * 0.95), 700), o.active ? '#ffffff' : 'rgba(150,180,255,0.9)', 'center', 'middle');
        if (o.tag) text(o.tag, ix + iw - px * 0.3, iy + px * 0.5, cond(Math.round(px * 0.55), 700), o.active ? '#cfe0ff' : 'rgba(120,160,255,0.75)', 'right', 'middle');
        hits.current.push({ x: bx, y, w: bw, h: bh, action: o.action });
      });
    };
    const controlBoxes = (x: number, y: number, ww: number, rowH: number, px: number) => {
      const c = getControls ? getControls() : { gearbox: 'auto' as const, style: 'auto' as const, resolvedStyle: 'sport' as const, lapRunning: false, lapHasData: false };
      const autoTag = c.style === 'auto' ? 'A' : undefined;
      // when the car's OWN gearbox is visible there is nothing to choose: it changes when you change.
      // AUTO is the way back out (paddles come with it), so the driver is never trapped.
      ctrlGroup(x, y, ww, rowH, 'GEAR', px, c.following
        ? [
          { text: 'YOUR CAR', active: true, action: 'gearbox:car' },
          { text: 'AUTO', active: false, action: 'gearbox:auto' },
        ]
        : [
          { text: 'AUTO', active: c.gearbox === 'auto', action: 'gearbox:auto' },
          { text: 'MANUAL', active: c.gearbox === 'manual', action: 'gearbox:manual' },
        ]);
      ctrlGroup(x, y + rowH * 1.18, ww, rowH, 'MODE', px, [
        { text: 'DRIVE', active: c.resolvedStyle === 'eco', action: 'style:eco', tag: c.resolvedStyle === 'eco' ? autoTag : undefined },
        { text: 'RACE', active: c.resolvedStyle === 'sport', action: 'style:sport', tag: c.resolvedStyle === 'sport' ? autoTag : undefined },
      ]);
    };
    /**
     * THE CAR'S OWN CONTROLS, in the shapes the car uses (Marc: "botones fieles al coche").
     *
     * On a real 812 these live in two places: the red ENGINE START/STOP button and the manettino are
     * on the steering wheel, AUTO and LAUNCH are on the exposed bridge over the transmission tunnel.
     * Here they share one row because there is one row of space, but every one of the four exists on
     * the car and does what it does there. The bridge's third button, R, is not drawn: there is no
     * reverse to select, and a button that does nothing is worse than a missing one.
     *
     * The manettino is engraved with all five of its real positions and only two of them light,
     * because SPORT and RACE are the two this app has. Tapping the knob moves between those two.
     */
    const f812Controls = (x: number, y: number, ww: number, hh: number, s: DrivetrainState) => {
      const c = getControls ? getControls() : null;
      const race = c?.resolvedStyle === 'sport';
      const auto = c?.gearbox !== 'manual';
      const running = s.engine === 'on';
      const sat = (bx: number, by: number, bw: number, bh: number, lit: boolean, col: string) => {
        const g = b.createLinearGradient(0, by, 0, by + bh);
        g.addColorStop(0, lit ? '#2a2622' : '#1a1917'); g.addColorStop(1, lit ? '#141210' : '#0b0b0a');
        b.fillStyle = g; rr(bx, by, bw, bh, bh * 0.22); b.fill();
        b.strokeStyle = lit ? col : 'rgba(255,255,255,0.14)';
        b.lineWidth = Math.max(1, bh * 0.05); rr(bx, by, bw, bh, bh * 0.22); b.stroke();
        if (lit) { b.save(); b.globalAlpha = 0.35; b.shadowColor = col; b.shadowBlur = bh * 0.5; b.stroke(); b.restore(); }
      };

      // the wheel's red starter, engraved the way the car engraves it
      const sr = hh * 0.42, scx = x + sr, scy = y + hh * 0.5;
      const cap = b.createRadialGradient(scx - sr * 0.3, scy - sr * 0.4, sr * 0.1, scx, scy, sr);
      cap.addColorStop(0, running ? '#ff5a44' : '#e8261a'); cap.addColorStop(1, running ? '#8c0f08' : '#6d0b05');
      b.fillStyle = cap; b.beginPath(); b.arc(scx, scy, sr, 0, Math.PI * 2); b.fill();
      b.strokeStyle = 'rgba(0,0,0,0.55)'; b.lineWidth = Math.max(1, sr * 0.10);
      b.beginPath(); b.arc(scx, scy, sr, 0, Math.PI * 2); b.stroke();
      b.strokeStyle = 'rgba(255,255,255,0.28)'; b.lineWidth = Math.max(1, sr * 0.05);
      b.beginPath(); b.arc(scx, scy, sr * 0.92, Math.PI * 1.05, Math.PI * 1.85); b.stroke();
      text('ENGINE', scx, scy - sr * 0.30, cond(Math.round(sr * 0.28), 700), '#fff', 'center', 'middle');
      text('START', scx, scy + sr * 0.04, cond(Math.round(sr * 0.30), 700), '#fff', 'center', 'middle');
      text('STOP', scx, scy + sr * 0.36, cond(Math.round(sr * 0.30), 700), '#fff', 'center', 'middle');
      hits.current.push({ x: scx - sr, y: scy - sr, w: sr * 2, h: sr * 2, action: 'engine:toggle' });

      // the manettino: red knob, white pointer, five engraved positions, two of them live
      const mr = hh * 0.36, mcx = x + ww * 0.40, mcy = y + hh * 0.54;
      // TWO POSITIONS, THE TWO THIS APP HAS (Marc: "quita lo de wet, CT y ESC. q solo haya sport
      // race. ponlos en un lugar con gracia"). The wheel engraves five and we could only ever select
      // two of them, so three were decoration that read as controls. The pair left is set square over
      // the knob, one either side of top dead centre, and the pointer swings between them.
      const POS = ['SPORT', 'RACE'];
      const ANG = [-128, -52];
      const live = race ? 1 : 0;
      for (let i = 0; i < POS.length; i++) {
        const a = (ANG[i] * Math.PI) / 180;
        const rr2 = mr * 1.62;
        text(POS[i], mcx + rr2 * Math.cos(a), mcy + rr2 * Math.sin(a),
          cond(Math.round(mr * 0.34), i === live ? 700 : 500),
          i === live ? '#ff3a2a' : 'rgba(200,200,200,0.32)', 'center', 'middle');
      }
      const knob = b.createRadialGradient(mcx - mr * 0.3, mcy - mr * 0.4, mr * 0.1, mcx, mcy, mr);
      knob.addColorStop(0, '#e8261a'); knob.addColorStop(1, '#6d0b05');
      b.fillStyle = knob; b.beginPath(); b.arc(mcx, mcy, mr, 0, Math.PI * 2); b.fill();
      b.strokeStyle = 'rgba(0,0,0,0.6)'; b.lineWidth = Math.max(1, mr * 0.09);
      b.beginPath(); b.arc(mcx, mcy, mr, 0, Math.PI * 2); b.stroke();
      const pa = (ANG[live] * Math.PI) / 180;
      b.strokeStyle = '#fff'; b.lineWidth = Math.max(2, mr * 0.13); b.lineCap = 'round';
      b.beginPath();
      b.moveTo(mcx + mr * 0.20 * Math.cos(pa), mcy + mr * 0.20 * Math.sin(pa));
      b.lineTo(mcx + mr * 0.80 * Math.cos(pa), mcy + mr * 0.80 * Math.sin(pa));
      b.stroke(); b.lineCap = 'butt';
      hits.current.push({ x: mcx - mr * 1.2, y: mcy - mr * 1.2, w: mr * 2.4, h: mr * 2.4, action: race ? 'style:eco' : 'style:sport' });

      // the bridge over the tunnel: AUTO latches, LAUNCH arms
      const bw2 = ww * 0.24, bh2 = hh * 0.44, bx0 = x + ww * 0.56;
      sat(bx0, y + hh * 0.02, bw2, bh2, auto, '#ff3a2a');
      text('AUTO', bx0 + bw2 / 2, y + hh * 0.02 + bh2 / 2, cond(Math.round(bh2 * 0.42), 700), auto ? '#fff' : '#8b8b8b', 'center', 'middle');
      hits.current.push({ x: bx0, y: y + hh * 0.02, w: bw2, h: bh2, action: auto ? 'gearbox:manual' : 'gearbox:auto' });
      // three states, the way the car's own button behaves: dark when there is nothing to arm, dim
      // once the conditions are there (stopped, running, on the brake), full when it is holding
      const armed = !!s.launch;
      const ready = !armed && s.engine === 'on' && s.speedKmh < 3 && s.brake > 0.25;
      b.save(); if (ready) b.globalAlpha = 0.55;
      sat(bx0, y + hh * 0.54, bw2, bh2, armed || ready, '#ffb400');
      b.restore();
      // Marc asked for a launch button on the Ferrari and it was already there - it just did not read
      // as one: dark grey on dark grey until the car was standing on the brake with the engine
      // running. It keeps its three states, the idle one is simply legible now.
      text('LAUNCH', bx0 + bw2 / 2, y + hh * 0.54 + bh2 / 2, cond(Math.round(bh2 * 0.40), 700),
        armed ? '#ffd25a' : ready ? '#c9a463' : '#aab3bd', 'center', 'middle');
      hits.current.push({ x: bx0, y: y + hh * 0.54, w: bw2, h: bh2, action: 'launch:toggle' });
    };

    /**
     * The SVJ's own controls. The red flip-up cover over the starter is the single most recognisable
     * thing in that cabin, so it is drawn as what it is: a hinged red lid standing open above a black
     * button. Beside it the ANIMA rocker, engraved STRADA / SPORT / CORSA the way the wheel is, then
     * the gearbox's A and the THRUST button. Same rule as the Ferrari: everything drawn is a control
     * the car has, and only the positions this app can actually select light up.
     */
    /**
     * The SVJ's starter, from Marc's photograph of the real tunnel: a hexagonal silver bezel standing
     * on its point with a black hexagonal key inside it reading START / ENGINE / STOP. The red
     * flip-up cover came back for one build (v492) and went again the same evening (Marc: 'quita eso
     * rojo del start stop'): the key is the control, the cover was a flag over it.
     */
    const svjStart = (bcx: number, bcy: number, r: number, running: boolean) => {
      const hex = (cx0: number, cy0: number, rr0: number, wide: number) => {
        b.beginPath();
        for (let i = 0; i < 6; i++) {
          const a = (Math.PI / 180) * (60 * i - 90);
          const X = cx0 + Math.cos(a) * rr0 * wide, Y = cy0 + Math.sin(a) * rr0;
          if (i) b.lineTo(X, Y); else b.moveTo(X, Y);
        }
        b.closePath();
      };
      // the bezel: brushed silver, lit from the top-left
      const gB = b.createLinearGradient(bcx - r, bcy - r, bcx + r, bcy + r);
      gB.addColorStop(0, '#e6e9ee'); gB.addColorStop(0.45, '#8c939c'); gB.addColorStop(0.6, '#5a6068'); gB.addColorStop(1, '#c9ced5');
      hex(bcx, bcy, r, 0.87); b.fillStyle = '#101214'; b.fill();
      b.strokeStyle = gB; b.lineWidth = Math.max(1.5, r * 0.16); b.stroke();
      // the key
      const gK = b.createRadialGradient(bcx - r * 0.2, bcy - r * 0.3, r * 0.1, bcx, bcy, r * 0.8);
      gK.addColorStop(0, running ? '#3a3c40' : '#26282c'); gK.addColorStop(1, '#0a0b0d');
      hex(bcx, bcy, r * 0.70, 0.87); b.fillStyle = gK; b.fill();
      b.strokeStyle = running ? '#ff3a24' : 'rgba(255,255,255,0.18)'; b.lineWidth = Math.max(1, r * 0.05); b.stroke();
      const ink = running ? '#ffd9d2' : '#dfe4ea';
      text('START', bcx, bcy - r * 0.24, cond(Math.round(r * 0.26), 700), ink, 'center', 'middle');
      text('ENGINE', bcx, bcy, cond(Math.round(r * 0.13), 600), running ? '#c98a80' : '#8b93a0', 'center', 'middle');
      text('STOP', bcx, bcy + r * 0.24, cond(Math.round(r * 0.26), 700), ink, 'center', 'middle');
      hits.current.push({ x: bcx - r * 1.1, y: bcy - r * 1.1, w: r * 2.2, h: r * 2.2, action: 'engine:toggle' });
    };
    /**
     * Four controls on one line at equal gaps (Marc: 'los 4 botones tienen que ir equidistantes, que
     * quede bonito'): the starter, the ANIMA rocker, the gate's A and LAUNCH, laid out from the
     * starter's left edge to the row's right edge with the same air between each pair. Where the
     * starter stands under the G-meter its centre is given (`startAt`) and the three others take the
     * rest of the line; where it does not, it opens the row like the others.
     */
    const svjControls = (x: number, y: number, ww: number, hh: number, s: DrivetrainState, startAt: { r: number } | null) => {
      const c = getControls ? getControls() : null;
      const corsa = c?.resolvedStyle === 'sport';
      const auto = c?.gearbox !== 'manual';
      const running = s.engine === 'on';
      const hexKey = (bx: number, by: number, bw: number, bh: number, lit: boolean, col: string) => {
        const k = Math.min(bh * 0.30, bw * 0.22);
        b.beginPath();
        b.moveTo(bx + k, by); b.lineTo(bx + bw - k, by); b.lineTo(bx + bw, by + bh / 2);
        b.lineTo(bx + bw - k, by + bh); b.lineTo(bx + k, by + bh); b.lineTo(bx, by + bh / 2);
        b.closePath();
        b.fillStyle = lit ? 'rgba(20,26,36,0.95)' : 'rgba(10,12,16,0.9)'; b.fill();
        b.strokeStyle = lit ? col : 'rgba(150,175,205,0.30)'; b.lineWidth = Math.max(1, bh * 0.06); b.stroke();
        if (lit) { b.save(); b.globalAlpha = 0.4; b.shadowColor = col; b.shadowBlur = bh * 0.5; b.stroke(); b.restore(); }
      };

      // ONE FAMILY OF KEYS, and no toggle (Marc: 'como cojones me pones un puto toggle en un coche').
      // The ANIMA control was a rounded rocker with two words in it, which is a web switch wearing a
      // car's name: nothing on a Lamborghini's tunnel looks like that, and next to two hexagonal keys
      // it read as a stranger. It is a hexagonal key now like the other three, showing the mode that
      // is LIVE in that mode's own colour with ANIMA engraved under it — press it and the mode
      // changes, which is what the real rocker does to the car. Four keys, one shape, one line.
      const mid = y + hh * 0.5;                                  // the one centre line
      const sr = startAt ? startAt.r : hh * 0.66;                // the starter's radius (hexagon on its point) — big on the formats that have no room under the G-meter either
      const sw = sr * 0.87 * 2;
      // v509 (Marc: 'mucho más a la derecha'). The keys used to be fractions of the ROW, so widening the
      // row to push them right fattened them at the same time and they barely moved. Their size comes
      // off the row's HEIGHT now and only the gaps come off its width: the four are a cluster of fixed
      // proportions hung on the row's right edge, and the row's width moves them and nothing else.
      const kh = hh * 0.58, ky = mid - kh * 0.5, slop = hh * 0.14;
      const aw = kh * 2.4, kw = kh * 1.05, tw = kh * 2.4;
      // TWO gaps, not one (Marc: 'junta las dos cajas adjuntas a la A'). ANIMA, A and LAUNCH are one
      // thought — the mode, the gate, the launch — and they sit close enough to read as a set; the
      // starter is a different thought and keeps its own air. Tightening the trio also walks the
      // starter to the right, because the whole cluster hangs off the row's right edge.
      const gap = hh * 0.30, startGap = hh * 0.50;
      const cluster = sw + startGap + aw + gap + kw + gap + tw;
      const sx = x + ww - cluster;
      const ax = sx + sw + startGap;
      svjStart(sx + sr * 0.87, mid, sr, running);

      // ANIMA: the live mode, in the live mode's colour
      const lit = corsa ? '#ff3a17' : '#e8dc40';
      // CORSA burns, SPORT glows. A mode is always selected, so this key is always lit, and lit as hard
      // as an armed LAUNCH it shouted louder than the two keys that actually change while you drive.
      b.save(); if (!corsa) b.globalAlpha = 0.62;
      hexKey(ax, ky, aw, kh, true, lit);
      b.restore();
      text(corsa ? 'CORSA' : 'SPORT', ax + aw / 2, ky + kh / 2,
        cond(Math.round(Math.min(kh * 0.42, aw * 0.30)), 700), corsa ? lit : '#c9bd3a', 'center', 'middle');
      // MODE, not the maker's own word for its selector (2026-09-10, the generic line-up)
      text('MODE', ax + aw / 2, ky + kh * 1.34, cond(Math.round(kh * 0.30), 600), '#7f93ad', 'center', 'middle');
      hits.current.push({ x: ax - slop, y: ky - slop, w: aw + 2 * slop, h: kh + 2 * slop, action: corsa ? 'style:eco' : 'style:sport' });

      // the gate's A: the automatic, lit blue when the box is changing for you
      const kx = ax + aw + gap;
      hexKey(kx, ky, kw, kh, auto, '#2f8fe0');
      text('A', kx + kw / 2, ky + kh / 2, cond(Math.round(Math.min(kh * 0.52, kw * 0.55)), 700), auto ? '#cfe4ff' : '#5d6b7c', 'center', 'middle');
      hits.current.push({ x: kx - slop, y: ky - slop, w: kw + 2 * slop, h: kh + 2 * slop, action: auto ? 'gearbox:manual' : 'gearbox:auto' });

      // LAUNCH, not THRUST (Marc). Lamborghini's name for it is THRUST MODE and that is what the car
      // engraves, but every other dash in this app calls the same key LAUNCH, and a driver reading
      // two of our cars should not have to learn two words for one button.
      const tx = kx + kw + gap;
      const armed = !!s.launch;
      const ready = !armed && s.engine === 'on' && s.speedKmh < 3 && s.brake > 0.25;
      b.save(); if (ready) b.globalAlpha = 0.55;
      hexKey(tx, ky, tw, kh, armed || ready, '#ff3a17');
      b.restore();
      text('LAUNCH', tx + tw / 2, ky + kh / 2, cond(Math.round(Math.min(kh * 0.36, tw * 0.26)), 700),
        armed ? '#ffb0a0' : ready ? '#a8705f' : '#aab3bd', 'center', 'middle');
      hits.current.push({ x: tx - slop, y: ky - slop, w: tw + 2 * slop, h: kh + 2 * slop, action: 'launch:toggle' });
    };

    // cosmetic track-condition chip, tap to toggle DRY/WET
    const dryChip = (x: number, y: number, ww: number, hh: number, px: number) => {
      const isWet = (getControls ? getControls().wet : null) ?? wet.current;
      // photo: black rounded key, soft glowing blue rim, light-grey plain sans legend
      b.fillStyle = isWet ? '#0d1e3a' : '#08090b'; rr(x, y, ww, hh, hh * 0.18); b.fill();
      b.save(); b.shadowColor = 'rgba(70,140,255,0.8)'; b.shadowBlur = hh * 0.25;
      b.strokeStyle = '#3f8cff'; b.lineWidth = Math.max(2, hh * 0.05); rr(x, y, ww, hh, hh * 0.18); b.stroke(); b.restore();
      text(isWet ? 'WET' : 'DRY', x + ww / 2, y + hh / 2, `500 ${px}px "Inter", "Helvetica Neue", Arial, sans-serif`, '#d9dce1', 'center', 'middle');
      hits.current.push({ x, y, w: ww, h: hh, action: 'wet:toggle' });
    };
    // RaceLogic lap-timer buttons: ▶ START, or LAP | ■ STOP while running
    const lapButtons = (x: number, y: number, ww: number, hh: number, px: number) => {
      const c = getControls ? getControls() : { lapRunning: false, lapHasData: false };
      const btn = (bx: number, bw: number, label: string, col: string, fill: string, action: DashAction) => {
        b.fillStyle = fill; rr(bx, y, bw, hh, 4); b.fill();
        b.strokeStyle = col; b.lineWidth = 2; rr(bx, y, bw, hh, 4); b.stroke();
        text(label, bx + bw / 2, y + hh / 2, cond(px, 700), col === rl ? '#fff' : col, 'center', 'middle');
        hits.current.push({ x: bx, y, w: bw, h: hh, action });
      };
      if (!c.lapRunning) {
        if (c.lapHasData) {
          const gap = ww * 0.04;
          btn(x, ww * 0.62 - gap / 2, 'START TIMING', rl, 'rgba(10,180,255,0.18)', 'lap:start');
          btn(x + ww * 0.62 + gap / 2, ww * 0.38 - gap / 2, 'RESET', '#d8d8d4', 'rgba(255,255,255,0.08)', 'lap:reset');
        } else {
          btn(x, ww, 'START TIMING', rl, 'rgba(10,180,255,0.18)', 'lap:start');
        }
      } else {
        const gap = ww * 0.04;
        btn(x, ww * 0.58 - gap / 2, 'LAP', '#ffd01a', 'rgba(255,208,26,0.16)', 'lap:mark');
        btn(x + ww * 0.58 + gap / 2, ww * 0.42 - gap / 2, '■ STOP', '#ff2a2a', 'rgba(255,42,42,0.14)', 'lap:stop');
      }
    };

    // carbon-fiber weave for everything OUTSIDE the screen (bottom row, PIT corner)
    let carbonPat: CanvasPattern | null = null;
    const carbon = () => {
      if (carbonPat) return carbonPat;
      const c2 = document.createElement('canvas'); c2.width = 12; c2.height = 12;
      const g2 = c2.getContext('2d')!;
      g2.fillStyle = '#141518'; g2.fillRect(0, 0, 12, 12);
      const tile = (x: number, y: number, light: boolean) => {
        const gr = g2.createLinearGradient(x, y, x + 6, y + 6);
        gr.addColorStop(0, light ? '#26282d' : '#1b1d21');
        gr.addColorStop(1, light ? '#1d1f24' : '#101114');
        g2.fillStyle = gr; g2.fillRect(x, y, 6, 6);
      };
      tile(0, 0, true); tile(6, 0, false); tile(0, 6, false); tile(6, 6, true);
      carbonPat = b.createPattern(c2, 'repeat');
      return carbonPat;
    };

    // ---- Porsche 911 GT3 — Cosworth ICD, laid out 1:1 from the real display photos ----
    // running clock for the cosmetic vitals (oil/water warm up while the engine runs)
    let runT = 0, lastVt = 0;
    let foldK = -1;   // bottom row fold animation 0 (hidden) … 1 (open); -1 = not initialised
    let glassLabel = '', glassAt = 0;   // the Racelogic glass: what it says and since when (the crawl restarts with it)
    const drawCosworth = (t: number, s: DrivetrainState, lap: LapState) => {
      const ui: DashControls = getControls ? getControls() : ({ gearbox: 'auto', style: 'auto', resolvedStyle: 'sport', lapRunning: false, lapHasData: false } as DashControls);
      // The fold is a movement, not a jump (Marc): the row travels over ~0.42 s and the dash grows into
      // the space it leaves. foldK is the linear travel; fe eases it in and out, and everything that
      // moves reads fe, so nothing starts or stops abruptly.
      const wantOpen = (ui.showLap ?? false) || (ui.showStart ?? true);
      const dtv0 = lastVt ? Math.min(0.2, (t - lastVt) / 1000) : 0;
      const foldTarget = wantOpen ? 1 : 0;
      if (foldK < 0) foldK = foldTarget;
      else foldK += Math.sign(foldTarget - foldK) * Math.min(Math.abs(foldTarget - foldK), dtv0 / 0.42);
      if (Math.abs(foldK - foldTarget) < 0.004) foldK = foldTarget;
      const fe = foldK * foldK * (3 - 2 * foldK);   // smoothstep
      const open = fe > 0.004;
      const showLap = open, showStart = open;
      // the dash takes the WHOLE height unless the Racelogic row is shown (Marc: HUD a pantalla completa)
      const hd = Math.min(h * (0.995 - 0.275 * fe), w * 0.60);
      // On a Tesla the screen is taller than 0.6 of its width, so the dash cannot grow to fill it and the
      // leftover piled up at the bottom as bare carbon (Marc's photo). The dash floats in the middle of
      // whatever space it actually has — the whole screen when the row is folded away, the part above the
      // row when it is not — the way a real one sits in its binnacle.
      // …only once the row is folded away, though: with the row on screen the dash keeps its place at the
      // top and nothing below it moves.
      const topPad = (1 - fe) * Math.max(0, (h - hd) / 2);
      const hitBase = hits.current.length;
      b.save();
      b.translate(0, topPad);   // top 72 % — CAPPED by width so square/Tesla format never lets hd-sized text outgrow w-sized boxes (Marc's Oil temp / bottom overlaps)
      const dtv = lastVt ? Math.min(0.2, (t - lastVt) / 1000) : 0; lastVt = t;
      runT = s.engine === 'on' ? runT + dtv : Math.max(0, runT - dtv * 0.3);
      const c = getControls ? getControls() : { gearbox: 'auto' as const, style: 'auto' as const, resolvedStyle: 'sport' as const, lapRunning: false, lapHasData: false };
      const cranking = s.engine === 'cranking';
      if (cranking) crankUntil.current = t + 1000;   // the blue STARTER ON screen lingers 1 s after the catch
      const showStarter = cranking || t < crankUntil.current;
      const dark = 'rgba(0,0,0,0.62)';
      // the vitals' left edge, from the same numbers the keypad and the photo block use below
      const keysX0 = () => (w * 0.5 - 420 * (((w * 0.5 - w * 0.199361) / 420) * (w / h < 1.2 ? 0.92 : 1)));
      // Cosworth ICD typography: plain bold sans (Arial-like), not condensed
      const f = (px: number, wt = 600) => `${wt} ${Math.round(px * 0.88)}px "Inter", "Helvetica Neue", Arial, sans-serif`;
      // thick rounded grey frames like the real unit
      const cframe = (x: number, y: number, ww: number, hh: number, fill = 'rgba(0,0,0,0.6)', r = hd * 0.018) => {
        // a touch of relief: soft drop shadow under the frame, lit top rim, darker bottom rim
        b.save(); b.shadowColor = 'rgba(0,0,0,0.55)'; b.shadowBlur = hd * 0.02; b.shadowOffsetY = hd * 0.006;
        b.fillStyle = fill; rr(x, y, ww, hh, r); b.fill(); b.restore();
        const rim = b.createLinearGradient(0, y, 0, y + hh); rim.addColorStop(0, '#b4b7bc'); rim.addColorStop(0.5, '#8d8f93'); rim.addColorStop(1, '#5d5f63');
        b.strokeStyle = rim; b.lineWidth = Math.max(2, hd * 0.006); rr(x, y, ww, hh, r); b.stroke();
      };
      // carbon fiber = the SURROUND (page edges, top/bottom); the dash bezel itself stays black.
      // Painted over the WHOLE screen in screen coordinates, so the weave still reaches the top when the
      // dash is centred inside a taller screen.
      { const cp = carbon(); if (cp) { b.save(); b.translate(0, -topPad); b.fillStyle = cp; b.fillRect(0, -1, w, h + 2); b.restore(); } }
      // ---- the housing, with some depth (Marc: 'un pelín tridimensional'): shadow onto the carbon,
      //      a lit top edge / dark bottom edge, and the screen panel sunk into it ----
      b.save(); b.shadowColor = 'rgba(0,0,0,0.7)'; b.shadowBlur = hd * 0.06; b.shadowOffsetY = hd * 0.02;
      const hg = b.createLinearGradient(0, hd * 0.04, 0, hd * 0.97); hg.addColorStop(0, '#15171b'); hg.addColorStop(0.12, '#0c0d10'); hg.addColorStop(1, '#060709');
      b.fillStyle = hg; rr(w * 0.012, hd * 0.012, w * 0.976, hd * 0.975, w * 0.03); b.fill(); b.restore();
      const eg = b.createLinearGradient(0, hd * 0.04, 0, hd * 0.97); eg.addColorStop(0, 'rgba(255,255,255,0.22)'); eg.addColorStop(0.25, 'rgba(255,255,255,0.05)'); eg.addColorStop(1, 'rgba(0,0,0,0.6)');
      b.strokeStyle = eg; b.lineWidth = Math.max(1.5, hd * 0.004); rr(w * 0.012 + 1, hd * 0.012 + 1, w * 0.976 - 2, hd * 0.975 - 2, w * 0.03); b.stroke();
      // sunk screen panel: dark inset shadow along its top-left, faint lit bottom edge
      b.fillStyle = '#111216'; rr(w * 0.09, hd * 0.25, w * 0.82, hd * 0.66, w * 0.01); b.fill();
      b.save(); rr(w * 0.09, hd * 0.25, w * 0.82, hd * 0.66, w * 0.01); b.clip();
      const ig = b.createLinearGradient(0, hd * 0.25, 0, hd * 0.25 + hd * 0.08); ig.addColorStop(0, 'rgba(0,0,0,0.75)'); ig.addColorStop(1, 'rgba(0,0,0,0)');
      b.fillStyle = ig; b.fillRect(w * 0.09, hd * 0.25, w * 0.82, hd * 0.08);
      const ig2 = b.createLinearGradient(w * 0.09, 0, w * 0.09 + w * 0.03, 0); ig2.addColorStop(0, 'rgba(0,0,0,0.55)'); ig2.addColorStop(1, 'rgba(0,0,0,0)');
      b.fillStyle = ig2; b.fillRect(w * 0.09, hd * 0.25, w * 0.03, hd * 0.66);
      b.restore();
      b.strokeStyle = 'rgba(255,255,255,0.07)'; b.lineWidth = 1; rr(w * 0.09, hd * 0.25, w * 0.82, hd * 0.66, w * 0.01); b.stroke();
      // ---- shift lights exactly as in Marc's photos: 15 LEDs in a row across the top of the bezel,
      //      4 green · 2 yellow · 3 red (centre) · 2 yellow · 4 green, lit from the OUTSIDE in.
      //      Thresholds from the GT3 R manual: greens from 8400/8600, yellows 8800/9000, red 9200; blue flash at the limiter.
      // Marc's photos: 16 LEDs — 3 green · 3 yellow · 4 red · 3 yellow · 3 green, filling from the
      // OUTSIDE in; once complete they all flash BLUE and back to dark. LED #2 and #15 sit exactly over
      // the P and the E of the wordmark (that sets the proportion).
      const cols16 = ['#2bff4a','#2bff4a','#2bff4a','#ffd01a','#ffd01a','#ffd01a','#ff2222','#ff2222','#ff2222','#ff2222','#ffd01a','#ffd01a','#ffd01a','#2bff4a','#2bff4a','#2bff4a'];
      const wmWl = w * 0.40;
      const step = wmWl / 15.5, ledX0 = w * 0.5 - step * 7.5;   // Marc: tighter spacing
      // THE STRIP IS SEQUENCED IN THE DRIVETRAIN (ShiftLadder) and the box WAITS for it: one LED at a
      // time, the full strip held, the blue blinked, then the change, then black. The dash paints what it
      // is told, which is the only way the lights and the change can agree in every gear (Marc).
      const lit = s.shiftLights.stage;
      const ledsDark = s.shiftLights.dark;
      const atLim = s.shiftLights.blue;
      const blueOn = atLim && Math.floor((t / 1000) * 6.5 * 2) % 2 === 0;   // 6.5 Hz: a blink you can count (Marc)
      const ledR = hd * 0.014, ledY = hd * 0.125;
      for (let k = 0; k < 16; k++) {
        const side = Math.min(k, 15 - k);                          // 0 = outermost each side, 7 = centre reds
        // launch control: the two centre reds still unlit FLICKER red (irregular, like a real traction light)
        const launchFlick = s.launch && side === 7 && lit >= 7 && (Math.sin(t * 0.071) + Math.sin(t * 0.117) + Math.sin(t * 0.043)) > 0.2;   // only once the revs have ARRIVED (7 of 8 lit), not the instant you arm
        const on = ledsDark ? false : atLim ? blueOn : launchFlick || side < lit;
        const col = atLim ? '#3aa0ff' : cols16[k];
        const cx = ledX0 + step * k;
        if (on) { b.globalAlpha = 0.4; b.fillStyle = col; b.beginPath(); b.arc(cx, ledY, ledR * 1.7, 0, Math.PI * 2); b.fill(); b.globalAlpha = 1; }
        b.fillStyle = on ? col : 'rgba(255,255,255,0.09)'; b.beginPath(); b.arc(cx, ledY, ledR, 0, Math.PI * 2); b.fill();
      }
      // ---- launch wheelspin: the moment launch control releases (car rolling) the side LEDs flash
      //      PURPLE for the first 10 km/h, like the traction light on the real dash ----
      if (prevLaunch.current && !s.launch && s.speedKmh > 0.5) launchSlip.current = true;
      if (s.speedKmh >= 25 || s.engine !== 'on' || (s.speedKmh < 0.3 && !s.launch)) launchSlip.current = false;   // Marc: purple until 25 km/h
      prevLaunch.current = s.launch;
      const slipFlash = launchSlip.current && Math.floor((t / 1000) * 14) % 2 === 0;
      // ---- side blue LEDs (4 each): pit-limiter cue; purple wheelspin flash right after a launch ----
      for (let k = 0; k < 4; k++) {
        const y = hd * 0.29 + k * hd * 0.062;
        for (const cx of [w * 0.062, w * 0.938]) {
          const cap2 = (getControls ? getControls().pitCap : null);
          const on = slipFlash || (cap2 != null && s.gear === 1 && s.speedKmh >= cap2 - 6);
          const lc = launchSlip.current ? '#c63cff' : '#3a7bff';
          if (on) { b.globalAlpha = 0.45; b.fillStyle = lc; b.beginPath(); b.arc(cx, y, hd * 0.03, 0, Math.PI * 2); b.fill(); b.globalAlpha = 1; }
          b.fillStyle = on ? lc : launchSlip.current ? 'rgba(198,60,255,0.25)' : 'rgba(60,110,255,0.35)'; b.beginPath(); b.arc(cx, y, hd * 0.016, 0, Math.PI * 2); b.fill();
        }
      }
      {
        // OUR OWN WORD where the real display carries the marque's logotype (2026-09-10: the wordmark
        // is a registered mark and this is a paid product, so the WORD came off). 2026-09-14, Marc: the
        // same lettering as before, spelling ROARDRIVE: the logotype's own outlines for R, O and E and
        // A, D, I, V built to the same rules (roardriveWordmark.ts). Same place, same chrome relief; the
        // width keeps the cap height the seven-letter word had (9 letters, 1052 units vs 884).
        const wmW = w * 0.40 * (ROARDRIVE_WORDMARK_W / 884), wmY = hd * 0.205;
        const gr = b.createLinearGradient(0, wmY - hd * 0.022, 0, wmY + hd * 0.022);
        gr.addColorStop(0, '#f2f3f5'); gr.addColorStop(0.5, '#8e9298'); gr.addColorStop(0.55, '#63676e'); gr.addColorStop(1, '#cfd2d7');
        drawRoardriveWordmark(b, w * 0.5 + 1.5, wmY + hd * 0.006, wmW, 'rgba(0,0,0,0.75)');
        drawRoardriveWordmark(b, w * 0.5, wmY, wmW, gr, 'rgba(255,255,255,0.55)', 0.8);
      }
      // ---- top row: RACE 1 + headlight · speed · Lap · DRY ----
      const rowY = hd * 0.3, rowH = hd * 0.085;
      text('RACE 1', w * 0.112, rowY + rowH * 0.45, f(hd * 0.038, 600), '#ff2a2a', 'left', 'middle');
      { // headlight pictogram from Marc's dash photo: FIVE straight horizontal bars on the left and a hollow,
        // thick-stroked D (flat side left, round side right) — blue, glowing when the CAN says the lights are on
        const hx = w * 0.262, hy = rowY + rowH * 0.45, hr = hd * 0.021;   // a touch taller than the RACE 1 text (Marc)
        const lo = c.lightsOn;
        if (lo) { b.shadowColor = 'rgba(60,130,255,0.95)'; b.shadowBlur = hr * 1.6; }
        b.globalAlpha = lo === false ? 0.3 : 1;
        const blue = '#3a86ff', lw = Math.max(1.5, hd * 0.0075);   // thinner D stroke (Marc)
        b.strokeStyle = blue; b.lineWidth = lw; b.lineJoin = 'round'; b.lineCap = 'round';
        // D: flat vertical edge at x = hx, bulging right to about hx + 1.5 hr
        b.beginPath(); b.moveTo(hx, hy - hr); b.lineTo(hx + hr * 0.35, hy - hr);
        b.bezierCurveTo(hx + hr * 2.2, hy - hr * 1.22, hx + hr * 2.2, hy + hr * 1.22, hx + hr * 0.35, hy + hr);   // the round side pokes just past the top/bottom lines (Marc)   // stretched further right, more hollow (Marc)
        b.lineTo(hx, hy + hr); b.closePath(); b.stroke();
        // four bars, straight, evenly spaced across the lamp height, ending short of the D
        const x1 = hx - hr * 0.45, x0 = hx - hr * 1.75;
        b.lineWidth = Math.max(1.5, hd * 0.0055);   // bars thinner than the D so the gaps read
        for (let k = 0; k < 5; k++) { const y0 = hy - hr * 0.8 + k * (hr * 1.6 / 4); const ext = k === 0 || k === 4 ? hr * 0.14 : 0;   // first + last a touch longer, on the right side (Marc)
          b.beginPath(); b.moveTo(x0, y0); b.lineTo(x1 + ext, y0); b.stroke(); }
        b.lineCap = 'butt'; b.lineJoin = 'miter'; b.shadowBlur = 0; b.globalAlpha = 1;
      }
      const showGreen = false;   // Marc: launch must NOT turn the speed/gear boxes green (starter shows only the blue STARTER ON panel)
      const pitMode = c.pitCap != null;
      if (pitMode) {
        // pit limiter ON — the real dash format (Marc's photo): the TOP band (PORSCHE + rpm + Lap +
        // DRY) stays black, and everything below is ONE green screen reaching the four blue keys and
        // running under the TYRE box / Brake Bias, red-orange the instant you exceed the cap
        const capE = c.pitCap ?? 59;
        const shownE = Math.max(s.speedKmh, c.rawSpeedKmh ?? 0);
        b.fillStyle = shownE > capE + 0.5 ? '#e0501f' : '#84dc30';
        const gTop = rowY + rowH + hd * 0.012;
        const gX0 = keysX0();   // the vitals' left edge — the green covers Oil/Water…
        const gBotG = hd * 0.255 + 305 * hd * 0.0015;   // = Q(305): the TYRE box's top edge — the green touches it exactly (Marc), the tyre box never moves
        rr(gX0, gTop, w * 0.875 - gX0, gBotG - gTop, hd * 0.02); b.fill();   // …across gear + laptime, to the Laptime's right edge
      }
      // ===== the whole centre/right cluster is traced from Marc's ICD photo (888x502 crop): P()/Q() map photo
      //       pixels onto the dash — the crop spans from the vitals' left edge to the Laptime's right edge =====
      // THE GEAR AND THE SPEED SIT ON THE MIDDLE OF THE SCREEN (Marc). The gear box is P(290)…P(550), so
      // its centre is P(420): the scale U is solved from that, and the 2×2 keypad is given the room to
      // its left, a little further left and a little smaller than it was, exactly as Marc drew it. Any
      // screen shape lands the same way, instead of the block drifting right on a square Tesla.
      // On a screen taller than it is wide (the Tesla) the centre block was eating the row, and the four
      // keys were shrinking into each other to make room for it. There the block gives back 8 %, which
      // the keypad takes: the gear stays dead centre either way, because U is solved from that.
      const narrow = w / h < 1.2;
      const U = ((w * 0.5 - w * 0.199361) / 420) * (narrow ? 0.92 : 1);
      const X0 = w * 0.5 - 420 * U;
      // Marc: a little bigger, so LAUNCH sits in its key without being squeezed. The grey box does not
      // move — X0 is still solved from the centre — the keys simply take the air that was between them
      // and the vitals, and a little of the gap between themselves.
      const kx0 = w * 0.088;
      const ksz = Math.min(hd * 0.128, Math.max(0, X0 - w * 0.006 - kx0) / 2.13);
      const kgap = ksz * 0.13;
      const keysRight = kx0 + ksz * 2 + kgap;
      void keysRight;
      const V = hd * 0.0015, Y0 = hd * 0.255;
      const P = (px: number) => X0 + px * U, Q = (py: number) => Y0 + py * V;
      const GREY = '#9c9ea1';   // the one plate grey of the photo
      const plate = (x: number, y: number, ww: number, hh: number, r: number) => {   // ONE flat grey — no rim, no second tone (Marc)
        b.fillStyle = GREY; rr(x, y, ww, hh, r); b.fill();
      };
      const black = (x: number, y: number, ww: number, hh: number, r: number, _rim = '') => {   // plain black box, no grey edge
        b.fillStyle = '#08090b'; rr(x, y, ww, hh, r); b.fill();
      };
      const spX = P(290), spW = 260 * U;   // speed box exactly as wide as the gear box below it (Marc)
      cframe(spX, rowY, spW, 60 * V, showGreen ? '#3fd35a' : dark);
      // in pit mode the small top box shows the RPM (like the real page); speed moves into the big panel
      if (t - rpmShown.current.t > 120) rpmShown.current = { v: Math.round((s.rpmDisplay ?? s.rpm) / 5) * 5, t };
      text(String(Math.round(pitMode ? rpmShown.current.v : spd(s.speedKmh))), spX + spW / 2, Q(60), f(hd * 0.072, 500), showGreen ? '#0a2a12' : '#fff', 'center', 'middle');
      // photo: Lap plate x 585-748, y 40-85; black count box x 660-745
      plate(P(585), Q(40), 163 * U, 45 * V, hd * 0.012);
      text('Lap', P(605), Q(62), f(hd * 0.04, 400), '#fff', 'left', 'middle');
      black(P(660), Q(45), 85 * U, 35 * V, hd * 0.008, '#5a5c60');
      text(String(lap.lapCount), P(702), Q(62), f(hd * 0.045, 600), '#fff', 'center', 'middle');
      dryChip(w * 0.80, rowY, w * 0.075, rowH, Math.round(hd * 0.04));   // Marc: further right
      // ---- keypad (photo): square keys, thick light rounded frame, black face, blue back-lit legend
      //      (big value on top, small label below). 2×2: GEAR · MODE / LAUNCH · TYRE ----
      const key = (x: number, y: number, sz: number, value: string, label: string, lit: boolean, action: DashAction) => {
        b.fillStyle = '#0b0c0e'; rr(x, y, sz, sz, sz * 0.14); b.fill();
        b.strokeStyle = '#cfd3d8'; b.lineWidth = Math.max(2, sz * 0.07); rr(x + sz * 0.05, y + sz * 0.05, sz * 0.9, sz * 0.9, sz * 0.12); b.stroke();
        b.fillStyle = '#050608'; rr(x + sz * 0.12, y + sz * 0.12, sz * 0.76, sz * 0.76, sz * 0.08); b.fill();
        const col = lit ? '#6d8dff' : '#2f4fd0';
        b.shadowColor = lit ? 'rgba(80,120,255,0.95)' : 'rgba(60,90,220,0.5)'; b.shadowBlur = sz * (lit ? 0.18 : 0.08);
        // the legend is FITTED, not guessed: it shrinks until it lives inside its own key, so two keys
        // can never run into each other (Marc's photo on the Tesla)
        const fit = (txt: string, px: number, maxW: number, weight: number) => {
          let size = Math.max(8, Math.round(px));
          for (let i = 0; i < 12; i++) {
            b.font = `${weight} ${size}px "Inter", Arial, sans-serif`;
            if (b.measureText(txt).width <= maxW || size <= 8) break;
            size--;
          }
          return `${weight} ${size}px "Inter", Arial, sans-serif`;
        };
        const inner = sz * 0.80;
        text(value, x + sz / 2, y + sz * 0.43, fit(value, sz * (value.length > 3 ? 0.24 : 0.34), inner, 800), col, 'center', 'middle');
        text(label, x + sz / 2, y + sz * 0.72, fit(label, sz * 0.15, inner, 700), col, 'center', 'middle');
        b.shadowBlur = 0;
        hits.current.push({ x, y, w: sz, h: sz, action });
      };
      const ky0 = hd * 0.38;
      key(kx0, ky0, ksz, c.following ? 'CAR' : c.gearbox === 'auto' ? 'AUTO' : 'MAN', 'GEAR', !c.following && c.gearbox === 'manual', c.following ? 'gearbox:auto' : c.gearbox === 'auto' ? 'gearbox:manual' : 'gearbox:auto');
      key(kx0 + ksz + kgap, ky0, ksz, c.resolvedStyle === 'sport' ? 'RACE' : 'DRIVE', 'MODE', c.resolvedStyle === 'sport', c.resolvedStyle === 'sport' ? 'style:eco' : 'style:sport');
      key(kx0, ky0 + ksz + kgap, ksz, ((getControls ? getControls().wet : null) ?? wet.current) ? 'WET' : 'DRY', 'TYRE', (getControls ? getControls().wet : null) ?? wet.current, 'wet:toggle');   // Marc: TYRE left, LAUNCH right
      key(kx0 + ksz + kgap, ky0 + ksz + kgap, ksz, s.launch ? 'ON' : 'OFF', 'LAUNCH', s.launch, 'launch:toggle');
      const gx = P(290), gw = 260 * U, gy = Q(92), gh = 213 * V;   // photo: gear box x 290-550, y 92-305
      if (pitMode) {
        // ---- pit page, PIXEL-matched to Marc's dash photo: elements drawn straight on the green —
        //      'Speed' + big number left, gear HUGE centre, 'Limit' in its own outlined box under
        //      Speed; the Go! Go! Go! box lives top-right (drawn in the laptime corner) ----
        const cap = c.pitCap ?? 59;
        const shown = Math.max(s.speedKmh, c.rawSpeedKmh ?? 0);
        const over = shown > cap + 0.5;
        const ink = over ? '#fff' : '#12300a';
        const gX0p = keysX0();
        const gTopP = rowY + rowH + hd * 0.012;
        const gBot = Q(305);   // the green's bottom edge = the tyre box's top
        text('Speed', gX0p + w * 0.025, gTopP + hd * 0.045, f(hd * 0.045, 600), ink, 'left', 'middle');
        // Limit — its OWN outlined box, pinned to the green's bottom edge
        const limY = gBot - hd * 0.09;
        // the big speed digits live BETWEEN the label and the Limit box — they must not touch either (Marc's photo)
        text(String(Math.round(spd(shown))), gX0p + w * 0.052, (gTopP + hd * 0.07 + limY) / 2 + hd * 0.005, f(hd * 0.14, 300), ink, 'center', 'middle');
        text(s.engine !== 'on' ? 'N' : s.selector === 'R' ? 'R' : s.neutral ? 'N' : String(s.gear), spX + spW / 2, (gTopP + gBot) / 2, f(hd * 0.26, 500), ink, 'center', 'middle');   // dead-centred under the rpm '0' box (spX + spW/2)
        b.strokeStyle = ink; b.lineWidth = Math.max(2, hd * 0.006);
        b.fillStyle = over ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.55)';
        rr(gX0p + w * 0.018, limY, w * 0.115, hd * 0.075, hd * 0.014); b.fill(); b.stroke();
        text(`Limit ${Math.round(spd(cap))}`, gX0p + w * 0.018 + w * 0.0575, limY + hd * 0.0375, f(hd * 0.052, 600), ink, 'center', 'middle');
      } else {
        // ---- vitals tiles: Oil temp / Oil press / Water temp / Water press ----
        // base values from Marc's real-dash photo: 101.0 / 5.9 / 87.0 / 2.1 (pressures read 0 with the engine off)
        const oilT = 101.0, oilP = s.engine === 'on' ? 5.9 : 0;
        const watT = 87.0, watP = s.engine === 'on' ? 2.1 : 0;
        const vit: [string, string][] = [['Oil temp', oilT.toFixed(1)], ['Oil press', oilP.toFixed(1)], ['Water temp', watT.toFixed(1)], ['Water press', watP.toFixed(1)]];
        // photo: vitals x 0-283, y 90-300 — grey plate, the values in a black box x 155-283 on the right
        const vx = P(0), vw = P(290) - vx, vy0 = Q(90), vH = 210 * V, lw = 155 * U, vh = vH / 4;   // right edge = the gear box's left edge (touching)
        plate(vx, vy0, vw, vH, hd * 0.012);
        black(vx + lw, vy0 + 3 * V, vw - lw - 3 * U, vH - 6 * V, hd * 0.008, '#5a5c60');
        let lf = hd * 0.036; b.font = f(lf, 400);
        const longest = Math.max(...vit.map(([k]) => b.measureText(k).width));
        if (longest > lw * 0.94) lf *= (lw * 0.94) / longest;   // one size for the four labels
        vit.forEach(([k, v], r) => {
          const y = vy0 + vh * (r + 0.5);
          text(k, vx + lw / 2, y, f(lf, 400), '#f4f4f2', 'center', 'middle');
          text(v, vx + lw + (vw - lw) / 2, y, f(hd * 0.05, 600), '#fff', 'center', 'middle');   // centred in the black column (Marc)
        });
        // (TC-LA / TC-LO / ABS chips removed — Marc)
        // ---- centre: gear ----
        cframe(gx, gy, gw, gh, showGreen ? '#3fd35a' : dark);
        text(s.engine !== 'on' ? 'N' : s.selector === 'R' ? 'R' : s.neutral ? 'N' : String(s.gear), gx + gw / 2, gy + gh * 0.52, f(gh * 0.82, 400), showGreen ? '#0a2a12' : s.shifting ? 'rgba(255,255,255,0.55)' : '#fff', 'center', 'middle');
      }
      // ---- TYRE PRESS box exactly like Marc's photo: black rounded box, thin amber rim, amber
      //      'TYRE PRESS' header tab across the top, four bold pressures in a 2x2 — no divider, no temps ----
      // photo: TYRE PRESS x 290-550, y 308-430; amber tab x 315-525, y 308-330
      const ty = gy + gh, th = 122 * V;   // TOUCHING the gear box's bottom edge — and it stays put in pit mode, the green comes down to meet it (Marc)
      b.fillStyle = '#08090b'; rr(gx, ty, gw, th, hd * 0.012); b.fill();
      b.strokeStyle = '#e8b400'; b.lineWidth = Math.max(2, hd * 0.005); rr(gx, ty, gw, th, hd * 0.012); b.stroke();
      b.fillStyle = '#e8b400'; rr(gx + 25 * U, ty, gw - 50 * U, th * 0.18, hd * 0.006); b.fill();
      text('TYRE PRESS', gx + gw / 2, ty + th * 0.09, f(hd * 0.032, 400), '#2a2000', 'center', 'middle');
      const warm = Math.min(1, runT / 300) * 0.12 + Math.min(0.08, s.speedKmh / 2500);
      const tp = (c.tires && c.tires.length === 4 ? c.tires : [1.89 + warm, 1.84 + warm, 1.91 + warm, 1.91 + warm]).map((v) => v.toFixed(2));   // real TPMS when the CAN has it; the photo values otherwise
      const tpx = [gx + gw * 0.3, gx + gw * 0.71], tpy = [ty + th * 0.4, ty + th * 0.78];
      text(tp[0], tpx[0], tpy[0], f(hd * 0.052, 600), '#fff', 'center', 'middle'); text(tp[1], tpx[1], tpy[0], f(hd * 0.052, 600), '#fff', 'center', 'middle');
      text(tp[2], tpx[0], tpy[1], f(hd * 0.052, 600), '#fff', 'center', 'middle'); text(tp[3], tpx[1], tpy[1], f(hd * 0.052, 600), '#fff', 'center', 'middle');
      // ---- right: Laptime / Time Diff / Pred. Time (or STARTER ON while cranking) ----
      const rx = P(550), rw = w * 0.875 - rx, ry = Q(95), rh = 210 * V;   // starts AT the gear box's right edge (touching), wider (Marc)
      if (c.wiperMsg && !showStarter) {
        // WIPER panel exactly like the photo: a solid blue plate over the Laptime block, 'WIPER' + state in light sans
        b.fillStyle = '#3f8cff'; rr(rx, ry, rw, rh, hd * 0.014); b.fill();
        text('WIPER', rx + rw / 2, ry + rh * 0.3, f(hd * 0.07, 400), '#eef3ff', 'center', 'middle');
        text(c.wiperMsg, rx + rw / 2, ry + rh * 0.68, f(hd * 0.07, 400), '#eef3ff', 'center', 'middle');
      } else if (showStarter) {
        b.fillStyle = '#2f6bff'; rr(rx, ry + rh * 0.15, rw, rh * 0.7, 4); b.fill();
        text('STARTER', rx + rw / 2, ry + rh * 0.4, f(hd * 0.085, 600), '#fff', 'center', 'middle');
        text('ON', rx + rw / 2, ry + rh * 0.68, f(hd * 0.085, 600), '#fff', 'center', 'middle');
      } else if (c.pitCap != null) {
        // Go! Go! Go! + pit-exit countdown, its own outlined box (photo, top-right of the green)
        const overG = Math.max(s.speedKmh, c.rawSpeedKmh ?? 0) > (c.pitCap ?? 59) + 0.5;
        const inkG = overG ? '#fff' : '#12300a';
        b.strokeStyle = inkG; b.lineWidth = Math.max(2, hd * 0.006);
        b.fillStyle = overG ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.5)';
        rr(rx + rw * 0.12, ry + rh * 0.06, rw * 0.76, rh * 0.42, hd * 0.012); b.fill(); b.stroke();
        text('Go! Go! Go!', rx + rw / 2, ry + rh * 0.17, f(hd * 0.036, 700), inkG, 'center', 'middle');
        text((220 - (runT % 220)).toFixed(2), rx + rw / 2, ry + rh * 0.36, mono(Math.round(hd * 0.055)), inkG, 'center', 'middle');
      } else {
        // ---- Laptime block, same structure/colours as the real ICD photo: grey framed box, grey
        //      'Laptime' header pill, big time, then 'Time Diff' + 'Best Lap' pills over black value boxes ----
        if (lap.lapCount !== lapSeen.current.count) {
          // a lap just completed: Time Diff = this lap vs the best BEFORE it (0.000 on the first / a new best shows negative)
          const prevBest = lapSeen.current.best;
          lapDiff.current = prevBest != null && lap.lastS != null ? lap.lastS - prevBest : 0;
          lapSeen.current = { count: lap.lapCount, best: lap.bestS };
        }
        // the whole block is a GREY plate (photo): thin lighter-grey header strips, the times in black
        // rounded boxes with a light rim; same plain sans as the rest of the ICD (NOT a mono font);
        // empty values read 0:00.00 like a freshly powered unit
        const lapF = (px: number) => `600 ${Math.round(px)}px "Inter", "Helvetica Neue", Arial, sans-serif`;
        const lt = (v: number | null) => (v == null ? '0:00.00' : formatLap(v));
        const lab = (x: number, y: number, label: string) => text(label, x, y, f(hd * 0.036, 400), '#f4f4f2', 'center', 'middle');
        plate(rx, ry, rw, rh, hd * 0.014);
        const m = 8 * U, cx = rx + rw / 2;
        lab(cx, Q(112), 'Laptime');
        black(rx + m, Q(130), rw - m * 2, 70 * V, hd * 0.008);
        text(lt(lap.currentS), cx, Q(166), lapF(hd * 0.068), '#fff', 'center', 'middle');
        const d = lapDiff.current;
        const diffTxt = `${d < 0 ? '-' : ''}${Math.abs(d).toFixed(3)}`;
        const lwd = (rw - m * 3) * 0.44, rwd = (rw - m * 3) * 0.56;   // photo: Time Diff box narrower than Best Lap
        const lx = rx + m, rxx = rx + m * 2 + lwd;
        lab(lx + lwd / 2, Q(220), 'Time Diff'); lab(rxx + rwd / 2, Q(220), 'Best Lap');
        black(lx, Q(240), lwd, 60 * V, hd * 0.008);
        black(rxx, Q(240), rwd, 60 * V, hd * 0.008);
        text(diffTxt, lx + lwd / 2, Q(271), lapF(hd * 0.046), d < 0 ? '#2bd84a' : '#fff', 'center', 'middle');   // smaller (Marc)
        text(lt(lap.bestS), rxx + rwd / 2, Q(271), lapF(hd * 0.046), '#fff', 'center', 'middle');
      }
      // photo: Brake Bias plate x 573-843, y 345-390; black value box x 730-843 (pit page: below the green)
      { // Brake Bias: equal margins left and right inside the plate, and the plate grows with the label so a
        // narrow (square / Tesla) screen never clips it (Marc)
        const bbx = rx + 10 * U, bbh = 48 * V, bby = pitMode ? hd * 0.795 - bbh / 2 : ty + th / 2 - bbh / 2;   // at the TYRE PRESS box's vertical middle (Marc)
        const mg = 12 * U, vbw = 113 * U, gap = mg, avail = w * 0.875 - bbx;   // Marc: gap label→box == the outer margins
        let lf = hd * 0.04; b.font = f(lf, 400); let labW = b.measureText('Brake Bias').width;
        while (mg * 2 + labW + gap + vbw > avail && lf > hd * 0.024) { lf *= 0.94; b.font = f(lf, 400); labW = b.measureText('Brake Bias').width; }
        const bbw = Math.min(avail, mg * 2 + labW + gap + vbw);
        plate(bbx, bby, bbw, bbh, hd * 0.012);
        text('Brake Bias', bbx + mg, bby + bbh / 2, f(lf, 400), '#f4f4f2', 'left', 'middle');
        const vbx = bbx + bbw - mg - vbw;
        black(vbx, bby + 8 * V, vbw, bbh - 16 * V, hd * 0.007);   // clearly INSIDE the grey plate
        text('-3.00', vbx + vbw / 2, bby + bbh / 2, f(Math.min(hd * 0.04, lf * 1.05), 600), '#fff', 'center', 'middle'); }
      // (green LAUNCH status text removed — the LAUNCH key's own backlight already shows the state, Marc)

      // ================= bottom row: the two separate units, as in the photos =================
      const by0 = h * 0.745, bh0 = h * 0.235;
      // grey icon column right of the Laptime block, under DRY (Marc's photo: the ⚠ and ◎ spot):
      // show / hide the engine START, the lap timer, and the HUD menu
      {
        // ONE control down here and it is a chevron, dead centre (Marc): up while the bottom row is on
        // the dash, down once it is folded away. No box around it — the target is bigger than the mark.
        // sized off the OPEN dash, never off the live hd: folded, hd grows to the whole screen and the
        // chevron used to swell with it (Marc). Its size is the one thing here that must not move.
        const hdRef = Math.min(h * 0.72, w * 0.60);
        const cy = (wantOpen ? h * 0.737 : h - hdRef * 0.052) - topPad;
        const cw = hdRef * 0.062;
        b.globalAlpha = 1;
        b.strokeStyle = 'rgba(210, 214, 222, 0.62)';
        b.lineWidth = Math.max(2, hdRef * 0.005);
        b.lineCap = 'round'; b.lineJoin = 'round';
        b.beginPath();
        if (wantOpen) { b.moveTo(w * 0.5 - cw / 2, cy + cw * 0.2); b.lineTo(w * 0.5, cy - cw * 0.2); b.lineTo(w * 0.5 + cw / 2, cy + cw * 0.2); }
        else { b.moveTo(w * 0.5 - cw / 2, cy - cw * 0.2); b.lineTo(w * 0.5, cy + cw * 0.2); b.lineTo(w * 0.5 + cw / 2, cy - cw * 0.2); }
        b.stroke();
        b.lineCap = 'butt'; b.lineJoin = 'miter';
        // pressed with a thumb, in a moving car
        hits.current.push({ x: w * 0.5 - cw * 1.6, y: cy - cw * 1.3, w: cw * 3.2, h: cw * 2.6, action: 'ui:bottom' });
      }
      // the bottom row travels: it slides down out of the screen as it fades, and back up as it returns
      const slide = (1 - fe) * h * 0.30;
      b.save();
      b.translate(0, slide);
      b.globalAlpha = fe;   // bottom units fade with the fold
      if (showStart && !showLap) {
        // START/STOP alone = a POP-UP over the full-height dash (bottom-left), dim plate behind it
        b.fillStyle = 'rgba(6,7,9,0.82)'; rr(w * 0.06, h * 0.7, w * 0.27, h * 0.29, w * 0.012); b.fill();
        b.strokeStyle = 'rgba(255,255,255,0.14)'; b.lineWidth = 1; rr(w * 0.06, h * 0.7, w * 0.27, h * 0.29, w * 0.012); b.stroke();
      }

      // ---- START / STOP exactly like Marc's photo: grey square bracket open at the top, matte yellow
      //      ball low inside it, NAVY plate with a glowing bright-blue border reading START/ STOP sitting
      //      on top. Beside it the PIT button: same construction, RED ball. ----
      {
        const unit = (bx: number, cap: '#e9c866' | '#e0211a', label1: string, label2: string, glow: boolean, action: DashAction, pressed: boolean) => {
          const bw = Math.min(w * 0.14, bh0 * 0.74), by = by0 + bh0 * 0.2, bh = bw * 1.0;   // Marc: deeper bracket (bottom line lower); height-capped so it stays on canvas
          // plate geometry first — the bracket stubs must reach its edges
          const pw = bw * 0.96, ph = bh * 0.52, px0 = bx + bw / 2 - pw / 2, py0 = by - ph / 2;   // the bracket hooks at the plate's middle
          // bracket: three sides + horizontal stubs at the top that TOUCH the plate (Marc's photo).
          // Marc: the two white vertical lines sit a bit further apart than the plate → bracket is wider than the plate
          const bwx = bw * 1.14, bxl = bx + bw / 2 - bwx / 2, bxr = bxl + bwx;
          b.strokeStyle = '#c9ccd1'; b.lineWidth = Math.max(2.5, h * 0.006); b.lineJoin = 'round';
          b.beginPath(); b.moveTo(px0 + pw * 0.08, by); b.lineTo(bxl, by); b.lineTo(bxl, by + bh); b.lineTo(bxr, by + bh); b.lineTo(bxr, by); b.lineTo(px0 + pw * 0.92, by); b.stroke();
          // ball — with the dark round bezel around it (same construction as the PIT button): a raised round push-button
          const capR = bw * 0.25, capX = bx + bw / 2, capY = by + bh * 0.63;   // ball + its bezel ring sit fully BELOW the plate (plate bottom = by + 0.26·bw; ring top = by + 0.295·bw)   // the ball sits LOW — clear of the plate
          const base = cap === '#e0211a';
          b.fillStyle = 'rgba(0,0,0,0.5)'; b.beginPath(); b.arc(capX + capR * 0.06, capY + capR * 0.12, capR * 1.36, 0, Math.PI * 2); b.fill();
          const bez = b.createRadialGradient(capX, capY, capR * 1.0, capX, capY, capR * 1.34);
          bez.addColorStop(0, '#0c0d10'); bez.addColorStop(0.75, '#1c1e23'); bez.addColorStop(1, '#3a3d44');
          b.fillStyle = bez; b.beginPath(); b.arc(capX, capY, capR * 1.34, 0, Math.PI * 2); b.fill();
          b.strokeStyle = 'rgba(255,255,255,0.22)'; b.lineWidth = Math.max(1, capR * 0.05); b.beginPath(); b.arc(capX, capY, capR * 1.34, 0, Math.PI * 2); b.stroke();
          const g1 = b.createRadialGradient(capX - capR * 0.32, capY - capR * 0.38, capR * 0.15, capX, capY, capR);
          if (base) { g1.addColorStop(0, '#ff5a4e'); g1.addColorStop(0.7, '#d21c14'); g1.addColorStop(1, '#8d120d'); }
          else { g1.addColorStop(0, '#efd070'); g1.addColorStop(0.7, '#d2ab45'); g1.addColorStop(1, '#a3842f'); }
          b.fillStyle = 'rgba(0,0,0,0.55)'; b.beginPath(); b.arc(capX + capR * 0.05, capY + capR * 0.1, capR * 1.04, 0, Math.PI * 2); b.fill();
          b.fillStyle = g1; b.beginPath(); b.arc(capX, capY, capR, 0, Math.PI * 2); b.fill();
          if (pressed) { b.fillStyle = 'rgba(255,255,255,0.25)'; b.beginPath(); b.arc(capX, capY, capR, 0, Math.PI * 2); b.fill(); }
          // navy plate with the glowing blue rim, sitting on the bracket top
          b.save(); b.translate(px0 + pw / 2, py0 + ph / 2);
          b.fillStyle = '#0e1a55'; rr(-pw / 2, -ph / 2, pw, ph, ph * 0.22); b.fill();
          b.shadowColor = glow ? 'rgba(80,130,255,0.95)' : 'rgba(70,110,255,0.55)'; b.shadowBlur = ph * (glow ? 0.5 : 0.22);
          b.strokeStyle = '#4d7dff'; b.lineWidth = Math.max(1.5, ph * 0.07); rr(-pw / 2, -ph / 2, pw, ph, ph * 0.22); b.stroke();
          b.shadowBlur = 0;
          const fpx = Math.round(ph * 0.36);
          text(label1, -pw * 0.08, -ph * 0.2, `800 ${fpx}px "Inter", Arial, sans-serif`, '#cfe0ff', 'center', 'middle');
          text(label2, pw * 0.14, ph * 0.24, `800 ${fpx}px "Inter", Arial, sans-serif`, '#cfe0ff', 'center', 'middle');
          b.restore();
          hits.current.push({ x: bxl - bw * 0.1, y: py0 - bh * 0.05, w: bwx + bw * 0.2, h: (by + bh) - py0 + bh * 0.15, action });
        };
        // equidistant at ANY aspect: gap(bracket right edge → centre chevron) == gap(chevron → Racelogic left edge)
        const rlGap = (w * 0.883 - w * 0.31) - w * 0.5;
        const bw0 = Math.min(w * 0.14, bh0 * 0.74);
        if (showStart) unit(w * 0.5 - rlGap - bw0 * 1.07, '#e9c866', 'START/', 'STOP', s.engine === 'on' || cranking, 'engine:press', cranking);
      }
      b.restore();   // …the PIT button belongs to the dash, not to the row that folds away
      { // PIT: round red button with white PIT letters — top right, on the bezel, outside the glass
        b.globalAlpha = 1;
        const pr = hd * 0.05, pcx = w * 0.935, pcy = hd * 0.135;
        const on = (getControls ? getControls().pitCap : null) != null;
        b.fillStyle = '#141518'; b.beginPath(); b.arc(pcx, pcy, pr * 1.35, 0, Math.PI * 2); b.fill();
        const pg = b.createRadialGradient(pcx - pr * 0.3, pcy - pr * 0.35, pr * 0.2, pcx, pcy, pr);
        pg.addColorStop(0, on ? '#ff6a5e' : '#f0554a'); pg.addColorStop(0.7, '#d21c14'); pg.addColorStop(1, '#8d120d');
        if (on) { b.shadowColor = 'rgba(255,70,50,0.9)'; b.shadowBlur = pr * 0.8; }
        b.fillStyle = pg; b.beginPath(); b.arc(pcx, pcy, pr, 0, Math.PI * 2); b.fill();
        b.shadowBlur = 0;
        text('PIT', pcx, pcy, `800 ${Math.round(pr * 0.72)}px "Inter", Arial, sans-serif`, '#fff', 'center', 'middle');
        hits.current.push({ x: pcx - pr * 1.5, y: pcy - pr * 1.5, w: pr * 3, h: pr * 3, action: 'pit:toggle' });
      }
      // ---- RACELOGIC: no screen, no timer text — three HORIZONTAL buttons: START · LAP · RESET ----
      b.save();
      b.translate(0, slide);
      b.globalAlpha = fe;
      if (showLap) {
        // Marc: mirror of START/STOP — same margin to the right edge as START/STOP has to the left (its bracket starts at ~0.117 w)
        const uw = w * 0.31, ux = w * 0.883 - w * 0.31, uy = by0 + bh0 * 0.04, uh = bh0 * 0.92;   // centred-left: the bottom-right corner stays free for the round controls (Marc)
        const c = getControls ? getControls() : { lapRunning: false, lapHasData: false };
        // ---- 3D body (Marc's product photo): shadow on the carbon, bevelled blue case with a lit top
        //      edge and dark bottom, a gloss sweep across the top half, and the inner panel SUNK in ----
        b.save(); b.shadowColor = 'rgba(0,0,0,0.75)'; b.shadowBlur = uh * 0.12; b.shadowOffsetY = uh * 0.05;
        const body = b.createLinearGradient(0, uy, 0, uy + uh); body.addColorStop(0, '#3f7ad8'); body.addColorStop(0.5, '#2d63c4'); body.addColorStop(1, '#1d4593');
        b.fillStyle = body; rr(ux, uy, uw, uh, uh * 0.16); b.fill(); b.restore();
        const bev = b.createLinearGradient(0, uy, 0, uy + uh); bev.addColorStop(0, 'rgba(255,255,255,0.7)'); bev.addColorStop(0.3, 'rgba(255,255,255,0.12)'); bev.addColorStop(1, 'rgba(0,0,0,0.55)');
        b.strokeStyle = bev; b.lineWidth = Math.max(2, uh * 0.012); rr(ux + 1, uy + 1, uw - 2, uh - 2, uh * 0.16); b.stroke();
        b.save(); rr(ux, uy, uw, uh, uh * 0.16); b.clip();
        const gloss = b.createLinearGradient(0, uy, 0, uy + uh * 0.5); gloss.addColorStop(0, 'rgba(255,255,255,0.22)'); gloss.addColorStop(1, 'rgba(255,255,255,0)');
        b.fillStyle = gloss; b.fillRect(ux, uy, uw, uh * 0.5); b.restore();
        // sunk inner panel: dark inset shadow at its top/left, faint lit bottom edge
        b.fillStyle = '#1e3f86'; rr(ux + uw * 0.02, uy + uh * 0.06, uw * 0.96, uh * 0.88, uh * 0.12); b.fill();
        b.save(); rr(ux + uw * 0.02, uy + uh * 0.06, uw * 0.96, uh * 0.88, uh * 0.12); b.clip();
        const ins = b.createLinearGradient(0, uy + uh * 0.06, 0, uy + uh * 0.22); ins.addColorStop(0, 'rgba(0,0,0,0.5)'); ins.addColorStop(1, 'rgba(0,0,0,0)');
        b.fillStyle = ins; b.fillRect(ux, uy + uh * 0.06, uw, uh * 0.16);
        const ins2 = b.createLinearGradient(ux + uw * 0.02, 0, ux + uw * 0.06, 0); ins2.addColorStop(0, 'rgba(0,0,0,0.4)'); ins2.addColorStop(1, 'rgba(0,0,0,0)');
        b.fillStyle = ins2; b.fillRect(ux + uw * 0.02, uy, uw * 0.04, uh);
        b.restore();
        b.strokeStyle = 'rgba(255,255,255,0.18)'; b.lineWidth = 1; rr(ux + uw * 0.02, uy + uh * 0.06, uw * 0.96, uh * 0.88, uh * 0.12); b.stroke();
        // raised keys: shadow under each, graded face, lit top rim
        const key3d = (x: number, y: number, sz: number) => {
          b.save(); b.shadowColor = 'rgba(0,0,0,0.7)'; b.shadowBlur = sz * 0.18; b.shadowOffsetY = sz * 0.08;
          const kg = b.createLinearGradient(0, y, 0, y + sz); kg.addColorStop(0, '#2a2d33'); kg.addColorStop(1, '#0f1013');
          b.fillStyle = kg; rr(x, y, sz, sz, sz * 0.2); b.fill(); b.restore();
          const kr = b.createLinearGradient(0, y, 0, y + sz); kr.addColorStop(0, 'rgba(255,255,255,0.35)'); kr.addColorStop(1, 'rgba(0,0,0,0.6)');
          b.strokeStyle = kr; b.lineWidth = Math.max(1, sz * 0.04); rr(x + 0.5, y + 0.5, sz - 1, sz - 1, sz * 0.2); b.stroke();
        };
        const bsz = uh * 0.21, by2 = uy + uh * 0.44;
        const bxs = [ux + uw * 0.1, ux + uw * 0.42, ux + uw * 0.74];
        // START (red square) · LAP (OK) · RESET (▲▼)
        key3d(bxs[0], by2, bsz);
        { const rg = b.createLinearGradient(0, by2, 0, by2 + bsz); rg.addColorStop(0, '#ff5a4e'); rg.addColorStop(1, '#b0150f');
          b.fillStyle = rg; rr(bxs[0] + bsz * 0.12, by2 + bsz * 0.12, bsz * 0.76, bsz * 0.76, bsz * 0.15); b.fill();
          b.strokeStyle = 'rgba(255,255,255,0.35)'; b.lineWidth = 1; rr(bxs[0] + bsz * 0.12, by2 + bsz * 0.12, bsz * 0.76, bsz * 0.76, bsz * 0.15); b.stroke(); }
        hits.current.push({ x: bxs[0] - bsz * 0.2, y: by2 - bsz * 0.2, w: bsz * 1.4, h: bsz * 1.4, action: c.lapRunning ? 'lap:stop' : 'lap:start' });
        key3d(bxs[1], by2, bsz);
        text('OK', bxs[1] + bsz / 2, by2 + bsz / 2, f(bsz * 0.42, 700), '#fff', 'center', 'middle');
        hits.current.push({ x: bxs[1] - bsz * 0.2, y: by2 - bsz * 0.2, w: bsz * 1.4, h: bsz * 1.4, action: 'lap:mark' });
        key3d(bxs[2], by2, bsz);
        text('▲', bxs[2] + bsz / 2, by2 + bsz * 0.32, f(bsz * 0.34, 700), '#fff', 'center', 'middle');
        text('▼', bxs[2] + bsz / 2, by2 + bsz * 0.7, f(bsz * 0.34, 700), '#fff', 'center', 'middle');
        hits.current.push({ x: bxs[2] - bsz * 0.2, y: by2 - bsz * 0.2, w: bsz * 1.4, h: bsz * 1.4, action: 'lap:reset' });
        text(c.lapRunning ? 'STOP' : 'START', bxs[0] + bsz / 2, uy + uh * 0.72, f(bsz * 0.3, 700), '#dfe6ff', 'center', 'middle');
        text('LAP', bxs[1] + bsz / 2, uy + uh * 0.72, f(bsz * 0.3, 700), '#dfe6ff', 'center', 'middle');
        text('RESET', bxs[2] + bsz / 2, uy + uh * 0.72, f(bsz * 0.3, 700), '#dfe6ff', 'center', 'middle');
        // ---- Display (Marc): the real VBOX LapTimer has a monochrome OLED — black glass flush in the fascia,
        //      crisp white pixels — with a row of 6 delta LEDs (3 green · 3 red) above it. Row 1: the circuit
        //      the GPS detected, creeping slowly (rest 10 s → creep → rest 3 s). Row 2: lap / laptime, or GPS state. ----
        {
          const lx = ux + uw * 0.075, ly = uy + uh * 0.11, lw = uw * 0.85, lh = uh * 0.25;
          // recessed window in the case: shadow on the top lip, lit bottom lip (light from above)
          const m = lh * 0.12;
          const lip = b.createLinearGradient(lx, ly - m, lx, ly + lh + m); lip.addColorStop(0, '#16307a'); lip.addColorStop(0.5, '#2a52b0'); lip.addColorStop(1, '#8fb0f0');
          b.fillStyle = lip; rr(lx - m, ly - m, lw + m * 2, lh + m * 2, m * 1.2); b.fill();
          const lip2 = b.createLinearGradient(lx, ly - m * 0.55, lx, ly + lh + m * 0.55); lip2.addColorStop(0, '#04060c'); lip2.addColorStop(1, '#141a2a');
          b.fillStyle = lip2; rr(lx - m * 0.55, ly - m * 0.55, lw + m * 1.1, lh + m * 1.1, m * 0.9); b.fill();
          // glass
          const bg = b.createLinearGradient(lx, ly, lx, ly + lh); bg.addColorStop(0, '#070a12'); bg.addColorStop(1, '#02030a');
          b.fillStyle = bg; rr(lx, ly, lw, lh, lh * 0.06); b.fill();
          b.save(); rr(lx, ly, lw, lh, lh * 0.07); b.clip();
          // ONE row, as big as the glass allows (Marc: it has to be readable on a small phone)
          const pad = lh * 0.12, inner = lw - pad * 2;
          let fpx = Math.round(lh * 0.74);
          const glassFont = (px: number) => `700 ${px}px "JetBrains Mono", "Courier New", monospace`;
          b.font = glassFont(fpx);
          b.textAlign = 'left'; b.textBaseline = 'middle';
          // On a circuit but not timing yet: the screen says the track it knows you are on and, every few
          // seconds, what it is waiting for. The timer starts itself when you cross the line (Marc).
          // The word CIRCUIT always leads (Marc): what follows it is a name, and on a lap timer you have
          // to know at a glance that the name IS the track. Only the connector goes, so
          // 'Circuit de Barcelona-Catalunya' reads CIRCUIT BARCELONA-CATALUNYA and 'Autodromo Nazionale
          // Monza' reads CIRCUIT MONZA.
          const shortTrack = (n: string) => `CIRCUIT ${n
            .replace(/^(circuit|circuito|aut[oó]dromo|autodrom)\b\s*(de la|de|del|do|of|nazionale)?\s*/i, '')
            .replace(/\s+circuit$/i, '')
            .toUpperCase()}`;
          const label = lap.circuitName
            ? (lap.currentS == null && Math.floor(t / 3600) % 2 === 1 ? 'WAITING FOR START LINE' : shortTrack(lap.circuitName))
            : c.lapRunning ? 'NO CIRCUIT NEARBY'
            : lap.nearestName ? `${lap.nearestKm != null && lap.nearestKm >= 100 ? Math.round(lap.nearestKm) : lap.nearestKm} KM → ${lap.nearestName.toUpperCase()}`
            : 'SEARCHING GPS';
          // a word that does not fit shrinks BEFORE it crawls: most labels then simply sit still
          let tw = b.measureText(label).width;
          if (tw > inner) {
            const fit = Math.max(Math.round(lh * 0.5), Math.floor((fpx * inner) / tw));
            if (fit < fpx) { fpx = fit; b.font = glassFont(fpx); tw = b.measureText(label).width; }
          }
          if (label !== glassLabel) { glassLabel = label; glassAt = t; }   // a new message starts from its beginning
          let offset = 0;
          if (tw > inner) {
            // timeline: rest 10 s → creep (18 px/s) until the end shows → rest 3 s → snap back
            const over = tw - inner, creep = over / 18 * 1000, period = 10000 + creep + 3000, ph = (t - glassAt) % period;
            offset = ph < 3000 ? 0 : ph < 3000 + creep ? ((ph - 3000) / creep) * over : over;
          }
          b.save(); b.beginPath(); b.rect(lx + pad, ly, inner, lh); b.clip();
          const tx = lx + pad - offset, ty = ly + lh * 0.52;
          b.fillStyle = '#f4f7ff'; b.fillText(label, tx, ty);
          b.restore();
          // glass reflection
          const gl = b.createLinearGradient(lx, ly, lx + lw * 0.5, ly + lh); gl.addColorStop(0, 'rgba(255,255,255,0.10)'); gl.addColorStop(0.5, 'rgba(255,255,255,0.02)'); gl.addColorStop(1, 'rgba(255,255,255,0)');
          b.fillStyle = gl; b.fillRect(lx, ly, lw, lh);
          b.restore();
          b.save(); rr(lx, ly, lw, lh, lh * 0.06); b.clip(); const ish = b.createLinearGradient(lx, ly, lx, ly + lh * 0.3); ish.addColorStop(0, 'rgba(0,0,0,0.7)'); ish.addColorStop(1, 'rgba(0,0,0,0)'); b.fillStyle = ish; b.fillRect(lx, ly, lw, lh * 0.3); b.restore();
        }
        { const brand = 'ROARDRIVE'; const fpx2 = Math.round(uh * 0.14); b.font = `900 ${fpx2}px "Arial Black", "Inter", Arial, sans-serif`; b.fillStyle = '#ffffff'; b.textAlign = 'center'; b.textBaseline = 'middle';
          const track = uh * 0.012; let tw2 = 0; for (const ch of brand) tw2 += b.measureText(ch).width + track;
          let cx2 = ux + uw * 0.5 - tw2 / 2;
          for (const ch of brand) { const cw2 = b.measureText(ch).width; b.fillText(ch, cx2 + cw2 / 2, uy + uh * 0.84); cx2 += cw2 + track; } }
      }
      b.restore();   // the lap-timer row's slide ends here
      b.globalAlpha = 1;
      b.restore();   // …and the whole dash's vertical centring
      for (let i = hitBase; i < hits.current.length; i++) hits.current[i].y += topPad;
    };

    // ---- 7-segment LED digits (F1 2004-era steering-wheel display) -------
    //   seg layout:  a
    //               f b
    //                g
    //               e c
    //                d
    const SEG: Record<string, number[]> = {
      '0': [1, 1, 1, 1, 1, 1, 0], '1': [0, 1, 1, 0, 0, 0, 0], '2': [1, 1, 0, 1, 1, 0, 1], '3': [1, 1, 1, 1, 0, 0, 1],
      '4': [0, 1, 1, 0, 0, 1, 1], '5': [1, 0, 1, 1, 0, 1, 1], '6': [1, 0, 1, 1, 1, 1, 1], '7': [1, 1, 1, 0, 0, 0, 0],
      '8': [1, 1, 1, 1, 1, 1, 1], '9': [1, 1, 1, 1, 0, 1, 1], '-': [0, 0, 0, 0, 0, 0, 1], ' ': [0, 0, 0, 0, 0, 0, 0],
      N: [0, 0, 1, 0, 1, 0, 1], // 'n' for neutral
    };
    // draws `str` right-aligned into a field of `cells` digits; returns nothing
    const seg7 = (str: string, x: number, y: number, cellW: number, cellH: number, cells: number, col = '#ff2a1a', dim = 'rgba(255,42,26,0.10)') => {
      const th = cellW * 0.17; // segment thickness
      const pad = cellW * 0.22; // gap between cells
      const txt = str.slice(-cells).padStart(cells, ' ');
      b.save();
      b.translate(x, y);
      b.transform(1, 0, -0.1, 1, cellH * 0.1, 0); // slight italic like the real LEDs
      for (let i = 0; i < cells; i++) {
        const ox = i * (cellW + pad);
        const m = SEG[txt[i]] ?? SEG[' '];
        const hx = cellW - th; // horizontal segment length
        const vy = (cellH - th * 3) / 2; // vertical segment length
        const segs: [number, number, number, number][] = [
          [ox + th * 0.5, 0, hx, th], // a
          [ox + cellW - th, th * 0.5, th, vy + th], // b
          [ox + cellW - th, cellH / 2, th, vy + th * 0.5], // c
          [ox + th * 0.5, cellH - th, hx, th], // d
          [ox, cellH / 2, th, vy + th * 0.5], // e
          [ox, th * 0.5, th, vy + th], // f
          [ox + th * 0.5, cellH / 2 - th / 2, hx, th], // g
        ];
        segs.forEach((sg, k) => {
          const on = !!m[k];
          if (on) {
            b.globalAlpha = 0.35;
            b.fillStyle = col;
            rr(sg[0] - th * 0.35, sg[1] - th * 0.35, sg[2] + th * 0.7, sg[3] + th * 0.7, th * 0.6);
            b.fill();
            b.globalAlpha = 1;
          }
          b.fillStyle = on ? col : dim;
          rr(sg[0], sg[1], sg[2], sg[3], th * 0.35);
          b.fill();
        });
      }
      b.restore();
    };
    // round LEDs row: 5 green / 5 red / 5 blue, lit left→right, all-blue flash at the cut
    const ledRowF1 = (t: number, s: DrivetrainState, x0: number, x1: number, y: number, r: number) => {
      const n = 15;
      const step = (x1 - x0) / n;
      const band = Math.max(0, Math.min(1, (s.rpm - max * 0.6) / (max * 0.4 - 200)));
      const lit = Math.round(band * n);
      const atLimiter = blueActive(t, s);
      const blueOn = atLimiter && Math.floor((t / 1000) * 10) % 2 === 0;
      for (let i = 0; i < n; i++) {
        const col = atLimiter ? '#3aa0ff' : i < 5 ? '#2bff4a' : i < 10 ? '#ff3020' : '#3aa0ff';
        const on = atLimiter ? blueOn : i < lit;
        const cx = x0 + step * (i + 0.5);
        if (on) {
          b.globalAlpha = 0.35; b.fillStyle = col;
          b.beginPath(); b.arc(cx, y, r * 1.9, 0, Math.PI * 2); b.fill();
          b.globalAlpha = 1;
        }
        b.fillStyle = on ? col : 'rgba(255,255,255,0.08)';
        b.beginPath(); b.arc(cx, y, r, 0, Math.PI * 2); b.fill();
      }
      // side marker LEDs (green when a shift is in progress / lap running)
      for (const cx of [x0 - step * 0.9, x1 + step * 0.9]) {
        b.fillStyle = s.shifting ? '#2bff4a' : 'rgba(255,255,255,0.08)';
        b.beginPath(); b.arc(cx, y + r * 2.2, r * 0.9, 0, Math.PI * 2); b.fill();
      }
    };
    const f1Timing = (x: number, y: number, ww: number, hh: number, lap: LapState, px: number) => {
      // white mono timing line + last/best, like the small LCD under the LEDs
      const d = lap.deltaS;
      const dcol = d == null ? '#fff' : d < -0.02 ? '#2bff4a' : d > 0.02 ? '#ff2a2a' : '#fff';
      text('LAP ' + lap.lapCount, x, y + hh * 0.25, cond(px, 600), muted, 'left', 'middle');
      text(formatLap(lap.currentS), x + ww * 0.5, y + hh * 0.25, mono(Math.round(px * 1.6)), dcol, 'center', 'middle');
      text(lap.circuitName ?? (lap.onTrack ? '' : 'GPS OFF'), x + ww, y + hh * 0.25, cond(px), muted, 'right', 'middle');
      text('LAST ' + formatLap(lap.lastS), x, y + hh * 0.75, mono(px), '#fff', 'left', 'middle');
      text('BEST ' + formatLap(lap.bestS), x + ww, y + hh * 0.75, mono(px), '#2bff4a', 'right', 'middle');
    };

    const drawF1 = (t: number, s: DrivetrainState, lap: LapState) => {
      // carbon/black wheel display bezel
      b.fillStyle = '#0b0c0e'; rr(w * 0.015, h * 0.02, w * 0.97, h * 0.96, w * 0.02); b.fill();
      b.fillStyle = '#040405'; rr(w * 0.06, h * 0.06, w * 0.88, h * 0.56, w * 0.012); b.fill();
      ledRowF1(t, s, w * 0.2, w * 0.8, h * 0.13, h * 0.02);
      // red 7-seg: speed (3) · gear (1, taller) · rpm (5)
      const cellH = Math.min(h * 0.2, w * 0.1);
      const cellW = cellH * 0.56;
      seg7(String(Math.round(spd(s.speedKmh))), w * 0.1, h * 0.3, cellW, cellH, 3);
      seg7(s.freeRev ? 'N' : String(s.gear), w * 0.5 - cellW * 0.75, h * 0.25, cellW * 1.5, cellH * 1.5, 1);
      seg7(String(Math.round(s.rpm / 5) * 5), w * 0.9 - (cellW * 5 + cellW * 0.22 * 4), h * 0.3, cellW, cellH, 5);
      text('KM/H', w * 0.1, h * 0.55, cond(Math.round(h * 0.03), 600), muted, 'left', 'middle');
      text('RPM', w * 0.9, h * 0.55, cond(Math.round(h * 0.03), 600), muted, 'right', 'middle');
      text(loadLabel(s.load, s.engine), w * 0.5, h * 0.57, cond(Math.round(h * 0.03), 600), s.limiter ? '#ff2a2a' : s.load === 'accel' ? accent : muted, 'center', 'middle');
      // timing strip
      f1Timing(w * 0.08, h * 0.64, w * 0.84, h * 0.12, lap, Math.round(h * 0.036));
      // touch controls row
      const px = Math.round(h * 0.034);
      controlBoxes(w * 0.06, h * 0.785, w * 0.26, h * 0.075, px); // GEAR + MODE stacked
      lapButtons(w * 0.66, h * 0.84, w * 0.28, h * 0.1, px);
    };
    // ---- modern F1 wheel display (McLaren Applied PCU-8 look): white-on-black boxes,
    // 5 red + 5 blue shift LEDs on top, amber side clusters, "PIT LIMITER" banner ----
    const pcuLeds = (t: number, s: DrivetrainState, x0: number, x1: number, y: number, r: number) => {
      const n = 10;
      const step = (x1 - x0) / n;
      const band = Math.max(0, Math.min(1, (s.rpm - max * 0.6) / (max * 0.4 - 200)));
      const lit = Math.round(band * n);
      const blue = blueActive(t, s);
      const blueOn = blue && Math.floor((t / 1000) * 10) % 2 === 0;
      for (let i = 0; i < n; i++) {
        const col = blue ? '#3a7bff' : i < 5 ? '#ff2a1a' : '#3a7bff';
        const on = blue ? blueOn : i < lit;
        const cx = x0 + step * (i + 0.5);
        if (on) { b.globalAlpha = 0.35; b.fillStyle = col; b.beginPath(); b.arc(cx, y, r * 1.9, 0, Math.PI * 2); b.fill(); b.globalAlpha = 1; }
        b.fillStyle = on ? col : 'rgba(255,255,255,0.08)';
        b.beginPath(); b.arc(cx, y, r, 0, Math.PI * 2); b.fill();
      }
    };
    // amber/white side clusters: left lights under braking, right under hard throttle
    const pcuSide = (x: number, y0: number, gap: number, r: number, on: boolean) => {
      const cols = ['#ffb000', '#ffb000', '#ffffff'];
      cols.forEach((c, i) => {
        const cy = y0 + i * gap;
        if (on) { b.globalAlpha = 0.35; b.fillStyle = c; b.beginPath(); b.arc(x, cy, r * 1.8, 0, Math.PI * 2); b.fill(); b.globalAlpha = 1; }
        b.fillStyle = on ? c : 'rgba(255,255,255,0.08)';
        b.beginPath(); b.arc(x, cy, r, 0, Math.PI * 2); b.fill();
      });
    };
    const whiteBox = (x: number, y: number, ww: number, hh: number) => { b.fillStyle = '#f4f4f2'; rr(x, y, ww, hh, 3); b.fill(); };
    const darkBox = (x: number, y: number, ww: number, hh: number) => { b.fillStyle = '#0a0b0d'; rr(x, y, ww, hh, 3); b.fill(); b.strokeStyle = 'rgba(255,255,255,0.35)'; b.lineWidth = 1.5; rr(x, y, ww, hh, 3); b.stroke(); };
    const pcuTyres = (x: number, y: number, ww: number, hh: number, s: DrivetrainState, px: number) => {
      // cosmetic tyre temps: warm up with speed, fronts a bit hotter under braking
      const base = 55 + Math.min(40, s.speedKmh * 0.25);
      const fl = Math.round(base + (s.load === 'brake' ? 6 : 0) + 2), fr = Math.round(base + (s.load === 'brake' ? 6 : 0));
      const rl = Math.round(base - 8 + (s.load === 'accel' ? 5 : 0)), rr2 = Math.round(base - 8 + (s.load === 'accel' ? 5 : 0) - 1);
      darkBox(x, y, ww, hh);
      const cell = (cx: number, cy: number, v: number) => { b.fillStyle = '#2f6fe0'; rr(cx - px * 1.1, cy - px * 0.65, px * 2.2, px * 1.3, 3); b.fill(); text(String(v), cx, cy, cond(px, 700), '#fff', 'center', 'middle'); };
      cell(x + ww * 0.22, y + hh * 0.28, fl); cell(x + ww * 0.78, y + hh * 0.28, fr);
      cell(x + ww * 0.22, y + hh * 0.74, rl); cell(x + ww * 0.78, y + hh * 0.74, rr2);
      // tyre glyph
      b.strokeStyle = '#cfd3d8'; b.lineWidth = 2;
      b.beginPath(); b.moveTo(x + ww * 0.5, y + hh * 0.18); b.lineTo(x + ww * 0.5, y + hh * 0.82); b.stroke();
      b.beginPath(); b.moveTo(x + ww * 0.38, y + hh * 0.28); b.lineTo(x + ww * 0.62, y + hh * 0.28); b.moveTo(x + ww * 0.38, y + hh * 0.74); b.lineTo(x + ww * 0.62, y + hh * 0.74); b.stroke();
      text('TYRE', x + ww * 0.5, y + hh * 0.5, cond(Math.round(px * 0.7), 600), '#cfd3d8', 'center', 'middle');
      text('TEMP', x + ww * 0.5, y + hh * 0.62, cond(Math.round(px * 0.6)), '#9a9a9a', 'center', 'middle');
    };
    /**
     * Lamborghini Aventador SVJ — traced from Marc's photographs of the real TFT cluster.
     *
     * Everything about it is one idea: a huge arch of segments over a honeycomb, with the gear as the
     * loudest thing on the screen. What makes it read as a Lamborghini and not as a generic rev arc:
     *  - the segments are TRAPEZOIDS radiating outward, not a smooth bar, and they light in blue;
     *  - a thin yellow contour runs inside them the whole way round, jagged rather than smooth;
     *  - the numbers sit INSIDE that contour, and the last two are red;
     *  - the gear is red, enormous, and overlaps the gear strip behind it;
     *  - the blue crown above the arch flashes at the limiter (Marc labelled the screenshot exactly
     *    that: "the blue shape flashes when at rpm limiter").
     * The odometer of the real car is where our lap timing goes: a dash we ship has to earn its space.
     */
    /**
     * THE SVJ'S GEAR DIGIT, TRACED off Marc's photographs.
     *
     * Two wrong answers before this one. It was Barlow Condensed italic, which he called simplona and
     * which it was. Then I decided from a blurry close-up that the face was a seven-segment display
     * with every corner chamfered at forty-five degrees, and built it out of bars - also wrong. Pulled
     * out of the photographs at full size, the letters are a heavy WIDE italic display face with square
     * corners and one rounded terminal on the N, nothing segmented about it. No font loaded here comes
     * near its proportions either: it measures 0.938 wide for its height and the closest available are
     * Barlow at 0.854 and Arial at 1.003.
     *
     * STRAIGHTENED (Marc: "hay algunos q no siguen lineas del todo recta y yo acabaria de afinarlos,
     * es decir el 1 no tiene una linea recta"). He is right and it is the tracer's fault, not the
     * face's: a threshold walked around a photographed edge wanders half a pixel either side of it,
     * so every long straight came out as a chain of forty little segments that wobble. Each ring is
     * now reduced (Douglas-Peucker at 1.8 of the glyph's 100-tall box, anchored on its sharpest
     * corner so no corner is rounded off) and the counts tell the story: 43 points on the 1 became
     * 11, the N's 44 became 12. The letters are unchanged in shape and finally straight.
     *
     * So each figure is the real outline, isolated from the frame where it appears (`tools/trace-glyph.py`)
     * and normalised into a 100-tall box. The 1, the 7 and the N were photographed HOLLOW, so the tracer
     * also finds the inside of their stroke; only their outer ring is kept, because this is the letter
     * and the app decides whether to fill it. The 6's second ring IS its counter and stays.
     *
     * Only the 4 is drawn by hand, in the same weight and slant: no photograph shows fourth gear. R and
     * 0 are not here because the big digit never shows them - reverse and zero are not gears the sim
     * selects, and the small strip below uses ordinary type.
     */
    const SVJ_GLYPH: Record<string, number[][][]> = {
      '1': [
        [[12.1, 5.9], [-1.2, 42.2], [-11.0, 49.1], [-27.7, 49.7], [-30.3, 43.9], [-5.2, -24.9], [-11.0, -29.5], [-7.2, -40.2], [6.3, -50.0], [25.9, -49.7], [30.3, -47.1]],
      ],
      '2': [
        [[25.0, -26.4], [-22.3, -27.7], [-25.0, -31.5], [-22.3, -37.5], [-4.9, -49.7], [35.6, -49.5], [43.2, -48.4], [49.5, -42.9], [50.8, -29.3], [41.0, -6.2], [-12.2, 26.6], [-8.2, 29.9], [24.5, 29.9], [25.3, 36.1], [21.7, 42.7], [8.7, 49.7], [-50.3, 50.0], [-51.1, 44.0], [-39.9, 16.8], [21.5, -19.8]],
      ],
      '3': [
        [[30.2, 0.4], [33.8, 6.9], [32.7, 14.6], [21.3, 40.5], [12.3, 48.6], [-35.7, 50.0], [-48.2, 39.1], [-48.0, 32.0], [-44.4, 30.4], [2.5, 29.0], [9.8, 13.8], [6.8, 9.9], [-15.5, 9.4], [-13.4, -5.6], [-8.7, -11.3], [18.0, -11.6], [23.7, -26.6], [-25.1, -28.5], [-24.3, -36.9], [-12.0, -48.6], [22.1, -50.0], [42.5, -48.4], [46.9, -44.8], [49.3, -29.8], [44.7, -15.9], [41.1, -8.9]],
      ],
      '5': [
        [[9.9, 9.5], [-21.6, 8.2], [-34.6, -6.0], [-20.0, -49.5], [37.6, -49.2], [47.4, -43.2], [49.6, -32.1], [46.3, -28.5], [2.0, -28.8], [-0.4, -25.8], [-3.1, -14.7], [27.0, -13.3], [36.8, 0.5], [17.0, 45.9], [9.9, 49.2], [-34.1, 50.0], [-49.9, 37.5], [-46.1, 30.7], [-0.4, 30.2]],
      ],
      '6': [
        [[-5.6, -11.1], [23.0, -10.9], [34.9, 0.0], [36.0, 4.1], [20.8, 41.0], [8.8, 49.2], [-36.5, 49.7], [-47.7, 40.5], [-49.0, 34.0], [-18.9, -49.7], [40.4, -48.6], [49.0, -40.2], [48.2, -29.1], [0.4, -27.7]],
        [[-21.6, 29.3], [-14.5, 10.9], [7.5, 11.1], [0.1, 29.3]],
      ],
      '7': [
        [[9.4, -26.6], [-34.6, -27.2], [-37.6, -32.6], [-33.0, -46.2], [-28.4, -50.0], [24.6, -49.2], [32.5, -45.4], [37.4, -37.5], [33.3, -16.3], [10.7, 39.9], [-0.1, 49.5], [-15.9, 50.0], [-17.8, 47.3]],
      ],
      'N': [
        [[12.7, 16.3], [33.7, -50.0], [44.5, -45.6], [47.0, -35.1], [22.7, 45.3], [15.2, 49.7], [-3.0, 48.6], [-11.3, 4.1], [-24.3, 48.9], [-46.1, 50.0], [-20.2, -47.5], [3.0, -47.8]],
      ],
      // THE 4, REDRAWN against Marc's close-up (it is the one figure no photograph of the cluster
      // gave me whole, so it is drawn rather than traced). Three things were wrong in the first
      // attempt, and all three show at the size this digit is drawn:
      //   the counter ran all the way down to the bar's BOTTOM edge, so the crossbar vanished under
      //   the hole and the diagonal appeared to meet the stem on nothing - in his photo there is a
      //   clear band of red under the counter;
      //   the counter was 42 tall against his 24, which made the diagonal a thin wire;
      //   and the stem stepped in below the bar, a notch his figure does not have.
      // Built on ONE slant now (0.26 x per y, the slant the traced glyphs carry) with a constant
      // 26-wide stem, so it sits in the same italic as the 1, the 5 and the 7 beside it.
      // THE COUNTER HAS TO SURVIVE THE CONTOUR. The rim is stroked on the path, so it eats about
      // three units INTO the hole on every side and the glow eats more; a counter drawn at the
      // opening the photograph shows closes up completely and the 4 comes out as a solid red slab
      // with no hole in it at all, which is what Marc is looking at. So the counter is drawn ~6
      // units bigger than the opening it has to leave: 32 x 28 on the path, ~26 x 22 on the screen.
      '4': [
        [[20, -50], [46, -50], [20, 50], [-6, 50], [2, 20], [-46, 20], [-41, 0]],
        [[14, -28], [-24, 0], [7, 0]],
      ],
      '-': [[[-30, -9], [30, -9], [30, 9], [-30, 9]]],
    };
    /**
     * THE CONTOUR IS ALWAYS DRAWN; THE INSIDE IS WHAT THE CAR PAINTS (Marc, off his own 4: "ese
     * contorno se queda siempre y es lo de dentro q se pinta o no").
     *
     * The rule for the inside, from the car:
     *   CORSA, on the throttle, above 6000 rpm  -> filled;
     *   lift off at 8000 and it empties again, so it is the pedal and not the needle that holds it;
     *   SPORT -> never filled, and the whole figure is yellow rather than red;
     *   mid-change -> never filled, whatever the rpm is doing.
     * That also retro-explains every photograph I could not account for: the hollow ones are N at
     * rest, first at rest, and SEVENTH at 322 km/h, which really is under 6000 in that gear.
     */
    const svjGearDigit = (ch: string, cx2: number, cy2: number, hh: number, filled: boolean, col: string, fillCol?: string) => {
      const rings = SVJ_GLYPH[ch] ?? SVJ_GLYPH['-'];
      const u = hh / 100;
      // a hair wider than tall: the traced outlines come in at 0.1758 of the arch's width against
      // the frame's 0.1768, which is the tracer's own edge threshold eating half a pixel all round
      b.save(); b.translate(cx2, cy2); b.scale(u * 1.0017, u);
      b.beginPath();
      for (const r of rings) {
        r.forEach(([x, y], i) => (i ? b.lineTo(x, y) : b.moveTo(x, y)));
        b.closePath();
      }
      b.restore();
      // fill FIRST, then the rim over it: stroking and filling were exclusive here, so a lit digit
      // lost its contour and an unlit one was a bare wire. The car has both at once.
      if (filled) { b.fillStyle = fillCol ?? col; b.fill('evenodd'); }
      b.save();
      b.strokeStyle = col; b.lineWidth = Math.max(2, hh * 0.030); b.lineJoin = 'round';
      // a filled digit gets a much smaller halo: at hh * 0.05 the glow closes the 4's counter from
      // both sides and the figure reads as a solid slab
      b.shadowColor = col; b.shadowBlur = hh * (filled ? 0.012 : 0.05);
      b.stroke(); b.restore();
    };
    const drawSvj = (t: number, s: DrivetrainState, _lap: LapState) => {
      // Re-measured off Marc's second set, which is sharp enough to read the construction rather than
      // guess at it. Three things change from the first build:
      //   the blocks are near BLACK with a lit top edge, not steel blue, with a blue band over the cold
      //   low revs and a red one at the top;
      //   the crown is not a stepped line but a chain of HEXAGONAL CELLS, one per figure, and its colour
      //   follows the drive mode - amber in CORSA, white in EGO;
      //   and everything in the middle sits higher than I had it. The figures ride at 0.814 of the arch
      //   radius, not 0.732, and the giant gear digit's centre is 0.62 up rather than 0.48.
      const ital = (px: number, wt = 700) => `italic ${wt} ${px}px "Barlow Condensed", sans-serif`;
      const LAMBO_RED = '#ff2a17';
      // the mode's own colour: the SVJ we ship has two, SPORT and CORSA (Marc), and the figure that
      // matters changes colour with the manettino - yellow in SPORT, red in CORSA.
      const LAMBO_YELLOW = '#e8dc40';
      const STRIP = '#c8d4e4';
      const DIAL = 10000;

      const cx = w * 0.5;
      // Every fidelity number below is a RATIO, so the arch can be scaled to fit the screen without
      // breaking any of them. The wider (correct) sweep drops its ends much lower, and at the old size
      // the 0 and the 10 landed on the controls and on the speed.
      // THE ARCH'S SIZE IN ITS PANEL, measured off Marc's frame rather than chosen: the radius is
      // 0.516 of the panel's width and 0.987 of its height, and the centre sits 0.107 of the radius
      // BELOW the bottom edge. Mine was 0.44 / 0.94 with the centre only 0.024 under, so the whole
      // instrument was small in a tall box and everything inside it was pushed down - which is why
      // the speed fell off the bottom the moment it was placed where the car actually puts it.
      // Sized from the CONTENT, which is what I can measure, rather than from my guess at where the
      // bezel is: in Marc's frame the picture runs from the arch's apex at dy -1.0 down to the unit
      // under the speed at dy -0.02, so the panel holds very nearly one radius of height, and the
      // radius is 0.516 of its width. My first pass put the centre 0.107r below the bottom edge from
      // an eyeballed bezel and cut the speed and the car clean off.
      // RESPONSIVE, and it has to be: the Tesla's browser can hand us a 1:1 square, and pinning the
      // centre to the bottom edge (cy = h - 0.02r) dropped the whole instrument into the lower half
      // with a dead black band above it. The drawn band runs from the apex at cy - r to the unit under
      // the speed at cy + 0.02r, so it is 1.02r tall and 1.904r wide (2r sin(sweep/2)); size it to
      // whichever axis binds and then CENTRE that band in the panel.
      // THE RECTANGLES BEGIN AFTER THE YELLOW LINE (Marc), so the ring starts outside the crown's
      // crest at 0.889 rather than swallowing it. That is also why the tabs "touch the rectangles":
      // the crest rises exactly to where the blocks start.
      // THE ARC CAME OUT CUT ALONG THE TOP (Marc: "simplemente bajame todo un poco para q el arco
      // quede completo"). The drawn band runs from the outer yellow at cy - 1.02r to the unit under
      // the speed at cy + 0.02r, so it is 1.04r tall and only fits if r <= 0.96h; at 0.98h the apex
      // stood 1 per cent of the height ABOVE the panel and got sliced off on any wide window, where
      // the height is what binds. 0.92h leaves a 2 per cent margin top and bottom, and centring the
      // band properly (0.50r rather than 0.49r) is what puts the whole assembly a little lower.
      const rOut = Math.min(h * 0.92, w * 0.515), rIn = rOut * 0.910;
      const cy = h / 2 + rOut * 0.50;
      // THE SWEEP, fixed by measurement rather than by eye. Reading the 0, the 5 and the 10 off a
      // photograph by hand gave 124 degrees and left the arch visibly too flat. The honest number comes
      // from a ratio that needs no centre and no landmark: the amber arc's apex-to-ends height over its
      // full width, which is tan(sweep/4)/2 for an arc drawn over exactly A0..A1. Marc's frame reads
      // 0.3646; 124 degrees gives 0.3020. 0.3646 is 144.4 degrees, and at that the render measures
      // 0.3655 against his 0.3646, a quarter of one per cent.
      const A0 = (197.9 * Math.PI) / 180, A1 = (342.1 * Math.PI) / 180;
      const ang = (f: number) => A0 + Math.max(0, Math.min(1, f)) * (A1 - A0);
      // THE SAME ANGLE WITHOUT THE CLAMP. The clamp is right for the bar and the needle (nothing is
      // lit past 10) and wrong for everything drawn AROUND them: the crown's hat over the 0 reaches
      // to -0.023 of a turn and the clamp folded that half onto the arch's first pixel, which is why
      // the 0 and the 10 had no hat while every figure between them did (Marc).
      const angU = (f: number) => A0 + f * (A1 - A0);
      const frac = Math.max(0, Math.min(1, needle / DIAL));
      const at = (dx: number, dy: number) => [cx + dx * rOut, cy + dy * rOut] as const;
      const c = getControls ? getControls() : null;
      const corsa = c?.resolvedStyle === 'sport';
      // amber in CORSA, white in EGO: the crown changes colour with the manettino, which is why the
      // two photographs of the same car disagree about it
      // #ceb358, the mean of the crown line's CORE (its brightest 15 per cent, so the antialiased
      // skirt does not drag it dark) across all nine frames. #e8a12a was too orange and too hot.
      // The colour off Marc's close-ups: a live, saturated YELLOW, not the muted gold I had taken
      // from a mean over whole frames. Averaging a thin bright line against a dark ground pulls it
      // dark and warm every time - #ceb358 was that error.
      const CROWN = corsa ? LAMBO_YELLOW : '#dfe8f4';
      // the giant gear digit: red in CORSA, yellow in SPORT. The inside is a shade hotter than the
      // rim when it does light, which is what the photograph shows - an orange core in a red contour.
      const DIGIT = corsa ? LAMBO_RED : LAMBO_YELLOW;
      const DIGIT_IN = corsa ? '#ff5b33' : LAMBO_YELLOW;

      // THE HONEYCOMB: TILTED, BIGGER, AND IT FADES INTO BLACK (Marc: "el panel de abeja esta como
      // tilt a 45 grados y hace smooth transition a black.. y es como mas grande"). Mine was an
      // upright lattice of small cells that began on a hard horizontal line under the arch, and that
      // line is the thing that gave it away as a pattern laid over the dial rather than a surface
      // behind it. Now: the whole lattice is turned 45 degrees about the dial's centre, the cell is
      // half again as big, and the top of the panel is faded out with black instead of clipped.
      const hTop = cy - rOut * 0.44;   // nothing above the CORSA line
      {
        // TILTED 40 DEGREES, WHICH MEANS PERSPECTIVE, NOT ROTATION (Marc: "el panel de abeja ahora
        // esta en vista cenital... tendria q estar en modo tilt a 40 grados"). I had read his first
        // note as a 45 degree turn in the plane of the screen, and a turned lattice is still a
        // lattice seen face on - which is exactly what he called it, a plan view. It is a FLOOR: it
        // lies away from you at 40 degrees, so the cells shrink and their rows close up as they
        // recede, and it goes into black at the far end instead of stopping on a line.
        const TILT = (45 * Math.PI) / 180;
        // THREE TIMES THE SIZE, AND SEEN FROM TWENTY DEGREES (Marc: "como 3 veces mas grande de lo
        // q hay ahora y acaba justo debajo de las letras del CORSA... el tilt tiene q ser mayor..
        // como de 20 grados mirando al horizonte. osea lo opuesto a verse como vista cenital").
        // Twenty degrees is measured from the FLOOR, not from face on, so it is the grazing view: a
        // row of cells is only sin(20) = 0.34 as deep on the screen as it is wide, and the rows shut
        // together fast. The vanishing line is not off the top of the panel any more either - it is
        // where the floor ENDS, just under the CORSA legend at dy -0.442.
        const B = rOut * 0.51;                    // a cell at the near edge: three times the old one
        const yHorizon = cy - rOut * 0.415;       // the floor's far end, a hair under CORSA
        const yNear = h + rOut * 0.10;            // the nearest row, just off the bottom
        const DP = (yNear - yHorizon) / (B * Math.sin(TILT));   // rows to the vanishing line
        const yHor = yHorizon;
        const sc = (v: number) => 1 / (1 + v / DP);
        const px = (u: number, v: number) => cx + u * B * sc(v);
        const py = (v: number) => yHor + (yNear - yHor) * sc(v);
        b.save();
        b.beginPath(); b.rect(0, hTop, w, h - hTop); b.clip();
        // the fade lives in the STROKE: painting black over the top drew its own hard edge, which
        // was the seam I was trying to remove in the first place
        const gH = b.createLinearGradient(0, hTop + rOut * 0.02, 0, hTop + rOut * 0.40);
        gH.addColorStop(0, 'rgba(120,150,190,0)');
        gH.addColorStop(0.5, 'rgba(120,150,190,0.10)');
        gH.addColorStop(1, 'rgba(120,150,190,0.24)');
        b.strokeStyle = gH; b.lineWidth = Math.max(1, h * 0.0035);
        b.beginPath();
        // a hexagon of radius 1 in floor units: rows 1.5 apart, columns sqrt(3), vertices at 60i-30.
        // DRAWN INSET (Marc: "esta linea del panel de abeja tiene q ser doble"). Each cell is drawn a
        // little smaller than its own share of the lattice, so the wall between two cells is their
        // two outlines running side by side - a double line - instead of one shared edge. Drawing the
        // cells at full size and then stroking twice would not do it: neighbours share their edges
        // exactly, so it would only be the same line painted over itself.
        const INSET = 0.93;
        const HEX: [number, number][] = [];
        for (let i = 0; i < 6; i++) {
          const a = (Math.PI / 180) * (60 * i - 30);
          HEX.push([Math.cos(a) * INSET, Math.sin(a) * INSET]);
        }
        for (let row = 0; row < 44; row++) {
          const v = row * 1.5;
          if (py(v) < hTop - B) break;             // past the top of the panel
          if ((py(v) - py(v + 1.5)) < 2) break;    // rows closer than two pixels: it is a smear
          const half = Math.ceil((w / 2) / (B * sc(v) * Math.sqrt(3))) + 1;
          for (let col = -half; col <= half; col++) {
            const u = col * Math.sqrt(3) + (Math.abs(row % 2) ? Math.sqrt(3) / 2 : 0);
            HEX.forEach(([hx, hy], i) => {
              const X = px(u + hx, v + hy), Y = py(v + hy);
              if (i) b.lineTo(X, Y); else b.moveTo(X, Y);
            });
            b.closePath();
          }
        }
        b.stroke();
        b.restore();
      }

      // ---- the arch of blocks ----
      // FORTY, counted against the figures rather than trusted to autocorrelation: between the 4 and
      // the 5 in Marc's frame there are four blocks, and there are ten such spans. (The autocorrelation
      // said 24 and was reading the perspective and the blocks' own inner highlight, not the pitch - it
      // gets my own 32 exactly right, so the method is sound and the photograph is just harder.)
      // TWENTY, from Marc, who has the car in front of him. I had counted forty off a blurred frame
      // and every automatic count I tried disagreed with itself (46-54 seams against 16-21 by FFT on
      // the same picture), which should have told me the count was not mine to take.
      const SEG = 20;
      // THE BOXES ARE ALWAYS THERE, THEY JUST DO NOT LIGHT (Marc: "las cajas siguen existiendo eeh????
      // solo q no se iluminan en azul"). They are moulded into the glass, so above the needle you see
      // the box and not the light: a near-black face with only its outer end catching a little of the
      // dial. Blue is the LIT state and it stops dead at the needle.
      // And the light does not arrive a unit at a time: "no se van volviendo azules por unidades, sino
      // segun pasa la aguja, se rellena en un %". The box the needle stands in is lit only as far as
      // the needle has crossed it - at 75 per cent of a box, 75 per cent of that box.
      const band = (aA: number, aB: number, am2: number, red2: boolean, lit2 = true) => {
        if (aB <= aA) return;
        const g = b.createLinearGradient(
          cx + rIn * Math.cos(am2), cy + rIn * Math.sin(am2),
          cx + rOut * Math.cos(am2), cy + rOut * Math.sin(am2));
        // Direction MEASURED off Marc's frame: walking out along a radius the block runs from
        // near-black at its inner edge to its brightest at the OUTER edge (luma 13 -> 68), then dips
        // into the amber line. AND IT FADES OUT UNDERNEATH (Marc) - the long ramp to black at the
        // inner edge is what melts the ring into the face instead of ending it on a line.
        if (red2 && lit2) { g.addColorStop(0, '#000000'); g.addColorStop(0.18, '#3a0500'); g.addColorStop(0.45, '#a81103'); g.addColorStop(0.75, '#e81704'); g.addColorStop(0.92, '#ff5030'); g.addColorStop(1, '#d01d07'); }
        // THE LIMITER'S RED IS RED WHETHER IT IS LIT OR NOT (Marc: "mira el rojo del rpm limiter!!
        // tiene q ser igual"). Those blocks are red glass, not a red lamp: in his photograph they
        // are a strong red with the same lit outer end as any other box long before the needle gets
        // near them. Mine were a near-black maroon until the needle arrived.
        else if (red2) { g.addColorStop(0, '#000000'); g.addColorStop(0.18, '#2c0602'); g.addColorStop(0.45, '#8f0f03'); g.addColorStop(0.78, '#c41505'); g.addColorStop(0.93, '#e03a1e'); g.addColorStop(1, '#b81806'); }
        else if (!lit2) { g.addColorStop(0, '#000000'); g.addColorStop(0.40, '#070910'); g.addColorStop(1, '#121722'); }
        // THE COLOUR OF EACH BOX, off Marc's close-up ("fijate bien en el color de cada caja de
        // rpm!!! haz lo mismo"): a lit box is a VIVID azure, not the pale steel blue I had. It runs
        // black at the inner edge, climbs through a deep navy and comes out bright and saturated at
        // the outer end, right under the yellow line. Mine topped out at #7fb0e6, which is grey next
        // to his; the top of the ramp is #4fb8f5 now and the middle is twice as saturated.
        else { g.addColorStop(0, '#000000'); g.addColorStop(0.16, '#06111f'); g.addColorStop(0.38, '#0f3762'); g.addColorStop(0.62, '#1a6cb4'); g.addColorStop(0.85, '#2f9ce4'); g.addColorStop(1, '#4fb8f5'); }
        b.beginPath();
        b.arc(cx, cy, rOut, aA, aB);
        b.arc(cx, cy, rIn, aB, aA, true);
        b.closePath();
        b.fillStyle = g; b.fill();
      };
      for (let i = 0; i < SEG; i++) {
        const f0 = i / SEG, f1 = (i + 1) / SEG - 0.0012, fm = (f0 + f1) / 2;
        const a0 = ang(f0), a1 = ang(f1), am = (a0 + a1) / 2;
        // THE LAST TWO AND A HALF ARE RED, so the red starts at 17.5 of 20 rather than at whatever
        // redline rpm the profile carries. The one before the last two is split down the middle,
        // which is what 'two and a half' means.
        const red = fm >= 18 / SEG;
        const halfRed = !red && i === SEG - 3;
        const half = (a0 + a1) / 2;
        // the box itself, unlit, all the way round
        if (halfRed) { band(a0, half, am, false, false); band(half, a1, am, true, false); }
        else band(a0, a1, am, red, false);
        // and the light over it, as far along THIS box as the needle has come
        const aLit = ang(Math.min(f1, frac));
        if (aLit > a0) {
          if (halfRed) {
            band(a0, Math.min(half, aLit), am, false);
            if (aLit > half) band(half, aLit, am, true);
          } else band(a0, aLit, am, red);
        }
        // THE SEAM BETWEEN BLOCKS IS A LIGHT LINE, NOT A BLACK GAP. Unrolling the band between the 4
        // and the 5 in Marc's frame, the divisions come out as pale vertical streaks; in mine they
        // were dark voids, which is most of why his rectangles read as solid keys and mine as slats.
        // It belongs to the block's far edge, and it is part of the moulding, so it is there whether
        // the box is lit or not - just fainter when it is not.
        // AND THE DIVISION BETWEEN BOXES IS DARK. In his close-up the lit boxes are separated by
        // black gaps, not by the pale streaks I had read off an unrolled strip: that reading came
        // from a blurred frame of my own render, not from the car.
        if (!red) {
          b.strokeStyle = frac >= f1 ? 'rgba(0,0,0,0.62)' : 'rgba(90,110,140,0.20)';
          b.lineWidth = Math.max(1, rOut * 0.005);
          b.beginPath();
          b.moveTo(cx + rIn * Math.cos(a1), cy + rIn * Math.sin(a1));
          b.lineTo(cx + rOut * Math.cos(a1), cy + rOut * Math.sin(a1));
          b.stroke();
        }
      }
      // the thin pale arc inside the blocks, and the crown's own line outside them
      b.strokeStyle = 'rgba(205,225,250,0.75)'; b.lineWidth = Math.max(1, rOut * 0.005);
      // THE WHITE ARC RIDES JUST OUTSIDE THE YELLOW LINE (Marc), not inside it: it runs a hair
      // above the crown's low runs at 0.850, and the bridges rise over it.
      // ABOVE the yellow, not below it (Marc). It runs at 0.886, just outside the crown's low runs
      // at 0.878, so between the figures the yellow sits UNDER the white line, and each bridge
      // rises over it to 0.908. I had it at 0.872, inside the runs, which put it the wrong side.
      // BROKEN WHERE THE BRIDGES ARE (Marc): the white line only runs along the crown's LOW stretches
      // and stops at each bridge's shoulder rather than passing behind it.
      // IT RUNS UNTIL IT TOUCHES THE YELLOW (Marc). Each white run used to stop where the shoulder
      // BEGINS, at the trough, leaving a black nick between the two lines; the shoulder is a ramp
      // from 0.878 to 0.908 and the white sits at 0.886, so the ramp crosses it a quarter of the way
      // up. That crossing is where the white has to end, and now does: the two lines meet.
      {
        const kM = Math.floor(DIAL / 1000), st = 1 / kM, sh = st * 0.105, fl = st * 0.125;
        const rW = rOut * 0.886;
        const cross = sh * (1 - (0.886 - 0.878) / (0.908 - 0.878));   // 0.733 of the shoulder
        let from = -0.04;
        for (let k = 0; k <= kM; k++) {
          const f = k * st, gapA = f - fl - cross, gapB = f + fl + cross;
          if (gapA > from) { b.beginPath(); b.arc(cx, cy, rW, angU(from), angU(gapA)); b.stroke(); }
          from = gapB;
        }
        if (from < 1.04) { b.beginPath(); b.arc(cx, cy, rW, angU(from), angU(1.04)); b.stroke(); }
      }
      // AND THE OUTER YELLOW IS NOT CUT (Marc): it runs the whole ring, past the 0 and the 10, the
      // same overhang the crown has, instead of stopping dead on the first and last block.
      b.strokeStyle = CROWN; b.lineWidth = Math.max(1.5, rOut * 0.007);
      b.beginPath(); b.arc(cx, cy, rOut * 1.006, angU(-0.06), angU(1.06)); b.stroke();

      // ---- the crown ----
      // ONE CONTINUOUS LINE, not a cell per figure. It runs low between the numbers and steps out over
      // each one so the figure sits inside its own bump, with slanted shoulders that give the chain its
      // hexagonal look. Drawing closed hexagons instead, which is what it looks like at a glance, makes
      // neighbours overlap into a mess. Amber in CORSA, white in EGO.
      // measured off the clearest frame and then corrected against Marc's own: the crest stands about
      // 0.053 of the radius above a figure's centre, but the trough barely dips BELOW that centre at all -
      // the line runs level with the numbers between them. Dropping it further makes the chain hang down
      // like teeth, which is what mine was doing.
      // The block ring is far THICKER than I had it and the crown sits correspondingly further in.
      // Walking straight down the middle of the dial, the crest falls 0.0986 of the crown's width below
      // the outer amber line in Marc's frame; mine fell 0.0680, so the blocks were thin and the whole
      // chain rode too close to them.
      // Measured against the frames: the TROUGH runs at the figures' own centre line (-0.015 of the
      // radius) so the line touches them, and the CREST stands +0.063 above it, tucking under the
      // blocks. Mine had the crest only +0.037 up and the whole assembly 0.045 too far in, which is
      // why the figures floated under a shallow chain instead of sitting in it.
      // MEASURED OFF MARC'S CLOSE-UP by converting screen positions back to radius, rather than by
      // eye: his figures centre on 0.834, his low runs sit at 0.878 and his bridges reach 0.908. Mine
      // were 0.808 / 0.850 / 0.889 - the whole assembly was 0.02 to 0.03 of the radius too far in, and
      // that is what made the crop read as a bridge stranded above a small number in a lot of black.
      // Polarity (bridge OUT over each figure, run IN between) was settled earlier and stands.
      const rN = rOut * 0.852, rTrough = rOut * 0.878, rCrest = rOut * 0.908;
      const kMax = Math.floor(DIAL / 1000);
      // the trough has to be a real flat run, not a point: leave 0.30 either side for the crest and
      // 0.12 for each slant and about a sixth of the step is left lying low, which is what the car draws.
      // With no flat left over the dips come out as deep V spikes instead of steps.
      // THE TAB IS A NARROW PEAK, NOT A PLATEAU. Measuring the angular width of each crest run in
      // Marc's frames it is 0.11 to 0.21 of a step; mine measured 0.844, five times too wide, which
      // is why his chain hugs each figure and mine sat on it like a shelf. (Line THICKNESS was
      // already right: 0.0071 of the radius against his 0.005-0.016.)
      // flat 0.12 either side = the 0.24 of a step his crest holds; shoulders 0.06 to reach it
      // the bridge and the run between are about the same length: half a step each
      // From the close-ups: the shoulders are clear DIAGONALS, not the near-vertical steps I had,
      // and the flat top is about half the bridge's width rather than nearly all of it.
      // NARROWER, AND THE FIGURE TUCKED UP INTO IT (Marc, on the close-up: "ese sombrero es pelin mas
      // estrecho y el numero esta un poco mas pegado ahi... pero mas pegadito y metido en ese
      // huequecito"). The hat loses about a sixth of its width and the figures ride 0.011 of the
      // radius further out, which closes the air under the bridge without letting them touch it.
      // and the flat top is a touch wider than the figure that stands under it. The figure's ink is
      // 0.047 of the radius wide, which at the figures' own radius is 0.214 of a step; a flat of
      // 0.25 of a step (2 x 0.125) left it exactly as wide as its hat, so a corner sat on a shoulder
      // wherever anything moved it by a hair. 2 x 0.14 leaves a third of the figure's width in air
      // on each side and it still reads as the narrow hat Marc asked for.
      const step = 1 / kMax, shoulder = step * 0.105, flat = step * 0.14;
      const pt = (f: number, r: number) => {
        const a = angU(Math.max(-0.06, Math.min(1.06, f)));
        return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
      };
      // "pelin mas gruesa"
      b.strokeStyle = CROWN; b.lineWidth = Math.max(1.5, rOut * 0.0085);
      b.lineJoin = 'round'; b.beginPath();
      {
        const seq: (readonly [number, number])[] = [];
        seq.push(pt(-0.04, rTrough));
        for (let k = 0; k <= kMax; k++) {
          const f = k * step;
          seq.push(pt(f - flat - shoulder, rTrough));
          seq.push(pt(f - flat, rCrest));
          seq.push(pt(f + flat, rCrest));
          seq.push(pt(f + flat + shoulder, rTrough));
        }
        seq.push(pt(1.04, rTrough));
        seq.forEach(([x, y], i) => (i ? b.lineTo(x, y) : b.moveTo(x, y)));
      }
      b.stroke(); b.lineJoin = 'miter';

      // THE FIGURE IS REFERENCED TO THE MIDDLE OF ITS HAT (Marc: "los numeros de las marchas tienen
      // q ir siempre referenciados a la mitad de la linea de en medio del sombrero"). It used to sit
      // on the same RADIUS as the hat's centre, which is not the same thing: everywhere but the top
      // of the arch that radius is tilted, so the figure - drawn upright - slid out from under its
      // bridge, toward the middle of the dial. Now it takes its x from the middle of the hat's flat
      // top and only its height from the radius, so every figure stands under its own hat.
      // capped, because at the two ends of a 144 degree sweep the radius is nearly horizontal and
      // the correction would be 0.05 of the radius: the 10 walked out onto the red blocks. Everywhere
      // it matters the correction is a fifth of that and lands in full.
      // capped, and not applied at all to the two ends: at the ends of a 144 degree sweep the radius
      // is nearly horizontal, the correction is 0.05 of the radius, and the 10 walks out onto the red
      // blocks and the yellow. Their hats are half outside the arch anyway. Everywhere between, the
      // correction is a third of that and lands in full.
      // WHAT WAS ACTUALLY OFF-CENTRE WAS THE INK, NOT THE POSITION. These figures are italic and
      // canvas centres the ADVANCE box, which for a slanted face is not where the ink sits: measured
      // on the render, every figure landed a constant 0.011 of the radius to the RIGHT of its own
      // anchor. That is a twentieth of a step of arc - enough to push the leading top corner off the
      // hat's flat top and onto the shoulder, and because every figure leans the same way while the
      // crown descends to the right, it showed on 5 to 10 and not on 0 to 4. Which is exactly what
      // Marc reported, down to which figures.
      // So the ink is measured and centred, and the position goes back to the ARC's own centre line:
      // pulling x toward the hat's screen x was a second correction for the same complaint and it
      // cannot work at the ends, where the radius is nearly horizontal (aligning the 9 that way puts
      // it at 0.89 of the radius, inside the blocks).
      const nFont = ital(Math.round(rOut * 0.1009));
      for (let k = 0; k <= kMax; k++) {
        const [x, y] = pt(k * step, rN);
        b.font = nFont;
        const m = b.measureText(String(k));
        const ink = (m.actualBoundingBoxRight - m.actualBoundingBoxLeft) / 2;
        text(String(k), x - ink, y, nFont,
          k * 1000 >= (profile.redlineRpm ?? max) ? LAMBO_RED : '#ffffff', 'center', 'middle');
      }
      const [rpx, rpy] = at(0.660, -0.230);
      text('RPM', rpx, rpy, ital(Math.round(rOut * 0.026), 600), STRIP, 'center', 'middle');
      text('x1000', rpx, rpy + rOut * 0.028, ital(Math.round(rOut * 0.026), 600), STRIP, 'center', 'middle');

      // the needle, a thin hair crossing the blocks
      // A TAPERED WEDGE, not a hair. Blown up, the pointer in Marc's frame is a triangle: broad where
      // it crosses the outer edge of the blocks and coming to a point on the inside. Drawn as a line of
      // constant width it read as a scratch on the glass.
      const an = ang(frac);
      b.save(); b.translate(cx, cy); b.rotate(an);
      const nw = rOut * 0.011;
      b.fillStyle = '#ff2a12'; b.beginPath();
      b.moveTo(rOut * 0.800, 0);
      b.lineTo(rOut * 1.010, -nw);
      b.lineTo(rOut * 1.010, nw);
      b.closePath(); b.fill();
      b.restore();

      // the blue wash over the arch: faint while driving, flashing at the limiter
      const flash = s.limiter ? (Math.sin(t * 0.022) > 0 ? 0.85 : 0.25) : 0.08 * frac;
      if (flash > 0.02) {
        b.save(); b.globalAlpha = flash;
        const gl = b.createRadialGradient(cx, cy - rOut * 1.06, rOut * 0.05, cx, cy - rOut * 1.06, rOut * 0.85);
        gl.addColorStop(0, 'rgba(90,175,255,0.85)'); gl.addColorStop(0.55, 'rgba(40,110,220,0.35)');
        gl.addColorStop(1, 'rgba(20,60,150,0)');
        // Filled through the ARCH'S OWN ANNULUS, not a rectangle. The rectangle's bottom edge cut
        // the gradient while it still had alpha, drawing a hard horizontal seam right across the
        // dial (it showed up as 955 pixels of a single row stepping together).
        b.beginPath();
        b.arc(cx, cy, rOut * 1.045, A0, A1);
        b.arc(cx, cy, rOut * 0.800, A1, A0, true);
        b.closePath();
        b.fillStyle = gl; b.fill();
        b.restore();
      }

      // ---- the middle ----
      const nGears = profile.gearTopSpeedsKmh?.length ?? 7;
      const gears = ['R', 'N', ...Array.from({ length: nGears }, (_, i) => String(i + 1))];
      // measured: the digit's centre sits 0.039 of the arch's width left of the middle and 0.240 of it
      // below the apex. The strip shares its row and its anchor, because the digit stands in the strip's
      // own slot - so they move together or the figure stops covering the gear you are in.
      // Centroid of the giant red digit in Marc's frame, measured by labelling the red blobs:
      // dx -0.069, dy -0.535 of the arch's radius.
      // the digit's own centre is its INK's bbox mid, -0.0745, which is a little left of the
      // clearing's centre (-0.038) rather than square in it
      const GDX = -0.0745, GDY = -0.5350;
      // the strip rides very slightly ABOVE the digit's own centre line, by 0.005 of the arch's width
      // measured against the frame: the row lands at dy -0.555, and at -0.0092 mine sat at -0.543
      const GDY_STRIP = GDY - 0.0212;
      const [, sty] = at(GDX, GDY_STRIP);
      // THE ROW SITS ON A BAND. In Marc's close-up the gear letters lie on a lighter blue stripe that
      // crosses the dial and feathers away - above, below AND at its ends. Drawn as a lens rather than
      // a rectangle, because a rectangle finishes on two hard vertical edges that the car does not have.
      // THE BLUE DIVISION LINE IS A BAR (Marc: "esa division line q hay en color azul, ahora la forma
      // q tenemos no se parece en nada a la q es"). I had drawn it as a lens - two quadratics over
      // and under the row - and a lens is an ellipse, which is what it read as. In his photograph it
      // is a straight band of blue lying across the dial: level top and bottom edges, a lighter line
      // along the top of it, and it does not stop, it feathers out at both ends.
      {
        const bh2 = rOut * 0.058, bw2 = rOut * 0.72;
        // Both fades at once without erasing what is behind it: `destination-out` would have taken
        // the honeycomb out with the band's ends. The bar is laid down as horizontal slices, each
        // one carrying the END fade in its own gradient and the TOP-TO-BOTTOM fade in its alpha.
        const SL = 12;
        b.save();
        for (let i = 0; i < SL; i++) {
          const q = (i + 0.5) / SL;                                  // 0 at the top edge, 1 at the bottom
          const a = q < 0.18 ? 0.10 + (q / 0.18) * 0.26 : 0.36 - ((q - 0.18) / 0.82) * 0.33;
          const gS = b.createLinearGradient(cx - bw2, 0, cx + bw2, 0);
          gS.addColorStop(0, 'rgba(96,138,196,0)');
          gS.addColorStop(0.24, 'rgba(96,138,196,1)');
          gS.addColorStop(0.76, 'rgba(96,138,196,1)');
          gS.addColorStop(1, 'rgba(96,138,196,0)');
          b.globalAlpha = a;
          b.fillStyle = gS;
          b.fillRect(cx - bw2, sty - bh2 + (2 * bh2 * i) / SL, bw2 * 2, (2 * bh2) / SL + 1);
        }
        // the lighter line the band carries along its top edge
        const gT = b.createLinearGradient(cx - bw2, 0, cx + bw2, 0);
        gT.addColorStop(0, 'rgba(160,200,250,0)');
        gT.addColorStop(0.28, 'rgba(160,200,250,1)');
        gT.addColorStop(0.72, 'rgba(160,200,250,1)');
        gT.addColorStop(1, 'rgba(160,200,250,0)');
        b.globalAlpha = 0.30; b.fillStyle = gT;
        b.fillRect(cx - bw2, sty - bh2, bw2 * 2, Math.max(1, rOut * 0.0035));
        b.restore();
      }
      // 0.0439 and not a hair less: reducing it made the MEASURED pitch go up, which is the metric
      // failing rather than the render changing - the row has an empty slot where the digit stands, so a
      // median gap can be a double, and a lattice fit on the photograph locks onto the second harmonic
      // at a coherence of 0.12. This number cannot be resolved better than about five per cent from the
      // frames we have, and it is left where it measured best.
      // THE ROW IS NOT A UNIFORM LADDER WITH ONE SLOT MISSING, which is what I had built. Measuring
      // the gaps between the eight glyphs in Marc's frame: the median step is 0.0423 of the radius and
      // there is ONE gap of 0.324 in the middle. So R and N sit packed together on the left, the giant
      // digit stands in a wide clearing, and 2..7 run packed away to the right. Modelling it as nine
      // even slots forced the pitch up to 0.0725 to span the same width and spread the letters out.
      const gwid = rOut * 0.0423;
      const here = s.engine !== 'on' || s.freeRev || s.gear === 0 ? 1 : Math.min(gears.length - 1, 1 + s.gear);
      const [gx, gy] = at(GDX, GDY);
      const gh2 = rOut * 0.3506;
      // There is ALWAYS a gear showing (Marc): with the engine off it reads N, never a dash.
      const gearTxt = s.engine !== 'on' || s.freeRev || s.gear === 0 ? 'N' : String(s.gear);
      // NO GEAR MAY CUT ANOTHER (Marc: in N the R came out sliced in half). The clearing the row
      // leaves for the giant digit was a MEASURED pair of constants, +-0.162 of the radius about
      // dx -0.038, and the digit is wider than that on its left side: its ink runs from -0.23 to
      // +0.09, so whatever letter sat at the left edge of the clearing was painted over. The
      // clearing is now taken from THE GLYPH ITSELF - the ink bounds of the figure actually being
      // drawn, scaled to the size it is drawn at - so R, N and 1..7 stand clear of it in every gear
      // and it does not matter which figure is standing there or how wide it is.
      const inkX = (ch: string) => {
        const rings = SVJ_GLYPH[ch] ?? SVJ_GLYPH['-'];
        let lo = 1e9, hi = -1e9;
        for (const r of rings) for (const [x] of r) { if (x < lo) lo = x; if (x > hi) hi = x; }
        return [lo, hi] as const;
      };
      {
        const [lo, hi] = inkX(gearTxt);
        const u2 = (gh2 / 100) * 1.0017;
        // half a strip letter plus a real gap, so they clear the digit instead of touching it
        const pad = rOut * 0.032;
        const leftEdge = gx + lo * u2 - pad, rightEdge = gx + hi * u2 + pad;
        gears.forEach((g, i) => {
          if (i === here) return;   // the slot the giant digit stands in is left empty
          const gx2 = i < here
            ? leftEdge - (here - 1 - i) * gwid
            : rightEdge + (i - here - 1) * gwid;
          text(g, gx2, sty, ital(Math.round(rOut * 0.059), 600), STRIP, 'center', 'middle');
        });
      }
      if (gearTxt !== svjGear.last) { svjGear.prev = svjGear.last; svjGear.last = gearTxt; svjGear.changedAt = t; }
      // THE CHANGE IS AN ANIMATION, and it has to be one that SHOWS. My first attempt only emptied the
      // digit to an outline for a quarter second, which is invisible for most of a lap: below 6000 the
      // digit is already an outline, so hollow-to-hollow is no change at all. Marc's frames show what
      // it really does - the old figure holds hollow, then the new one comes in SMALL and grows to
      // full before it fills - and scale reads in both states.
      // IT DOES NOT EMPTY ON THE WAY THROUGH A CHANGE (Marc: "si sabes q va a cambiar de 8900rpm a
      // 7000, el color se queda relleno de rojo, no pasa primero a sin tener relleno a tener
      // relleno"). Both ends of an upshift in CORSA are above 6000 with the pedal down, so the fill
      // has no business blinking off in between: the shift no longer takes part in the decision, the
      // rpm and the pedal do. The figure still shrinks and swells - that is the change - it just
      // does it filled.
      const digitFilled = corsa && s.rpm >= 6000 && s.throttleIntent > 0.05;
      const GD = 340;
      const age = t - svjGear.changedAt;
      if (age < GD && svjGear.prev) {
        // THE CHANGE, ALL THREE BEATS OF IT (Marc: "el 2 pasa a ser del tamaño de los numeros donde se
        // alojan todas las marchas, y luego de ahí se ilumina la siguiente marcha y vuelve a hacerse
        // grande - esa es la animación completa"). The figure does not shrink to some fraction and
        // swell again: it goes and JOINS THE ROW, at the row's own size and on the row's own line, the
        // next gear lights up there, and that one grows back out into the clearing.
        //
        // SMALL is not a number I picked. It is the strip's own type size - the same rOut * 0.059 the
        // row is set in, times the cap height of that face - divided by the digit's height, so the two
        // can never drift apart when either is retuned. It works out around 0.12; it used to be 0.30,
        // which is why it read as "smaller" rather than as "one of those".
        const SMALL = (rOut * 0.059 * 0.72) / gh2;
        const p2 = age / GD;
        if (p2 < 0.45) {
          const q = p2 / 0.45, e = q * q;                    // gathers speed as it goes in
          b.save(); b.globalAlpha = 1 - 0.45 * e;
          svjGearDigit(svjGear.prev, gx, gy + (sty - gy) * e, gh2 * (1 - (1 - SMALL) * e), digitFilled, DIGIT, DIGIT_IN);
          b.restore();
        } else {
          // …and the new one LIGHTS UP where the old one landed - full brightness the instant it
          // appears, not a fade-in - before it grows. The old pass came in at 0.35 alpha and rose,
          // which reads as the same figure recovering rather than as the next gear arriving.
          const q = (p2 - 0.45) / 0.55, e = 1 - (1 - q) * (1 - q);   // and eases out of the swell
          svjGearDigit(gearTxt, gx, sty + (gy - sty) * e, gh2 * (SMALL + (1 - SMALL) * e), digitFilled, DIGIT, DIGIT_IN);
        }
      } else {
        // the pedal, not the effective throttle: a shift cut must not blink the fill off and on
        svjGearDigit(gearTxt, gx, gy, gh2, digitFilled, DIGIT, DIGIT_IN);
      }

      // NO ESC, in the hexagon the car draws it in, and the suspension lift beside the digit
      // well inside the figures: at the old radius the badge sat on the 4 and the lift icon on the 6
      // Two lines of RED TEXT at dx +0.193, dy -0.701 and -0.669, measured off the red blobs in
      // Marc's frame. It was a filled hexagon reading NO ESC on the OTHER SIDE of the digit, which is
      // a shape the cluster does not draw at all.
      // ESC CORSA, back at Marc's request and ONLY in CORSA: turning the manettino to it is what
      // puts the stability control in its track setting, and the cluster says so in the car's red,
      // two lines, on the right of the giant digit. Positions measured off the red blobs in his
      // screenshot: dx +0.193, dy -0.701 and -0.669.
      if (corsa) {
        const [ex, ey] = at(0.193, -0.701);
        text('ESC', ex, ey, ital(Math.round(rOut * 0.036), 700), LAMBO_RED, 'center', 'middle');
        text('CORSA', ex, ey + rOut * 0.032, ital(Math.round(rOut * 0.036), 700), LAMBO_RED, 'center', 'middle');
      }

      // AUTO START-STOP, DEFEATED: the ring with an A in it and a stroke through it, out in the
      // panel's top right corner clear of the arch. It was a yellow battery with OFF under it sitting
      // beside the gear digit, where it collided with the ESC line.
      // the yellow start-stop glyph: removed at Marc's request.
      // the crossed-out AUTO START-STOP ring: removed at Marc's request.
      const [mx, my] = at(-0.463, -0.442);
      // TWO MODES (Marc): SPORT and CORSA. STRADA and EGO exist on the real manettino but this app
      // only ever resolves two styles, so naming a third was naming a position we cannot select.
      text(corsa ? 'CORSA' : 'SPORT', mx, my,
        ital(Math.round(rOut * 0.052)), DIGIT, 'center', 'middle');
      // THE ODOMETER IS THE CAR'S, and it goes back where the car puts it: eight characters running
      // dx +0.196..+0.514 at dy -0.45. Our lap timing, which used to sit on top of it, drops clear.
      const [odx, ody] = at(0.355, -0.450);
      // Set character by character on the MEASURED pitch. Drawn as one string in our condensed
      // italic it came out 0.175 wide against the car's 0.318 - the cluster's face is wide, and no
      // font size can fix a pitch, only make the figures too tall.
      {
        const odo = String(Math.floor(sim812.odoKm)).padStart(6, '0') + ' km';
        // 0.0398: the string carries a space, so its eight visible glyphs are EIGHT pitches apart,
        // not seven. Dividing by seven made the row 13 per cent too wide.
        const opitch = rOut * 0.0398;
        for (let i = 0; i < odo.length; i++) {
          text(odo[i], odx + (i - (odo.length - 1) / 2) * opitch, ody,
            ital(Math.round(rOut * 0.052), 600), '#e8eef7', 'center', 'middle');
        }
      }
      // the lap block on the dial: removed at Marc's request; the timer lives in the top corner.


      // ---- the G-meter, lower left: a perspective pyramid with the G in it ----
      {
        if (svjG.t === 0) { svjG.t = t; svjG.spd = s.speedKmh; }
        const dt = Math.max(0.016, (t - svjG.t) / 1000);
        if (dt > 0.05) {
          const raw = ((s.speedKmh - svjG.spd) / 3.6) / dt / 9.81;
          svjG.lon += (Math.max(-2, Math.min(2, raw)) - svjG.lon) * 0.25;
          svjG.spd = s.speedKmh; svjG.t = t;
        }
        // Sits where the real cluster puts it, left of the aero panel and INSIDE the arch, but
        // lifted clear of the ANIMA/THRUST keys: those are ours, the real car has them on the
        // tunnel, and at the photograph's own height they landed straight on top of each other.
        // Measured: the column of white blobs that make up this graphic sits at dx -0.68..-0.45 and
        // runs dy -0.25 to -0.04, so its centre is about (-0.62, -0.145) and it stands 0.21 tall.
        const [gmx, gmy] = at(-0.620, -0.145);
        const gw = rOut * 0.255, ghh = rOut * 0.210;
        const topW = gw * 0.34;
        b.save();
        b.translate(gmx, gmy);
        b.strokeStyle = 'rgba(196,214,236,0.72)';
        b.lineWidth = Math.max(1, rOut * 0.0042);
        // the pyramid: narrow at the top, wide at the foot, the way a road recedes
        b.beginPath();
        b.moveTo(-topW / 2, -ghh / 2); b.lineTo(topW / 2, -ghh / 2);
        b.lineTo(gw / 2, ghh / 2); b.lineTo(-gw / 2, ghh / 2);
        b.closePath(); b.stroke();
        // rungs across it, widening as they come forward
        for (let i = 1; i <= 3; i++) {
          const q = i / 4, yy = -ghh / 2 + q * ghh, ww = topW + (gw - topW) * q;
          b.globalAlpha = 0.5;
          b.beginPath(); b.moveTo(-ww / 2, yy); b.lineTo(ww / 2, yy); b.stroke();
        }
        b.globalAlpha = 1;
        // the flanking hatch bars either side of the letter
        const bars = (sx: number) => {
          for (let i = 0; i < 4; i++) {
            const yy = -ghh * 0.06 + i * ghh * 0.085, ln = gw * 0.10;
            b.globalAlpha = 0.62;
            b.beginPath();
            b.moveTo(sx * (gw * 0.16 + i * gw * 0.022), yy);
            b.lineTo(sx * (gw * 0.16 + i * gw * 0.022 + ln), yy);
            b.stroke();
          }
          b.globalAlpha = 1;
        };
        bars(-1); bars(1);
        text('G', 0, ghh * 0.04, ital(Math.round(rOut * 0.052), 700), '#dbe6f4', 'center', 'middle');
        b.restore();
        // the four figures: longitudinal is real, lateral is honestly zero
        const gf = (v: number) => Math.abs(v).toFixed(1);
        const gfont = ital(Math.round(rOut * 0.030), 600), gcol = '#e4ecf7';
        text(gf(svjG.lon > 0 ? svjG.lon : 0), gmx, gmy - ghh * 0.50, gfont, gcol, 'center', 'middle');
        text(gf(svjG.lon < 0 ? svjG.lon : 0), gmx, gmy + ghh * 0.50, gfont, gcol, 'center', 'middle');
        text('0.0', gmx - gw * 0.62, gmy, gfont, gcol, 'center', 'middle');
        text('0.0', gmx + gw * 0.62, gmy, gfont, gcol, 'center', 'middle');
      }

      // ALA / ANT / POST / CARICO ALA / SISTEMA ALA: removed at Marc's request.

      // speed, bottom right
      // dx +0.718, dy -0.12 (the 91-pixel-tall white blob), with the unit at dy -0.02. It was at
      // +0.460, a quarter of the arch's radius too far in towards the middle.
      // the glyph's own centroid lands 0.024 left of where it is anchored, so the anchor goes right
      const [spx, spy] = at(0.742, -0.124);
      text(String(Math.round(spd(s.speedKmh))), spx, spy, ital(Math.round(rOut * 0.135)), '#fff', 'center', 'middle');
      text(unitLabel, spx, spy + rOut * 0.100, ital(Math.round(rOut * 0.038), 500), STRIP, 'center', 'middle');

      // LAUNCH, in the red hexagon where the car puts its THRUST MODE banner: one word across the app
      const launchReady = s.engine === 'on' && s.speedKmh < 3 && s.brake > 0.25;
      const launching = launchReady && s.throttle > 0.5;
      if (launchReady) {
        const [bx, by] = at(-0.044, -0.060);
        const bw = rOut * 0.42, bh = rOut * 0.105;
        b.beginPath();
        b.moveTo(bx - bw / 2 + bh * 0.4, by - bh / 2); b.lineTo(bx + bw / 2 - bh * 0.4, by - bh / 2);
        b.lineTo(bx + bw / 2, by); b.lineTo(bx + bw / 2 - bh * 0.4, by + bh / 2);
        b.lineTo(bx - bw / 2 + bh * 0.4, by + bh / 2); b.lineTo(bx - bw / 2, by);
        b.closePath();
        b.fillStyle = launching ? 'rgba(255,42,23,0.25)' : 'rgba(8,10,14,0.88)'; b.fill();
        b.strokeStyle = LAMBO_RED; b.lineWidth = Math.max(1.5, rOut * 0.007); b.stroke();
        text(launching ? 'LAUNCH' : 'LAUNCH READY', bx, by, ital(Math.round(bh * 0.42)), '#fff', 'center', 'middle');
      }

      // OUR controls, not the car's. They go DOWN THE BOTTOM now (Marc: "pon todos los botones
      // debajo, entre las fuerzas G y a la izq del velocimetro") - the gap the cluster leaves between
      // the G-meter and the speed, which is the one place inside the arch with nothing in it. They
      // were in the top-left corner, where they sat over the honeycomb the moment the panel grew.
      // Anchored in ARCH units, not screen units, so they keep their place at every aspect. The box
      // was 0.55 of the radius wide by 0.14 tall and the keys inside it came out 50 by 36 px on the
      // Tesla (Marc: 'los botones del Lambo son pequeños'). v491, Marc again: 'el start debajo de las
      // fuerzas G, el resto más amplio pero un pelín más pequeño y más abajo, que aún tienes espacio'.
      // The row is 0.80 wide by 0.17 tall now, its foot at -0.02 (it was -0.065). The starter goes
      // under the G-meter only where the canvas has the room: on the Tesla's 4:3 the arch leaves
      // 0.23 of a radius under the centre, on a wide desktop or a phone it leaves 0.04, and a starter
      // squeezed onto the G-meter's figures is worse than one at the head of the row.
      if (c?.showStart !== false) {
        const room = (h - 6 - cy) / rOut;
        const startBelowG = room >= 0.13;
        // Marc, v492: 'bájalos todos al nivel del start, y el start un pelín más abajo, similar a la
        // foto'. One centre line at +0.11 of the radius for the starter and the row alike where the
        // starter stands under the G-meter; on the cramped formats the row keeps its old height.
        // v497 (Marc): a bigger starter, and the three others further right. The line now runs to
        // +0.52 of the radius (it stopped at +0.40) and the starter's radius is 0.085 (was 0.07);
        // the equal gaps grow with it, and the starter's own line rose a hair so it clears the foot.
        // v505 (Marc): everything on this line moves right again, the starter included, and the
        // starter grows. The row now runs to +0.68 of the radius (it stopped at +0.52 and v504 shoved
        // the keys past it by hand), so the trio hangs under the speed instead of floating in the
        // middle of the arch; the starter steps out from under the G-meter's centre to its right
        // shoulder at -0.545 and its radius goes 0.085 → 0.100, which is the size a thumb wants on a
        // screen with no haptics.
        const [ctx0, cty00] = startBelowG ? at(-0.40, 0.03) : at(-0.40, -0.19);
        // the bigger starter is the tallest thing on the line, so the line has to know it: at 0.100 of
        // the radius it reaches 0.215 below the dial's centre and on a canvas that only leaves 0.13 it
        // used to have its bottom point sliced off by the edge. Lift the row by whatever it overruns.
        const rowH = rOut * 0.17, startR = rOut * 0.155;
        const foot = cty00 + rowH * 0.5 + startR;
        const cty0 = startBelowG ? cty00 - Math.max(0, foot - (h - 6)) : cty00;
        // …and the same on the other axis: the keys hang off the row's RIGHT edge, so the row is never
        // allowed to end past the canvas. Without this, pushing the row right simply slid LAUNCH's point
        // off the screen on the format with the least room, which is the one Marc drives.
        const rowW = Math.min(rOut * (startBelowG ? 1.30 : 1.00), w - rOut * 0.02 - ctx0);
        svjControls(ctx0, cty0, rowW, rowH, s, startBelowG ? { r: startR } : null);
      }
      // START TIMING and the fold chevron: removed at Marc's request. The lap timer runs itself
      // off the circuit's own line; there is nothing on this dial to press any more.
    };
    /**
     * Ferrari 812 Superfast — traced from Marc's photograph of the real binnacle.
     *
     * Three separate instruments on one black shelf, and the whole character of the car is that the
     * middle one is enormous and YELLOW while the two beside it are small and navy. What has to be
     * right for it to read as an 812 rather than "a round rev counter":
     *  - the tach face is a warm yellow with the light falling across it, not a flat swatch;
     *  - its numbers run 0 to 10 over about 280 degrees and are white, huge, and inside the ticks;
     *  - the needle is BLACK on that yellow, with a black hub the size of a coin;
     *  - the gear sits in a red lozenge at four o'clock with `auto` under it;
     *  - the left instrument is a plain white-on-navy speedometer with a clock over it;
     *  - the right one is the info panel: drive mode on a slanted banner, two temperature dials,
     *    a fuel strip, and the speed repeated in digits.
     * Above all of it, the LED bar off the steering wheel (Marc's second photograph): ten lamps,
     * eight red then two blue, filling left to right and flashing blue at the limiter.
     */
    /**
     * THE BADGE ON THE FACE. The real dial carries the marque's emblem here; ours carries the house
     * glyph, the six engine-waveform bars off the app icon (2026-09-10: the emblem is a registered
     * mark and this is a paid product). Same box the emblem had, 100 tall, 74 wide, centred on the
     * body, so its place and size on the dial did not move. The caller sets fill and stroke.
     */
    const f812Horse = (cx: number, cy: number, hh: number) => {
      const u = hh / 100;
      b.save(); b.translate(cx, cy); b.scale(u, u);
      // heights and greys follow icon.svg: small on the left, the last bar full height
      const bars: [number, number][] = [[-31, 10], [-19, 14], [-7, 18], [5, 26], [17, 40], [29, 100]];
      const bw = 7.4;
      for (const [x, h] of bars) {
        const r = bw / 2;
        b.beginPath();
        b.moveTo(x - r, -h / 2 + r);
        b.arc(x, -h / 2 + r, r, Math.PI, 0);
        b.lineTo(x + r, h / 2 - r);
        b.arc(x, h / 2 - r, r, 0, Math.PI);
        b.closePath();
        b.fill();
        b.lineWidth = 1.2; b.stroke();
      }
      b.restore();
    };
    /** the gear shield, traced off the badge in the 458 photograph; 111 wide by 100 tall */
    /**
     * The gear shield. Traced first, then drawn by hand, and the second is the honest answer: the
     * badge in the photograph is a moulded shield with a soft point at its foot, and the trace kept
     * catching the light border around it, so it came out either jagged or - once smoothed enough to
     * lose the jaggedness - a rounded blob with no point at all. These are its measured proportions,
     * 1.11 wide for its height, in the shape the crop plainly shows. Same outline on both cars: Marc
     * said so and the measurement agrees, 1.129 on the 812 against 1.109 on the 458.
     */
    const drawF812 = (t: number, s: DrivetrainState, _lap: LapState) => {
      const FER_RED = '#d40000';
      const DIAL_MAX = 10000;

      b.fillStyle = '#050506'; b.fillRect(0, 0, w, h);

      // ---- the steering wheel's LED bar, over the top of everything ----
      // Marc's second photograph is of the wheel itself, and the thing it shows that a straight row of
      // ten boxes cannot is that the strip is CURVED: it follows the top of the rim, so the lamps at
      // the ends sit lower than the ones in the middle and each one is tilted to the arc. Eight red
      // then two blue, each with the bloom the real ones throw onto the rim around them.
      const nLed = 10, ledW = w * 0.019, ledH = h * 0.026, gap = w * 0.007;
      const barW = nLed * ledW + (nLed - 1) * gap;
      const arcR = barW * 2.6, arcCx = w * 0.5, arcCy = h * 0.055 + arcR;
      const halfA = barW / (2 * arcR);
      const litTo = Math.max(0, Math.min(nLed, Math.floor(((s.rpm / max) - 0.45) / 0.55 * nLed + 0.001)));
      const limFlash = s.limiter && Math.sin(t * 0.02) > 0;
      // the rim the lamps are set into, so they read as inlaid rather than floating
      b.save(); b.strokeStyle = 'rgba(255,255,255,0.045)'; b.lineWidth = ledH * 1.55; b.lineCap = 'round';
      b.beginPath(); b.arc(arcCx, arcCy, arcR, -Math.PI / 2 - halfA * 1.18, -Math.PI / 2 + halfA * 1.18); b.stroke();
      b.restore();
      for (let i = 0; i < nLed; i++) {
        const blue = i >= nLed - 2;
        const on = s.engine === 'on' && (limFlash ? true : i < litTo);
        const a = -Math.PI / 2 - halfA + (i / (nLed - 1)) * 2 * halfA;
        b.save();
        b.translate(arcCx + arcR * Math.cos(a), arcCy + arcR * Math.sin(a));
        b.rotate(a + Math.PI / 2);
        if (on) {
          const core = b.createLinearGradient(0, -ledH / 2, 0, ledH / 2);
          if (blue) { core.addColorStop(0, '#4d93ff'); core.addColorStop(0.45, '#9ec6ff'); core.addColorStop(1, '#1247c8'); }
          else { core.addColorStop(0, '#ff5a2a'); core.addColorStop(0.45, '#ffb494'); core.addColorStop(1, '#c40d00'); }
          b.fillStyle = core;
          b.save(); b.shadowColor = blue ? '#2f7bff' : '#ff2a12'; b.shadowBlur = h * 0.045;
          rr(-ledW / 2, -ledH / 2, ledW, ledH, h * 0.005); b.fill(); b.fill(); b.restore();
        } else {
          b.fillStyle = blue ? 'rgba(40,70,130,0.28)' : 'rgba(120,25,18,0.28)';
          rr(-ledW / 2, -ledH / 2, ledW, ledH, h * 0.005); b.fill();
        }
        b.restore();
      }

      // ---- centre: the tachometer ----
      // Rebuilt off Marc's second set of photographs, which are sharp enough to measure and overturn
      // two things the first blurry one had me draw backwards. The figures are CREAM and lit, not
      // black; and the needle is the RED arm, with a short black tail opposite it - in every shot the
      // black arm points up while the car is parked, which no rev counter does.
      //
      // R is the whole dial to the outer edge of its tick band. Measured against that: the olive face
      // ends at 0.84, the ticks live on the dark ring outside it, the figures ride at 0.72, and the
      // ladder of fine arcs sits between 0.46 and 0.63.
      // and the cluster itself grows to fill a narrow screen: its three instruments span about
      // 5.95 of the tacho's radius, so 0.155 of the width is what fits, not 0.13
      const cx = w * 0.5, cy = h * 0.505, R = Math.min(h * 0.215, w * 0.155);
      // 0 straight down, 10 back round to three o'clock: 275 degrees, 27.5 a thousand revs. Measured
      // from the pixel positions of the 0, the 3, the 5, the 7 and the 10 in the full-dial photograph.
      const A0 = (88 * Math.PI) / 180, A1 = (363 * Math.PI) / 180;
      const ang = (v: number) => A0 + Math.max(0, Math.min(1, v / DIAL_MAX)) * (A1 - A0);
      // per car, not per dial: the 812 goes red at 8700, the 458 at its own redline
      const RED_FROM = profile.redlineRpm ?? Math.round(max * 0.96);
      const bezR = R * 1.25;

      // the binnacle, an oval whose centre sits above the dial's own
      b.save(); b.translate(cx, cy - R * 0.34); b.scale(1, 1.13);
      const bez = b.createRadialGradient(0, R * 0.30, R * 0.08, 0, 0, bezR);
      bez.addColorStop(0, '#3b342d'); bez.addColorStop(0.34, '#211e1a');
      bez.addColorStop(0.62, '#121110'); bez.addColorStop(1, '#0a0908');
      b.fillStyle = bez; b.beginPath(); b.arc(0, 0, bezR, 0, Math.PI * 2); b.fill();
      if (litTo > 0 || limFlash) {
        const heat = limFlash ? 1 : Math.min(1, litTo / nLed);
        b.save(); b.beginPath(); b.arc(0, 0, bezR, 0, Math.PI * 2); b.clip();
        const gy2 = -bezR * 0.74;
        const red = b.createRadialGradient(0, gy2, R * 0.04, 0, gy2, bezR * 0.55);
        red.addColorStop(0, `rgba(255,29,13,${(0.17 * heat).toFixed(3)})`);
        red.addColorStop(1, 'rgba(255,29,13,0)');
        b.fillStyle = red; b.fillRect(-bezR, -bezR, bezR * 2, bezR * 2);
        if (litTo >= nLed - 1 || limFlash) {
          const blue = b.createRadialGradient(0, gy2, R * 0.03, 0, gy2, bezR * 0.34);
          blue.addColorStop(0, 'rgba(47,123,255,0.13)'); blue.addColorStop(1, 'rgba(47,123,255,0)');
          b.fillStyle = blue; b.fillRect(-bezR, -bezR, bezR * 2, bezR * 2);
        }
        b.restore();
      }
      b.strokeStyle = 'rgba(255,255,255,0.22)'; b.lineWidth = Math.max(1, R * 0.016);
      b.beginPath(); b.arc(0, 0, bezR * 0.965, Math.PI * 1.06, Math.PI * 1.70); b.stroke();
      b.strokeStyle = 'rgba(255,255,255,0.06)'; b.lineWidth = Math.max(1, R * 0.012);
      b.beginPath(); b.arc(0, 0, bezR * 0.965, 0, Math.PI * 2); b.stroke();
      b.restore();

      // the dark ring the tick marks are printed on, outside the olive
      b.fillStyle = '#100e0b'; b.beginPath(); b.arc(cx, cy, R, 0, Math.PI * 2); b.fill();
      // a thin chrome lip around the whole dial
      b.strokeStyle = 'rgba(214,209,198,0.55)'; b.lineWidth = Math.max(1, R * 0.022);
      b.beginPath(); b.arc(cx, cy, R * 0.995, 0, Math.PI * 2); b.stroke();

      // the olive face. Not the bright yellow of the first attempt: it is a deep olive gold, brightest
      // up and to the left where the instrument's own light falls, and it darkens to a brown at the rim.
      const DIAL = profile.hud?.dial;
      const FACE = DIAL?.face ?? ['#d8b03c', '#bb932a', '#8a6b18', '#513c0b'];
      const face = b.createRadialGradient(cx - R * 0.24, cy - R * 0.26, R * 0.04, cx, cy + R * 0.06, R * 0.92);
      face.addColorStop(0, FACE[0]); face.addColorStop(0.42, FACE[1]);
      face.addColorStop(0.78, FACE[2]); face.addColorStop(1, FACE[3]);
      b.fillStyle = face; b.beginPath(); b.arc(cx, cy, R * 0.84, 0, Math.PI * 2); b.fill();

      // ticks, on the dark ring: every 200 rpm short, every 1000 long, orange once past the line
      // every hundred, not every two hundred: counting them between the 0 and the 1 in the photograph
      // gives nine or ten, and that fineness is a lot of why the dial reads as an instrument
      for (let v = 0; v <= DIAL_MAX; v += 100) {
        const a = ang(v), major = v % 1000 === 0;
        const r1 = R * (major ? 0.850 : 0.915), r2 = R * 0.985;
        // THE TICKS STAY WHITE THROUGH THE REDLINE. Sampling the tick ring in the aligned polar
        // frame, the brightest pixels past 8500 rpm measure (236,235,238) on Marc's dial against
        // (171,116,79) on mine: he marks the red zone with the FIGURES and the thin arc, and I was
        // turning the whole comb orange.
        b.strokeStyle = 'rgba(246,236,206,0.95)';
        b.lineWidth = major ? Math.max(1.5, R * 0.017) : Math.max(1, R * 0.007);
        b.beginPath();
        b.moveTo(cx + r1 * Math.cos(a), cy + r1 * Math.sin(a));
        b.lineTo(cx + r2 * Math.cos(a), cy + r2 * Math.sin(a));
        b.stroke();
      }

      // THE LADDER. Five fine concentric arcs with rungs across them, which is the detail that makes
      // this dial itself rather than a generic rev counter, and which the first pass drew as one plain
      // circle. Cream up to the redline figure, orange after it.
      // Marc: "la anchura de las lineas estas blancas q creo son negras". He is right on both counts.
      // Radially scanning the band, the arcs sit between 0.45R and 0.57R and dip to a tenth-percentile
      // luma of 34 against a face at 86 - so on the 458 they are DARKER than the face, not lighter, and
      // they measure 0.012 of the radius across where I drew them at 0.006. They follow the figures'
      // colour the way the horse does: cream on the 812's olive, dark on the 458's yellow.
      // Unrolled, his ladder occupies 0.52R to 0.77R. Mine sat at 0.45-0.60, which put it right
      // through the horse - Marc: "el caballo no esta encima de esas lineas circulares".
      // FOUR, AND PACKED (Marc, on the 812: "las 4 lineas q son arcos tienen q ir juntisisimas!! la
      // unica q no se mueve de su lugar es la q está más en el interior"). Mine were five, spread
      // over a quarter of the radius, which drew a wide combed band across half the face; his are a
      // fine group you read as one line until you look. The inner one stays at 0.52 and the other
      // three come in to meet it: 0.575 for the outermost, so the whole group is 0.055R deep.
      const L0 = R * 0.52, L1 = R * 0.575, LN = 4;
      const LAD = DIAL?.ladder ?? 'rgba(252,242,210,0.78)';
      // 0.010 came out as heavy rope: the photograph's 0.012-0.048 reading is its own blur, and
      // what the dial actually shows is fine engraving. Drawn at 0.006 and let through at part
      // strength, which is how it reads on both cars.
      b.lineWidth = Math.max(1, R * 0.006);
      for (let i = 0; i < LN; i++) {
        const r = L0 + (L1 - L0) * (i / (LN - 1));
        b.strokeStyle = LAD;
        b.beginPath(); b.arc(cx, cy, r, ang(900), ang(RED_FROM)); b.stroke();
        b.strokeStyle = 'rgba(255,106,16,0.72)';
        b.beginPath(); b.arc(cx, cy, r, ang(RED_FROM), ang(DIAL_MAX - 200)); b.stroke();
      }
      // the rungs cross the band only every thousand, and only its outer half, or the whole thing
      // closes up into a grid instead of reading as fine engraving
      for (let v = 1000; v < DIAL_MAX; v += 1000) {
        const a = ang(v), r0 = L0 + (L1 - L0) * 0.34;
        b.strokeStyle = v >= RED_FROM ? 'rgba(255,106,16,0.72)' : LAD;
        b.beginPath();
        b.moveTo(cx + r0 * Math.cos(a), cy + r0 * Math.sin(a));
        b.lineTo(cx + L1 * Math.cos(a), cy + L1 * Math.sin(a));
        b.stroke();
      }

      // 0 to 10, CREAM and lit. The first photograph was dark enough that these read as black on gold
      // and I drew them that way; at this resolution they are plainly the brightest thing on the face.
      for (let k = 0; k <= 10; k++) {
        const a = ang(k * 1000), r = R * 0.72;
        const past = k * 1000 >= RED_FROM;
        text(String(k), cx + r * Math.cos(a), cy + r * Math.sin(a),
          cond(Math.round(R * 0.19), 500), past ? (DIAL?.numRed ?? '#ff7a18') : (DIAL?.num ?? '#f6ecce'), 'center', 'middle');
      }
      // the two figures the car prints small, laid along the arc
      for (const v of (profile.hud?.dialMarks ?? [])) {
        const a = ang(v), r = R * 0.80;
        b.save();
        b.translate(cx + r * Math.cos(a), cy + r * Math.sin(a));
        b.rotate(a + Math.PI / 2);
        text(String(v), 0, 0, cond(Math.round(R * 0.075), 600), '#f0e2b4', 'center', 'middle');
        b.restore();
      }
      // stacked ABOVE the shield, which is where the car puts them: the badge sits directly under
      // lifted clear: the shield's top edge is at +0.004R once it is drawn at its real size, and the
      // second line used to sit at -0.005R, straight underneath it
      text('RPM', cx + R * 0.31, cy - R * 0.135, cond(Math.round(R * 0.105), 600), DIAL?.num ?? '#f6ecce', 'center', 'middle');
      text('x 1000', cx + R * 0.31, cy - R * 0.055, cond(Math.round(R * 0.078), 500), DIAL?.num ?? '#f6ecce', 'center', 'middle');

      // MEASURED on the 458: centre (-0.471R, -0.135R), 0.325R tall. And its colour is the CAR'S,
      // not a constant: on the 812 the horse is a pale cream relief (luma 161 over a 49-110 face),
      // on the 458 it is DARK - luma 70 against a face of 138, half as bright as what it sits on.
      // I had measured the 812 and painted both cars cream.
      b.fillStyle = DIAL?.horse ?? '#f3ead6';
      b.strokeStyle = (DIAL?.horse ?? '#f3ead6') === '#f3ead6' ? 'rgba(60,42,8,0.45)' : 'rgba(255,240,190,0.30)';
      // In the rectified frame the horse sits at 0.412 of the sweep, 0.504 of the radius out, and
      // it is SMALL: about 0.18R tall. I had it at 0.36R, twice the size, so it sprawled across
      // the ladder however the two were placed.
      // 0.244R tall, measured on an 8x crop against a known dial radius - and the check that it is
      // the HORSE being measured and not a patch of shadow is that its width over its height comes
      // out 0.73, which is the cavallino's own 0.744. The blob readings that gave 0.36 and then
      // 0.18 both had w/h near 1.2: they were measuring the window, not the badge.
      // WHERE THE CAR PUTS IT (Marc): "el caballo está a la altura del numero 3 y 4 y cerca de la
      // aguja.. ahi en medio sin q toque el arco este ni nada". The 3 sits at 170 degrees and the 4
      // at 198, so the badge goes on 184 - between them - and comes IN to 0.34 of the radius, which
      // is the middle of the gap between the hub and the arcs. At 0.51 it was standing on the group
      // of arcs, and now that they have closed up to 0.52-0.575 it would have been sitting in them.
      {
        const ha = (184 * Math.PI) / 180, hr = R * 0.34;
        f812Horse(cx + hr * Math.cos(ha), cy + hr * Math.sin(ha), R * 0.244);
      }

      // THE GEAR SHIELD, traced off the badge in Marc's 458 photograph. It is the same outline on both
      // cars - measured, his claim checks out: 1.129 wide for its height on the 812 and 1.109 on the
      // 458 - and it is a shield with a pointed foot, not the clipped lozenge I had. Its size and place
      // are measured off the 812 frame too, and both were wrong: 0.431 of the dial radius across
      // against the half I was drawing, and sitting further in at 0.350 rather than 0.420.
      // The POSITION was right (+0.352R, +0.305R measured) but the SHIELD IS MUCH BIGGER than I drew
      // it: 0.661R across and 0.602R tall against my 0.431 x 0.382, so it was barely two thirds the
      // size. Marc: "la forma donde se aloja lo de la P N no es igual".
      // THE WINDOW, FROM MARC'S OWN DESCRIPTION OF IT, which is worth more than four passes of me
      // squinting at a reflection: "la barra de la izq es totalmnte vertical, la barra de arriba es
      // totalmnte horizontal. luego la otra de abajo es 45 grados a la derecha y luego de esos dos
      // puntos es circular equidistante al dial de fuera".
      // So it is not a badge shape at all - it is a panel cut out of the dial: a vertical left edge,
      // a horizontal top edge, a 45 degree chamfer off the bottom of the vertical, and the whole
      // outer side is an ARC CONCENTRIC WITH THE DIAL, riding at a constant distance inside the rim.
      // That is why every shield I drew looked wrong however I bent it: its outer edge was a shape's
      // edge, and this one belongs to the instrument.
      // Placed where he wants it (Marc): the horizontal drops CLEAR OF THE 10 - that figure sits at
      // dy +0.038R and stands 0.19R tall, so the panel's lid goes to +0.16R and stops cutting it; the
      // vertical moves right to +0.105R, out of the needle's way at rest (parked at 88 degrees it
      // passes through +0.022R); and the vertical runs further down, to +0.52R, which shortens the
      // 45 degree run to 0.24R because the arc catches it sooner.
      const wArc = R * 0.740;                 // the outer side, 0.10R inside the face's own 0.84R
      const wxL = cx + R * 0.105;             // the vertical
      const wyT = cy + R * 0.160;             // the horizontal
      const wyC = cy + R * 0.520;             // where the vertical gives way to the chamfer
      // the foot of the chamfer, which is where the panel's own shading has to end
      const qb = (wxL - cx) + (wyC - cy), qc = (wxL - cx) ** 2 + (wyC - cy) ** 2 - wArc * wArc;
      const wyC2 = wyC + (-qb + Math.sqrt(qb * qb - 2 * qc)) / 2;
      // WHERE THE LETTERING FITS, which is not the same as where the box's middle is. The panel's
      // floor is the ARC, so the room under a line of text depends on how far out it sits: at the
      // gear's own x the arc is at 0.626R, and `auto` was being drawn through it - I had placed both
      // lines off a box centre and never asked what the boundary was doing at their height. Solved
      // against the arc instead: the gear rides at 0.33R and the word at 0.545R, and because the
      // window narrows as it drops, the word is set a little further in than the figure.
      const gx = cx + R * 0.390, gy = cy + R * 0.330;
      const shield = (inset: number) => {
        const rA = wArc - inset, xL = wxL + inset, yT = wyT + inset, yC = wyC + inset * 0.4;
        const xR = cx + Math.sqrt(Math.max(1, rA * rA - (yT - cy) * (yT - cy)));
        const b2 = (xL - cx) + (yC - cy), c2 = (xL - cx) ** 2 + (yC - cy) ** 2 - rA * rA;
        const t2 = (-b2 + Math.sqrt(Math.max(0, b2 * b2 - 2 * c2))) / 2;
        const xC = xL + t2, yC2 = yC + t2;
        // "y tiene un poco de rounded corners": all four, including the two where a straight edge
        // runs into the arc - each side stops a hair short and a quadratic carries it round.
        const k = R * 0.038, d = k * 0.7071;
        const aT = Math.atan2(yT - cy, xR - cx), aC = Math.atan2(yC2 - cy, xC - cx);
        const aT2 = aT + k / rA, aC2 = aC - k / rA;
        b.beginPath();
        b.moveTo(xL + k, yT);
        b.lineTo(xR - k, yT);
        b.quadraticCurveTo(xR, yT, cx + rA * Math.cos(aT2), cy + rA * Math.sin(aT2));
        b.arc(cx, cy, rA, aT2, aC2);
        b.quadraticCurveTo(xC, yC2, xC - d, yC2 - d);
        b.lineTo(xL + d, yC + d);
        b.quadraticCurveTo(xL, yC, xL, yC - k);
        b.lineTo(xL, yT + k);
        b.quadraticCurveTo(xL, yT, xL + k, yT);
        b.closePath();
      };
      const gGrad = b.createLinearGradient(0, wyT, 0, wyC2);
      gGrad.addColorStop(0, DIAL ? DIAL.badge[0] : '#e01a10');
      gGrad.addColorStop(1, DIAL ? DIAL.badge[1] : '#7c0703');
      b.fillStyle = gGrad; shield(0); b.fill();
      b.strokeStyle = 'rgba(0,0,0,0.55)'; b.lineWidth = Math.max(1, R * 0.012); shield(0); b.stroke();
      // the inner highlight is a hint of a bezel, not a drawn outline: on his screen it barely reads
      b.strokeStyle = 'rgba(255,244,230,0.28)'; b.lineWidth = Math.max(1, R * 0.006); shield(R * 0.014); b.stroke();
      const gearTxt = s.engine !== 'on' ? 'P' : s.freeRev || s.gear === 0 ? 'N' : String(s.gear);
      const c = getControls ? getControls() : null;
      // THE FIGURE ALMOST TOUCHES THE TOP. In his close-up the gear fills the badge - its top is a
      // hair under the badge's own edge - where mine sat small and centred with a wide margin over
      // it. Bigger (0.72 of the badge's height against 0.62) and lifted to -0.15.
      // And it is ORANGE, not white: both of his crops show the figure in the same orange as the
      // dial's red-zone numerals, on a dark red badge.
      // THE LETTER ITSELF IS SQUARE (Marc: "cambia la forma de la P"). This is an LCD panel and its
      // characters are drawn the way a segment display draws them - a straight stem and a rectangular
      // bowl - where I was setting them in the same condensed sans as the rest of the dial, which
      // gives a round-bellied P and a soft 5. Orbitron is the squared face already loaded in the app,
      // and it is what a gear window reads like.
      // `auto` follows: a few millimetres to the left, and the SAME colour as the gear rather than a
      // cream of its own - in his close-up both lines are the one orange.
      const lcd = (px: number, wt = 700) => `${wt} ${px}px "Orbitron", "Michroma", sans-serif`;
      const gearCol = DIAL?.badgeText ?? '#ff7a18';
      text(gearTxt, gx, gy, lcd(Math.round(R * 0.30)), gearCol, 'center', 'middle');
      // higher again (Marc): 0.495R sits it just under the gear's own feet, which end at 0.435R
      text(c?.gearbox === 'manual' ? 'man' : 'auto', cx + R * 0.335, cy + R * 0.495,
        lcd(Math.round(R * 0.075), 500), gearCol, 'center', 'middle');

      // THE NEEDLE IS THE RED ARM. The black one opposite it is the tail, and it is short: in both
      // photographs the black arm points UP while the car sits in park, which no rev counter does.
      const an = ang(needle);
      b.save(); b.translate(cx, cy); b.rotate(an);
      // the tail
      // and it is NOT short: measured on the 458 the black arm runs out to 0.672 of the dial radius,
      // nearly as far as the red one. At 0.38 it was a stub, which is why the needle looked unbalanced
      // against the photograph even though its colours were the right way round.
      // SHORT, and I have had this wrong twice in both directions. Cropping the hub at a known
      // scale, the black arm runs about 0.26 of the dial radius from the centre: a stub beside
      // the pale pointer, not a second needle. 0.38 was too short by eye, 0.672 came from a
      // dark-pixel blob that had swallowed the numerals and the visor's shadow with it.
      b.fillStyle = '#0d0d0e'; b.beginPath();
      b.moveTo(-R * 0.262, -R * 0.016); b.lineTo(-R * 0.06, -R * 0.040);
      b.lineTo(-R * 0.06, R * 0.040); b.lineTo(-R * 0.262, R * 0.016);
      b.closePath(); b.fill();
      // THE POINTER IS A PALE BLADE WITH A LIT RED FILAMENT DOWN ITS SPINE (Marc's close-up: "es de
      // estas fotoluminiscentes en rojo"). Three things were wrong in mine. The red ran ALONGSIDE the
      // blade, off to one side, where the car runs it along the blade's own centre line. The blade
      // was a parallel bar; his tapers, wide where it leaves the hub and a hair at the tip. And the
      // red did not glow: on the real one it is a luminescent thread that throws a halo onto the
      // olive around it, which is most of why the needle reads as lit rather than painted.
      // The red also runs OUT past the blade, which the old one had right and which stays.
      {
        const bladeEnd = R * 0.60, redEnd = R * 0.755, hub = R * 0.055;
        const gBlade = b.createLinearGradient(hub, 0, bladeEnd, 0);
        gBlade.addColorStop(0, '#e6e6e2'); gBlade.addColorStop(0.55, '#cfd2d4'); gBlade.addColorStop(1, '#a9aeb4');
        b.fillStyle = gBlade; b.beginPath();
        b.moveTo(hub, -R * 0.038); b.lineTo(bladeEnd, -R * 0.011);
        b.lineTo(bladeEnd, R * 0.011); b.lineTo(hub, R * 0.038);
        b.closePath(); b.fill();
        // a hairline of shadow under the blade so it separates from the olive it lies on
        b.strokeStyle = 'rgba(40,28,6,0.35)'; b.lineWidth = Math.max(1, R * 0.004); b.stroke();
        // the filament: a close halo, then the thread, then a hot core inside it. The halo was
        // R*0.055 across and swallowed the blade whole - on the real needle the glow is tight to
        // the thread and the pale body reads right through it.
        b.save();
        b.shadowColor = 'rgba(255,42,18,0.9)'; b.shadowBlur = R * 0.020;
        b.fillStyle = '#e8180c'; b.beginPath();
        b.moveTo(hub, -R * 0.0115); b.lineTo(redEnd, -R * 0.0052);
        b.lineTo(redEnd, R * 0.0052); b.lineTo(hub, R * 0.0115);
        b.closePath(); b.fill();
        b.restore();
        b.fillStyle = '#ff6a4a'; b.beginPath();
        b.moveTo(hub, -R * 0.0045); b.lineTo(redEnd, -R * 0.0022);
        b.lineTo(redEnd, R * 0.0022); b.lineTo(hub, R * 0.0045);
        b.closePath(); b.fill();
      }
      b.restore();
      // the hub, a black dome
      const hub = b.createRadialGradient(cx - R * 0.03, cy - R * 0.04, R * 0.01, cx, cy, R * 0.089);
      hub.addColorStop(0, '#3a3a3d'); hub.addColorStop(0.6, '#161617'); hub.addColorStop(1, '#0a0a0b');
      // 0.089, measured off the crop; the dome was a third too wide
      b.fillStyle = hub; b.beginPath(); b.arc(cx, cy, R * 0.089, 0, Math.PI * 2); b.fill();

      // the tell-tales, in the black above each side instrument, exactly where the car keeps them:
      // engine and belt on the left, battery, oil and handbrake on the right, lit off real state.
      const telltale = (x: number, y: number, sz: number, on: boolean, col: string, draw: () => void) => {
        b.save(); b.translate(x, y); b.scale(sz / 100, sz / 100);
        b.strokeStyle = on ? col : 'rgba(255,255,255,0.09)'; b.fillStyle = b.strokeStyle;
        b.lineWidth = 9; draw(); b.restore();
      };
      const ttY = cy - R * 0.46, ttS = R * 0.17;
      telltale(cx - R * 1.92, ttY, ttS, s.engine !== 'on', '#f0a81e', () => {
        b.beginPath(); b.moveTo(-38, -6); b.lineTo(-22, -6); b.lineTo(-22, -22); b.lineTo(10, -22);
        b.lineTo(18, -10); b.lineTo(40, -10); b.lineTo(40, 20); b.lineTo(-22, 20); b.lineTo(-22, 6);
        b.lineTo(-38, 6); b.closePath(); b.stroke();
      });
      telltale(cx - R * 1.55, ttY, ttS, s.speedKmh > 5 && s.engine === 'on', '#d40000', () => {
        b.beginPath(); b.arc(0, -24, 12, 0, Math.PI * 2); b.stroke();
        b.beginPath(); b.moveTo(-16, 30); b.lineTo(-4, -6); b.lineTo(18, 30); b.stroke();
      });
      telltale(cx + R * 1.55, ttY, ttS, s.engine === 'off', '#d40000', () => {
        b.beginPath(); b.rect(-34, -16, 68, 34); b.stroke();
        b.beginPath(); b.rect(-22, -24, 12, 8); b.rect(10, -24, 12, 8); b.fill();
      });
      telltale(cx + R * 1.75, ttY, ttS, false, '#d40000', () => {
        b.beginPath(); b.moveTo(-36, 16); b.quadraticCurveTo(-40, -8, -14, -14);
        b.lineTo(20, -14); b.quadraticCurveTo(34, -14, 34, 2); b.quadraticCurveTo(34, 16, 14, 16);
        b.closePath(); b.stroke();
        b.beginPath(); b.moveTo(-2, -14); b.lineTo(14, -30); b.stroke();
      });
      telltale(cx + R * 1.95, ttY, ttS, s.speedKmh < 1 && s.brake > 0.3, '#d40000', () => {
        b.beginPath(); b.arc(0, 0, 28, 0, Math.PI * 2); b.stroke();
        b.font = '700 34px sans-serif'; b.textAlign = 'center'; b.textBaseline = 'middle';
        b.fillText('P', 0, 2);
      });

      // ---- left: the speedometer ----
      // Also rebuilt off the sharp photograph. It reads 0 to 360 in thirties, and the figures
      // ALTERNATE in size - the sixties are big, the thirties between them small - which is the detail
      // that stops it looking like a generic dial. Same 125 to 415 degree sweep as the tach.
      const lw = R * 1.66, lh = R * 1.32, lx = cx - bezR - w * 0.007 - lw, ly = cy + R * 0.49 - lh / 2;
      const panel = b.createLinearGradient(0, ly, 0, ly + lh);
      panel.addColorStop(0, '#141b2c'); panel.addColorStop(1, '#0a0e18');
      b.fillStyle = panel; rr(lx, ly, lw, lh, h * 0.014); b.fill();
      b.strokeStyle = 'rgba(255,255,255,0.12)'; b.lineWidth = 1; rr(lx, ly, lw, lh, h * 0.014); b.stroke();
      const scx = lx + lw * 0.50, scy = ly + lh * 0.56, sr = Math.min(lh * 0.40, lw * 0.32);
      const topSpd = mph ? 220 : 360, stepSpd = mph ? 20 : 30;
      const sA0 = (125 * Math.PI) / 180, sA1 = (415 * Math.PI) / 180;
      const sAng = (v: number) => sA0 + Math.max(0, Math.min(1, v / topSpd)) * (sA1 - sA0);
      // the shaded band that lies just inside the scale
      b.strokeStyle = 'rgba(150,165,195,0.18)'; b.lineWidth = sr * 0.13;
      b.beginPath(); b.arc(scx, scy, sr * 0.90, sA0, sA1); b.stroke();
      // the scale arc, with its ticks standing outside it
      b.strokeStyle = '#eef2f8'; b.lineWidth = Math.max(1, sr * 0.022);
      b.beginPath(); b.arc(scx, scy, sr * 0.985, sA0, sA1); b.stroke();
      const nStep = Math.round(topSpd / stepSpd);
      for (let i = 0; i <= nStep * 3; i++) {
        const v = (i * stepSpd) / 3;
        if (v > topSpd) break;
        const a = sAng(v), major = i % 3 === 0;
        b.strokeStyle = '#eef2f8'; b.lineWidth = Math.max(1, sr * (major ? 0.045 : 0.020));
        b.beginPath();
        b.moveTo(scx + sr * 0.985 * Math.cos(a), scy + sr * 0.985 * Math.sin(a));
        b.lineTo(scx + sr * (major ? 1.14 : 1.08) * Math.cos(a), scy + sr * (major ? 1.14 : 1.08) * Math.sin(a));
        b.stroke();
      }
      for (let k = 1; k <= nStep; k++) {
        const v = k * stepSpd, a = sAng(v), big = k % 2 === 0;
        const nr = big ? 0.76 : 0.63;
        text(String(v), scx + sr * nr * Math.cos(a), scy + sr * nr * Math.sin(a),
          cond(Math.round(sr * (big ? 0.27 : 0.185)), 500), '#f2f5fa', 'center', 'middle');
      }
      text(unitLabel, scx, scy - sr * 0.30, cond(Math.round(sr * 0.22), 500), '#c8d2e2', 'center', 'middle');
      // the pointer is RED with a white surround, and its tail is plain grey - the same design as the
      // rev counter's, which is how the tach's own needle was finally read the right way round
      const sAn = sAng(spd(s.speedKmh));
      b.save(); b.translate(scx, scy); b.rotate(sAn);
      b.fillStyle = '#9aa3b2'; b.beginPath();
      b.moveTo(-sr * 0.42, -sr * 0.018); b.lineTo(-sr * 0.04, -sr * 0.038);
      b.lineTo(-sr * 0.04, sr * 0.038); b.lineTo(-sr * 0.42, sr * 0.018);
      b.closePath(); b.fill();
      b.fillStyle = '#f4f7fb'; b.beginPath();
      b.moveTo(sr * 0.05, -sr * 0.072); b.lineTo(sr * 0.88, -sr * 0.026);
      b.lineTo(sr * 0.88, sr * 0.026); b.lineTo(sr * 0.05, sr * 0.072);
      b.closePath(); b.fill();
      b.fillStyle = '#e8241a'; b.beginPath();
      b.moveTo(sr * 0.12, -sr * 0.030); b.lineTo(sr * 0.82, -sr * 0.009);
      b.lineTo(sr * 0.82, sr * 0.009); b.lineTo(sr * 0.12, sr * 0.030);
      b.closePath(); b.fill();
      b.restore();
      const shub = b.createRadialGradient(scx - sr * 0.04, scy - sr * 0.05, sr * 0.01, scx, scy, sr * 0.12);
      shub.addColorStop(0, '#3d4557'); shub.addColorStop(1, '#11151f');
      b.fillStyle = shub; b.beginPath(); b.arc(scx, scy, sr * 0.12, 0, Math.PI * 2); b.fill();
      const now = new Date();
      text(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
        lx + lw * 0.07, ly + lh * 0.12, cond(Math.round(lh * 0.155), 400), '#eef2f8', 'left', 'middle');

      // ---- right: the info panel ----
      const rw = lw, rh = lh, rx = cx + bezR + w * 0.007, ry = ly;
      const rpanel = b.createLinearGradient(0, ry, 0, ry + rh);
      rpanel.addColorStop(0, '#141b2c'); rpanel.addColorStop(1, '#0a0e18');
      b.fillStyle = rpanel; rr(rx, ry, rw, rh, h * 0.014); b.fill();
      b.strokeStyle = 'rgba(255,255,255,0.12)'; b.lineWidth = 1; rr(rx, ry, rw, rh, h * 0.014); b.stroke();
      const mode = c?.resolvedStyle === 'eco' ? 'WET' : c?.style === 'sport' ? 'RACE' : 'SPORT';
      b.fillStyle = '#f2efe4'; b.beginPath();
      b.moveTo(rx, ry); b.lineTo(rx + rw * 0.42, ry); b.lineTo(rx + rw * 0.33, ry + rh * 0.145); b.lineTo(rx, ry + rh * 0.145);
      b.closePath(); b.fill();
      text(mode, rx + rw * 0.05, ry + rh * 0.075, cond(Math.round(rh * 0.115), 500), '#12161f', 'left', 'middle');
      text(`${Math.round(9 + 6 * Math.max(0, Math.min(1, state812.water / 95)))}°C`, rx + rw * 0.95, ry + rh * 0.075,
        cond(Math.round(rh * 0.135), 400), '#f2f5fa', 'right', 'middle');

      // THE TWO GAUGES. Not a plain coloured arc: a band that runs blue where the engine is cold,
      // WHITE for as far as the reading has come, dark for the rest, and red at the top end, with fine
      // ticks standing off it. The white portion IS the value, which is what the first version missed.
      const dials: [number, number, boolean][] = [[rx + rw * 0.27, state812.water, true], [rx + rw * 0.73, state812.oil, false]];
      for (const [dx, val, isWater] of dials) {
        const dy = ry + rh * 0.455, dr = Math.min(rh * 0.215, rw * 0.150);
        const dA0 = (152 * Math.PI) / 180, dA1 = (388 * Math.PI) / 180;
        const dAng = (f: number) => dA0 + Math.max(0, Math.min(1, f)) * (dA1 - dA0);
        const f = Math.max(0, Math.min(1, (val - 10) / 110));
        b.lineWidth = Math.max(2, dr * 0.17);
        b.lineCap = 'butt';
        b.strokeStyle = 'rgba(120,140,180,0.35)';
        b.beginPath(); b.arc(dx, dy, dr, dAng(0), dAng(1)); b.stroke();
        b.strokeStyle = '#2f5fd0';
        b.beginPath(); b.arc(dx, dy, dr, dAng(0), dAng(0.13)); b.stroke();
        b.strokeStyle = '#e8241a';
        b.beginPath(); b.arc(dx, dy, dr, dAng(0.86), dAng(1)); b.stroke();
        b.strokeStyle = '#f4f7fb';
        b.beginPath(); b.arc(dx, dy, dr, dAng(0.13), dAng(Math.max(0.13, Math.min(0.86, f)))); b.stroke();
        for (let i = 0; i <= 16; i++) {
          const a = dAng(i / 16);
          b.strokeStyle = 'rgba(238,242,248,0.85)'; b.lineWidth = Math.max(1, dr * 0.035);
          b.beginPath();
          b.moveTo(dx + dr * 1.10 * Math.cos(a), dy + dr * 1.10 * Math.sin(a));
          b.lineTo(dx + dr * 1.26 * Math.cos(a), dy + dr * 1.26 * Math.sin(a));
          b.stroke();
        }
        text(String(Math.round(val)), dx, dy - dr * 0.10, cond(Math.round(dr * 0.78), 400), '#ffffff', 'center', 'middle');
        text('°C', dx, dy + dr * 0.34, cond(Math.round(dr * 0.30), 400), '#ffffff', 'center', 'middle');
        // the pictogram under the figure, the size the car draws it
        b.save(); b.translate(dx, dy + dr * 0.86); b.scale(dr / 100, dr / 100);
        b.strokeStyle = '#ffffff'; b.fillStyle = '#ffffff'; b.lineWidth = 9; b.lineCap = 'round';
        if (isWater) {
          b.beginPath(); b.moveTo(0, -30); b.lineTo(0, 4); b.stroke();
          b.beginPath(); b.arc(0, 14, 11, 0, Math.PI * 2); b.fill();
          b.lineWidth = 7;
          for (const yy of [26, 40]) {
            b.beginPath(); b.moveTo(-34, yy); b.quadraticCurveTo(-22, yy - 9, -10, yy);
            b.quadraticCurveTo(2, yy + 9, 14, yy); b.quadraticCurveTo(26, yy - 9, 34, yy); b.stroke();
          }
        } else {
          b.beginPath(); b.moveTo(14, -30); b.lineTo(14, -2); b.stroke();
          b.beginPath(); b.arc(14, 8, 10, 0, Math.PI * 2); b.fill();
          b.lineWidth = 7;
          b.beginPath(); b.moveTo(-38, 20); b.lineTo(-14, 44); b.lineTo(18, 44); b.lineTo(30, 20); b.closePath(); b.stroke();
          b.beginPath(); b.moveTo(30, 26); b.lineTo(46, 18); b.stroke();
          b.beginPath(); b.moveTo(46, 26); b.quadraticCurveTo(52, 34, 46, 38); b.quadraticCurveTo(40, 34, 46, 26); b.fill();
        }
        b.restore(); b.lineCap = 'butt';
      }
      // the three page dots the cluster carries between the gauges. They sat at 0.755 with the red
      // rule at 0.760 - one radius apart, so the dots were ON the line and the line looked broken
      // where they crossed it (Marc: "esto en el ferrari está cortado"). Dots up, and that WAS the bug.
      for (let i = 0; i < 3; i++) {
        b.fillStyle = i === 0 ? '#f2f5fa' : 'rgba(200,212,232,0.35)';
        b.beginPath(); b.arc(rx + rw * 0.5 + (i - 1) * rw * 0.045, ry + rh * 0.722, rh * 0.017, 0, Math.PI * 2); b.fill();
      }
      // THE RED RULE, ACROSS THE WHOLE PANEL. v476 also pulled it in to 0.07-0.93 "out of the rounded
      // corners", which was wrong twice over: the corner radius is h*0.014 and the rule sits at 0.775
      // of the panel's height, nowhere near either corner - and shortening it made it read as cut all
      // over again (Marc: "y aqui está cortao"), because the row UNDER it is wider than it was. The
      // fuel pump starts at 0.044 of the panel and LAPS is right-aligned at 0.95, so a rule that stops
      // at 0.93 is visibly shorter than its own contents. It brackets them now, with an even margin.
      b.strokeStyle = '#e8241a'; b.lineWidth = Math.max(1, rh * 0.012);
      b.beginPath(); b.moveTo(rx + rw * 0.03, ry + rh * 0.775); b.lineTo(rx + rw * 0.97, ry + rh * 0.775); b.stroke();

      // fuel: the pump, then filled orange blocks and empty outlined ones
      // INSIDE THE BOX (Marc's screenshot: the 0 / 1/2 / 1 row and the figures were hanging out of
      // the bottom of the panel). The row sat at 0.835 of the panel's height with its labels a whole
      // 1.85 of the bar's height under it, which lands at 1.02 - past the panel's own edge. The block
      // is lifted and tightened so the last thing drawn, the baseline of the figures, ends at 0.97.
      const fy = ry + rh * 0.800, fh = rh * 0.085, fx = rx + rw * 0.115, fw = rw * 0.34;
      b.save(); b.translate(rx + rw * 0.055, fy + fh * 0.5); b.scale(fh / 100, fh / 100);
      b.strokeStyle = '#f0a81e'; b.fillStyle = '#f0a81e'; b.lineWidth = 10;
      b.beginPath(); b.rect(-30, -42, 46, 84); b.stroke();
      b.fillRect(-20, -32, 26, 26);
      b.beginPath(); b.moveTo(16, -20); b.lineTo(30, -20); b.lineTo(30, 20); b.stroke();
      b.restore();
      const cells = 10, lit2 = Math.round(cells * Math.max(0, Math.min(1, state812.fuel)));
      for (let i = 0; i < cells; i++) {
        const bx2 = fx + i * (fw / cells), bw3 = fw / cells - fw * 0.012;
        if (i < lit2) { b.fillStyle = state812.fuel < 0.15 ? '#e8241a' : '#f0a81e'; b.fillRect(bx2, fy, bw3, fh); }
        else { b.strokeStyle = 'rgba(238,242,248,0.75)'; b.lineWidth = 1; b.strokeRect(bx2 + 0.5, fy + 0.5, bw3 - 1, fh - 1); }
      }
      text('0', fx, fy + fh * 1.55, cond(Math.round(rh * 0.080), 400), '#eef2f8', 'left', 'middle');
      text('1/2', fx + fw * 0.5, fy + fh * 1.55, cond(Math.round(rh * 0.080), 400), '#eef2f8', 'center', 'middle');
      text('1', fx + fw, fy + fh * 1.55, cond(Math.round(rh * 0.080), 400), '#eef2f8', 'right', 'middle');
      // the speed. LAPS is gone (Marc), and the unit sits clear of the red rule above the panel
      // instead of touching it: label and figure both a little lower, the figure a touch smaller so
      // its baseline still ends inside the panel.
      text(unitLabel, rx + rw * 0.72, fy + fh * 0.62, cond(Math.round(rh * 0.075), 400), '#c8d2e2', 'center', 'middle');
      text(String(Math.round(spd(s.speedKmh))), rx + rw * 0.72, fy + fh * 1.62, cond(Math.round(rh * 0.155), 400), '#ffffff', 'center', 'middle');

      // ---- LAUNCH, the way the Porsche has it ----
      const launchReady = s.engine === 'on' && s.speedKmh < 3 && s.brake > 0.25;
      if (launchReady) {
        const bw2 = w * 0.20, bh2 = h * 0.075, bx2 = w * 0.5 - bw2 / 2, by2 = h * 0.90;
        b.fillStyle = s.throttle > 0.5 ? 'rgba(212,0,0,0.30)' : 'rgba(10,12,15,0.85)';
        rr(bx2, by2, bw2, bh2, bh2 * 0.35); b.fill();
        b.strokeStyle = FER_RED; b.lineWidth = Math.max(1.5, h * 0.005); rr(bx2, by2, bw2, bh2, bh2 * 0.35); b.stroke();
        text(s.throttle > 0.5 ? 'LAUNCH' : 'LAUNCH READY', w * 0.5, by2 + bh2 * 0.52, cond(Math.round(bh2 * 0.55), 700), '#fff', 'center', 'middle');
      }

      // the car's own switchgear instead of the house's generic boxes
      // RESPONSIVE: the switchgear box has a fixed 3.9:1 layout inside it. Sizing its width off w and
      // its height off h independently means that on a 1:1 screen it arrives at 2:1 and the starter,
      // the manettino and the AUTO/LAUNCH keys pile on top of each other. Drive both from one scale.
      const cbw = Math.min(w * 0.42, h * 0.145 * 3.9);
      f812Controls(w * 0.030, h * 0.800, cbw, cbw / 3.9, s);
      // START TIMING: removed at Marc's request, the same as on the SVJ. The lap timer runs itself
      // off the circuit's own line; there is nothing on this binnacle to press.
    };
    const drawPcu = (t: number, s: DrivetrainState, lap: LapState) => {
      b.fillStyle = '#0b0c0e'; rr(w * 0.015, h * 0.02, w * 0.97, h * 0.96, w * 0.02); b.fill();
      pcuLeds(t, s, w * 0.3, w * 0.7, h * 0.075, h * 0.02);
      pcuSide(w * 0.055, h * 0.13, h * 0.07, h * 0.014, s.load === 'brake');
      pcuSide(w * 0.945, h * 0.13, h * 0.07, h * 0.014, s.throttle > 0.8);
      // the display panel
      b.fillStyle = '#050506'; rr(w * 0.1, h * 0.13, w * 0.8, h * 0.84, w * 0.01); b.fill();
      // row 1: speed (white) · gear (white) · timing (dark)
      const r1y = h * 0.16, r1h = h * 0.3;
      whiteBox(w * 0.115, r1y, w * 0.235, r1h);
      text(String(Math.round(spd(s.speedKmh))), w * 0.225, r1y + r1h * 0.52, cond(Math.round(r1h * 0.72), 700), '#111', 'center', 'middle');
      text('KPH', w * 0.335, r1y + r1h * 0.3, cond(Math.round(h * 0.03), 700), '#111', 'right', 'middle');
      whiteBox(w * 0.36, r1y, w * 0.15, r1h);
      text(s.freeRev ? 'N' : String(s.gear), w * 0.435, r1y + r1h * 0.54, cond(Math.round(r1h * 0.9), 700), '#111', 'center', 'middle');
      darkBox(w * 0.52, r1y, w * 0.365, r1h);
      const d = lap.deltaS;
      const dcol = d == null ? '#fff' : d < -0.02 ? '#2bff4a' : d > 0.02 ? '#ff2a2a' : '#fff';
      text('LAST ' + formatLap(lap.lastS), w * 0.535, r1y + r1h * 0.17, mono(Math.round(h * 0.036)), '#fff', 'left', 'middle');
      text(d == null ? '+0.00' : (d >= 0 ? '+' : '-') + Math.abs(d).toFixed(2), w * 0.87, r1y + r1h * 0.48, mono(Math.round(h * 0.11)), dcol, 'right', 'middle');
      text('BEST ' + formatLap(lap.bestS), w * 0.535, r1y + r1h * 0.48, mono(Math.round(h * 0.036)), '#2bff4a', 'left', 'middle');
      text(formatLap(lap.currentS), w * 0.87, r1y + r1h * 0.82, mono(Math.round(h * 0.055)), '#fff', 'right', 'middle');
      text('LAP', w * 0.535, r1y + r1h * 0.82, cond(Math.round(h * 0.03), 600), '#9a9a9a', 'left', 'middle');
      // row 2: rpm/mode (left) · tyres (centre) · lap count (right)
      const r2y = h * 0.49, r2h = h * 0.2;
      darkBox(w * 0.115, r2y, w * 0.235, r2h);
      text(`${Math.round(s.rpm / 10) * 10}`, w * 0.23, r2y + r2h * 0.4, mono(Math.round(r2h * 0.42)), '#ffd01a', 'center', 'middle');
      text('RPM', w * 0.125, r2y + r2h * 0.82, cond(Math.round(h * 0.028), 600), '#9a9a9a', 'left', 'middle');
      text(loadLabel(s.load, s.engine), w * 0.34, r2y + r2h * 0.82, cond(Math.round(h * 0.028), 600), s.limiter ? '#ff2a2a' : '#cfd3d8', 'right', 'middle');
      pcuTyres(w * 0.36, r2y, w * 0.2, r2h, s, Math.round(h * 0.03));
      darkBox(w * 0.57, r2y, w * 0.315, r2h);
      b.fillStyle = '#2bff4a'; rr(w * 0.572, r2y + 2, w * 0.006, r2h - 4, 2); b.fill();
      b.fillStyle = '#2f6fe0'; rr(w * 0.879, r2y + 2, w * 0.006, r2h - 4, 2); b.fill();
      text('LAP', w * 0.73, r2y + r2h * 0.26, cond(Math.round(h * 0.03), 600), '#9a9a9a', 'center', 'middle');
      text(String(lap.lapCount), w * 0.73, r2y + r2h * 0.66, cond(Math.round(r2h * 0.5), 700), '#fff', 'center', 'middle');
      text(lap.circuitName ?? '', w * 0.87, r2y + r2h * 0.85, cond(Math.round(h * 0.024)), '#9a9a9a', 'right', 'middle');
      // row 3: banner
      const r3y = h * 0.71, r3h = h * 0.08;
      whiteBox(w * 0.115, r3y, w * 0.77, r3h);
      const banner = s.engine !== 'on' ? (s.engine === 'off' ? 'ENGINE OFF' : 'STARTING') : s.speedKmh < 3 ? 'PIT LIMITER' : s.limiter ? 'REV LIMITER' : s.load === 'accel' ? 'POWER' : s.load === 'brake' ? 'BRAKING' : 'ROARDRIVE';
      text(banner, w * 0.5, r3y + r3h * 0.52, cond(Math.round(r3h * 0.7), 700), '#111', 'center', 'middle');
      // row 4: touch controls
      controlBoxes(w * 0.115, h * 0.805, w * 0.36, h * 0.072, Math.round(h * 0.03));
      lapButtons(w * 0.6, h * 0.86, w * 0.285, h * 0.085, Math.round(h * 0.034));
    };
    // Faithful Bosch DDU 10 (Mercedes-AMG GT3): pure-black 5:3 panel, full-width
    // top LED rev bar green→red→blue with a blue shift-flash, huge central gear,
    // speed to its right, left TC/ABS/MAP tiles, right timing/fuel, bottom status.
    const drawBosch = (t: number, s: DrivetrainState, lap: LapState) => {
      // --- top LED rev bar: green → red → blue/violet, blue flash at limiter ---
      const pad = w * 0.02;
      const barW = w - pad * 2;
      const n = 18;
      const gap = Math.max(2, barW * 0.005);
      const segW = (barW - gap * (n - 1)) / n;
      const barH = h * 0.09;
      const lit = Math.round(Math.min(1, needle / max) * n);
      const blue = blueActive(t, s);
      const flash = blue && Math.floor((t / 1000) * 8) % 2 === 0;
      for (let i = 0; i < n; i++) {
        const p = i / n;
        let col = p < 0.5 ? '#00e000' : p < 0.8 ? '#ff2000' : '#3a5bff';
        if (i >= n - 3) col = '#7a2cff'; // violet shift tip
        const on = blue ? flash : i < lit;
        const c = blue ? '#1e5aff' : col;
        seg(pad + i * (segW + gap), h * 0.03, segW, barH, c, on);
      }

      // --- huge central gear (slightly left), squared white ---
      const gx = w * 0.36;
      const gy = h * 0.5;
      text(s.freeRev ? 'N' : String(s.gear), gx, gy, cond(Math.round(h * 0.6), 700), s.freeRev ? '#00e000' : s.shifting ? 'rgba(242,242,240,0.5)' : fg, 'center', 'middle');
      text(`${Math.round(needle / 10) * 10}`, gx, gy + h * 0.31, mono(Math.round(h * 0.055)), s.limiter ? '#ff2000' : muted, 'center', 'top');
      text('RPM', gx, gy + h * 0.37, cond(Math.round(h * 0.028)), muted, 'center', 'top');

      // --- speed to the right of the gear ---
      const sx = w * 0.52;
      text(String(Math.round(spd(s.speedKmh))), sx, gy, cond(Math.round(h * 0.2), 700), fg, 'left', 'middle');
      const spW = b.measureText(String(Math.round(spd(s.speedKmh)))).width;
      text(unitLabel, sx + spW + w * 0.008, gy + h * 0.03, cond(Math.round(h * 0.036)), '#9a9a9a', 'left', 'middle');

      // --- left column: TC / ABS / MAP control tiles (AMG signature) ---
      // These are the car's on-board driver settings (cosmetic dash chrome; they
      // are not derived from Tesla telemetry, kept for authentic AMG identity).
      controlBoxes(pad, h * 0.2, w * 0.2, h * 0.16, Math.round(h * 0.036));
      dryChip(pad, h * 0.62, w * 0.09, h * 0.08, Math.round(h * 0.034));

      // --- right column: timing + fuel/laps ---
      const rx = w * 0.72;
      const rw = w * 0.26;
      text(lap.currentS != null ? 'LAP' : 'READY', rx, h * 0.19, cond(Math.round(h * 0.036)), muted, 'left', 'top');
      text(lap.circuitName ?? 'GPS OFF', rx + rw, h * 0.19, cond(Math.round(h * 0.032)), muted, 'right', 'top');
      text(formatLap(lap.currentS), rx, h * 0.23, mono(Math.round(h * 0.11)), fg, 'left', 'top');
      // delta
      const d = lap.deltaS;
      const dcol = d == null ? muted : d < -0.02 ? '#00e000' : d > 0.02 ? '#ff2000' : '#ffc000';
      text('Δ ' + (d == null ? '—.——' : formatDelta(d)), rx, h * 0.4, mono(Math.round(h * 0.07)), dcol, 'left', 'top');
      dduTileR(rx, h * 0.56, rw / 2 - w * 0.01, 'LAST', formatLap(lap.lastS), fg);
      dduTileR(rx + rw / 2 + w * 0.01, h * 0.56, rw / 2 - w * 0.01, 'BEST', formatLap(lap.bestS), '#00c8ff');
      dduTileR(rx, h * 0.72, rw / 2 - w * 0.01, 'LAP', String(lap.lapCount), fg);
      dduTileR(rx + rw / 2 + w * 0.01, h * 0.72, rw / 2 - w * 0.01, 'GEAR', s.mode === 'auto' ? 'AUTO' : 'MAN', s.mode === 'auto' ? fg : '#ffc000');

      // --- bottom status row ---
      const byy = h * 0.9;
      b.fillStyle = 'rgba(255,255,255,0.03)';
      rr(pad, byy, w - pad * 2, h * 0.08, 4);
      b.fill();
      const stAlarm = s.limiter;
      text('STATE', pad + w * 0.015, byy + h * 0.014, cond(Math.round(h * 0.028)), '#9a9a9a', 'left', 'top');
      text(loadLabel(s.load, s.engine), pad + w * 0.015, byy + h * 0.04, cond(Math.round(h * 0.04), 600), stAlarm ? '#ff2000' : fg, 'left', 'top');
      // the DDU prints the car it is bolted into, not the car it was born in (it said AMG GT3 to a Nissan)
      text(profile.name, w * 0.5, byy + h * 0.04, cond(Math.round(h * 0.03)), silver, 'center', 'middle');
      lapButtons(rx, byy + h * 0.008, rw, h * 0.064, Math.round(h * 0.03));
    };
    // right timing tile
    const dduTileR = (x: number, y: number, tw: number, label: string, val: string, valCol: string) => {
      b.fillStyle = 'rgba(255,255,255,0.03)';
      rr(x, y, tw, h * 0.13, 4);
      b.fill();
      text(label, x + w * 0.012, y + h * 0.012, cond(Math.round(h * 0.03)), '#9a9a9a', 'left', 'top');
      text(val, x + w * 0.012, y + h * 0.045, mono(Math.round(h * 0.05)), valCol, 'left', 'top');
    };

    // v490 tried painting every other frame on the car's browser to spare its audio thread, and the
    // revs went up the dial in steps (Marc: 'van a trompicones'): that browser was not at 60 to begin
    // with. Every frame, everywhere; the audio got its wider buffer instead.
    const draw = (t: number) => {
      const s = getState();
      const lap = getLap();
      needle += (s.rpm - needle) * 0.5;
      {
        const dt = sim812.lastT ? Math.min(0.25, Math.max(0, (t - sim812.lastT) / 1000)) : 0;
        sim812.lastT = t;
        // the odometer is REAL distance, integrated from the speed the dash is already showing
        sim812.odoKm += (s.speedKmh / 3600) * dt;
        if (s.engine === 'on' && dt) {
          const work = 0.35 + 0.65 * Math.max(0, Math.min(1, 0.5 * s.throttle + 0.5 * s.rpmNorm));
          sim812.water += (92 * work + 8 - sim812.water) * Math.min(1, dt / 55);
          sim812.oil += (sim812.water * 1.02 - sim812.oil) * Math.min(1, dt / 110);
          sim812.fuel = Math.max(0, sim812.fuel - dt * (0.6 + 8 * s.throttle * Math.max(0, s.rpmNorm)) / 5400);
        }
      }

      b.setTransform(1, 0, 0, 1, 0, 0);
      b.fillStyle = '#050607';
      b.fillRect(0, 0, back.width, back.height);
      b.setTransform(dpr, 0, 0, dpr, 0, 0);

      hits.current = [];
      if (style === 'f812') drawF812(t, s, lap);
      else if (style === 'svj') drawSvj(t, s, lap);
      else if (style === 'pcu') drawPcu(t, s, lap);
      else if (style === 'f1') drawF1(t, s, lap);
      else if (style === 'bosch') drawBosch(t, s, lap);
      else drawCosworth(t, s, lap);

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(back, 0, 0);
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [profile, getState, getLap, accent, accent2, style]);

  return <canvas ref={ref} class="racedash" onPointerDown={onPointer} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} />;
}

function loadLabel(l: DrivetrainState['load'], engine: DrivetrainState['engine'] = 'on'): string {
  if (engine === 'off') return 'ENGINE OFF';
  if (engine === 'cranking') return 'STARTING';
  return { idle: 'IDLE', accel: 'POWER', cruise: 'CRUISE', coast: 'COAST', brake: 'BRAKING', shift: 'SHIFT', limiter: 'LIMITER' }[l];
}
